import React, { useState, useEffect } from 'react';
import { FileText, Upload, Download, X, Trash2, Check, Square, CheckSquare, UserPlus, Mail, Phone, Users, Clock, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useEventYear } from './EventYearContext';
import { useGuests } from '../hooks/useGuests';
import type { GuestAgent } from '../lib/guestService';
import { useJournalists } from '../hooks/useJournalists';
import { useInterviewRequests } from '../hooks/useInterviewRequests';
import { inviteJournalist, checkInvitationStatus, bulkInviteJournalists, sendPasswordReset, invitePrTeamMember } from '../lib/invitationService';
import { supabase } from '../lib/supabase';
import { AdminService } from '../lib/adminService';
import { messagingService } from '../lib/messagingService';
import { PressMaterialsBrowser } from './PressMaterialsBrowser';
import { MessagingDashboard } from './MessagingDashboard';
import type { EventYear } from './EventYearContext';

// Year creation sub-component
const EventYearCreator: React.FC<{
  refreshYears: () => Promise<void>;
  availableYears: EventYear[];
}> = ({ refreshYears, availableYears }) => {
  const [showForm, setShowForm] = useState(false);
  const [newYear, setNewYear] = useState(new Date().getFullYear() + 1);
  const [eventName, setEventName] = useState('FAN EXPO Chicago');
  const [copyJournalists, setCopyJournalists] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (availableYears.some(y => y.year === newYear)) {
      alert(`Year ${newYear} already exists.`);
      return;
    }

    if (!confirm(`Create event year ${newYear}?${copyJournalists ? ' Journalists will be copied from the most recent year.' : ''}`)) {
      return;
    }

    setCreating(true);
    try {
      // Create the event_settings row
      const { error: settingsError } = await supabase
        .from('event_settings')
        .insert({ year: newYear, event_name: eventName, is_archived: false });
      if (settingsError) throw settingsError;

      // Optionally copy journalists
      if (copyJournalists) {
        const sourceYear = availableYears[0]?.year; // Most recent year
        if (sourceYear) {
          const { data: existingJournalists, error: fetchError } = await supabase
            .from('journalists')
            .select('*')
            .eq('event_year', sourceYear);
          if (fetchError) throw fetchError;

          if (existingJournalists && existingJournalists.length > 0) {
            const newJournalists = existingJournalists.map(j => {
              const { id, ...rest } = j;
              return { ...rest, event_year: newYear, credentials_picked_up: false };
            });
            const { error: insertError } = await supabase
              .from('journalists')
              .insert(newJournalists);
            if (insertError) throw insertError;
          }
        }
      }

      await refreshYears();
      setShowForm(false);
      alert(`Event year ${newYear} created successfully!`);
    } catch (err: any) {
      alert('Failed to create year: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Create New Year
      </button>
    );
  }

  return (
    <div className="bg-white border border-indigo-200 rounded-lg p-4">
      <h4 className="font-medium text-gray-900 mb-3">Create New Event Year</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
          <input
            type="number"
            value={newYear}
            onChange={(e) => setNewYear(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={copyJournalists}
            onChange={(e) => setCopyJournalists(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">Copy journalists from most recent year</span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Journalist login accounts persist across years. This copies their credentialing records.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleCreate}
          disabled={creating}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
        >
          {creating ? 'Creating...' : 'Create Year'}
        </button>
        <button
          onClick={() => setShowForm(false)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const EventInterviewAppSimple: React.FC = () => {
  const { user: currentUser, logout } = useAuth();
  const { 
    guests: celebrities, 
    agents, 
    loading: guestsLoading,
    error: guestsError,
    addGuest,
    updateGuest,
    bulkAddGuests,
    deleteGuest,
    bulkDeleteGuests,
    deleteAllGuests,
    addAgent,
    assignAgentToGuest,
    removeAgentFromGuest,
    setGuestAgents,
    updateAgent,
    reloadData: reloadGuests,
    deleteAgent,
    deleteAllAgents,
  } = useGuests();

  // Agent helper functions
  const getPrimaryAgent = (guest: any): GuestAgent | null => {
    if (!guest.agents || guest.agents.length === 0) return null;
    return guest.agents.find((a: GuestAgent) => a.isPrimary) || guest.agents[0];
  };

  const getAgentDisplayName = (guest: any): string => {
    const primary = getPrimaryAgent(guest);
    if (!primary) return '-';
    if (guest.agents.length > 1) return `${primary.name} (+${guest.agents.length - 1})`;
    return primary.name;
  };

  const getAgentContact = (guest: any): string => {
    const primary = getPrimaryAgent(guest);
    if (!primary) return '';
    return [primary.email, primary.phone].filter(Boolean).join(', ');
  };

  const extractAgentEmail = (guest: any): string | null => {
    const primary = getPrimaryAgent(guest);
    return primary?.email || null;
  };
  
  const {
    journalists: allJournalists,
    loading: journalistsLoading,
    error: journalistsError,
    addJournalist,
    bulkAddJournalists,
    bulkUpsertJournalists,
    updateJournalist,
    deleteJournalist,
    bulkDeleteJournalists,
    deleteAllJournalists
  } = useJournalists();

  const {
    requests: interviewRequests,
    loading: requestsLoading,
    error: requestsError,
    reloadData: reloadInterviewRequests,
    createRequest,
    updateRequestStatus,
    updateRequestPriority,
    updateJournalistPriority,
    markRequestCompleted,
    deleteRequest,
    deleteRequestsByJournalist,
    cleanupOrphanedRequests,
    getRequestsByJournalist,
    getRequestsByCelebrity,
    getRequestsByStatus
  } = useInterviewRequests();

  // Event Year
  const { currentYear, setCurrentYear, availableYears, refreshYears, canSwitchYears } = useEventYear();
  const currentYearInfo = availableYears.find(y => y.year === currentYear);
  const isViewingArchived = currentYearInfo?.is_archived ?? false;

  // Basic state
  const [activeTab, setActiveTab] = useState('celebrities');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  
  // Use actual user role only
  const effectiveUser = {
    ...currentUser,
    role: currentUser?.role,
    outlet: currentUser?.outlet
  };
  
  // CSV Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState(''); // 'celebrities' or 'journalists'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'single', 'selected', 'all'
  const [deleteTarget, setDeleteTarget] = useState(''); // 'celebrities' or 'journalists'
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Agent modal state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [addAgentGuestSearch, setAddAgentGuestSearch] = useState('');
  const [addAgentSelectedGuests, setAddAgentSelectedGuests] = useState<string[]>([]);

  // Guest detail agent management
  const [showGuestAgentPicker, setShowGuestAgentPicker] = useState(false);
  const [guestAgentSearch, setGuestAgentSearch] = useState('');
  
  // Danger Zone search and sort state
  const [dangerZoneGuestSearch, setDangerZoneGuestSearch] = useState('');
  const [dangerZoneJournalistSearch, setDangerZoneJournalistSearch] = useState('');
  const [dangerZoneGuestSort, setDangerZoneGuestSort] = useState<'asc' | 'desc'>('asc');
  const [dangerZoneJournalistSort, setDangerZoneJournalistSort] = useState<'asc' | 'desc'>('asc');

  // Role check for interview request permissions
  const isPRTeam = currentUser?.role === 'pr' || currentUser?.role === 'admin';
  
  // Invitation state
  const [invitationStatuses, setInvitationStatuses] = useState<Record<string, string>>({});
  const [sendingInvitations, setSendingInvitations] = useState<Set<string>>(new Set());
  
  // Bulk invitation state
  const [selectedJournalistsForInvite, setSelectedJournalistsForInvite] = useState<Set<string>>(new Set());
  const [selectAllInvitations, setSelectAllInvitations] = useState(false);
  const [isSendingBulkInvites, setIsSendingBulkInvites] = useState(false);
  
  // Load invitation statuses when journalists change
  useEffect(() => {
    const loadStatuses = async () => {
      if (!allJournalists.length) return;
      
      const statuses: Record<string, string> = {};
      for (const journalist of allJournalists) {
        try {
          const status = await checkInvitationStatus(journalist.email);
          statuses[journalist.email] = status || 'not_invited';
        } catch (error) {
          console.error(`Error checking invitation status for ${journalist.email}:`, error);
          statuses[journalist.email] = 'not_invited';
        }
      }
      setInvitationStatuses(statuses);
    };

    if (allJournalists.length > 0) {
      loadStatuses();
    }
  }, [allJournalists]);

  // Real-time subscription for immediate unread badge updates
  useEffect(() => {
    if (!currentUser) return

    let messageSubscription: any = null

    const setupRealtimeSubscription = async () => {
      // Subscribe to all message insertions
      messageSubscription = supabase
        .channel('top-level-message-updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          async (payload) => {
            // If this is not the user's own message, there might be new unread messages
            if (payload.new.sender_id !== currentUser.id) {
              // Quick check: get updated unread status
              const unreadStatus = await messagingService.getUnreadStatus(currentUser.id, currentYear)
              const hasAnyUnread = Object.values(unreadStatus).some(status => status)
              setHasUnreadMessages(hasAnyUnread)
            }
          }
        )
        .subscribe()
    }

    setupRealtimeSubscription()

    return () => {
      if (messageSubscription) {
        supabase.removeChannel(messageSubscription)
      }
    }
  }, [currentUser?.id])

  
  // Modal state
  const [showCelebrityModal, setShowCelebrityModal] = useState(false);
  const [selectedCelebrity, setSelectedCelebrity] = useState(null);
  const [showJournalistModal, setShowJournalistModal] = useState(false);
  const [selectedJournalist, setSelectedJournalist] = useState(null);
  
  // Add Interview Request modal state
  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [selectedJournalistForRequest, setSelectedJournalistForRequest] = useState(null);
  const [selectedGuestForRequest, setSelectedGuestForRequest] = useState(null);
  const [addRequestStep, setAddRequestStep] = useState<'journalist' | 'guest' | 'confirm'>('journalist');
  const [journalistSearchTerm, setJournalistSearchTerm] = useState('');
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  
  // Interview Requests view state
  const [requestsViewMode, setRequestsViewMode] = useState<'by-journalist' | 'by-guest'>('by-journalist');
  const [showNewOnly, setShowNewOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [requestCredentialsFilter, setRequestCredentialsFilter] = useState<string>('all'); // 'all', 'picked-up', 'not-picked-up'
  const [priorityFilter, setPriorityFilter] = useState<string>('all'); // 'all', 'A', 'B', 'C', 'unassigned'
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Journalists grid state
  const [journalistSortField, setJournalistSortField] = useState<string>('journalist');
  const [journalistSortDirection, setJournalistSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [credentialsFilter, setCredentialsFilter] = useState<string>('all'); // 'all', 'picked-up', 'not-picked-up'
  
  // Guests grid state
  const [celebritySortField, setCelebritySortField] = useState<string>('celebrity');
  const [celebritySortDirection, setCelebritySortDirection] = useState<'asc' | 'desc'>('asc');
  const [celebritySearchTerm, setCelebritySearchTerm] = useState<string>('');
  
  // Agents grid state
  const [agentSearchTerm, setAgentSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [daysFilter, setDaysFilter] = useState<string[]>([]);
  const [knownForFilter, setKnownForFilter] = useState<string>('');
  const [guestTierFilter, setGuestTierFilter] = useState<string>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all');
  const [journalistTierFilter, setJournalistTierFilter] = useState<string>('all');
  
  // Category color mapping for show floor location reference
  const getCategoryColors = (category: string) => {
    switch (category) {
      case 'Celebrity':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200', 
          badge: 'bg-blue-100 text-blue-800',
          text: 'text-blue-900'
        };
      case 'Voice':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          badge: 'bg-green-100 text-green-800', 
          text: 'text-green-900'
        };
      case 'Anime':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          badge: 'bg-green-100 text-green-800',
          text: 'text-green-900'
        };
      case 'Gaming':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          badge: 'bg-blue-100 text-blue-800',
          text: 'text-blue-900'
        };
      case 'Comics':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          badge: 'bg-purple-100 text-purple-800',
          text: 'text-purple-900'
        };
      case 'Exec':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          badge: 'bg-orange-100 text-orange-800',
          text: 'text-orange-900'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          badge: 'bg-gray-100 text-gray-700',
          text: 'text-gray-900'
        };
    }
  };

  // Helper function to get Not Available styling
  const getNotAvailableStyles = (prAvailable: string) => {
    if (prAvailable === 'Not Available') {
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-900'
      };
    }
    return null;
  };

  // Helper function to normalize outlet names for proper alphabetical sorting
  const normalizeOutletName = (outlet: string) => {
    if (!outlet) return '';
    
    // Remove articles (The, A, An) and special characters for sorting
    return outlet
      .toLowerCase()
      .replace(/^(the|a|an)\s+/i, '') // Remove leading articles
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
      .trim();
  };

  // Helper function to sort requests by journalist last name for By Guest view
  const sortRequestsByJournalist = (requests: any[], direction: 'asc' | 'desc' = 'asc') => {
    return [...requests].sort((a, b) => {
      const aName = (a.journalistName || '').split(' ');
      const bName = (b.journalistName || '').split(' ');
      const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
      const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      
      if (direction === 'asc') {
        return aLastName.localeCompare(bLastName);
      } else {
        return bLastName.localeCompare(aLastName);
      }
    });
  };

  // Helper function to sort celebrity names by last name for By Journalist view
  const sortCelebritiesByLastName = (celebrityNames: string[], direction: 'asc' | 'desc' = 'asc') => {
    return [...celebrityNames].sort((a, b) => {
      const aName = (a || '').split(' ');
      const bName = (b || '').split(' ');
      const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
      const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      
      if (direction === 'asc') {
        return aLastName.localeCompare(bLastName);
      } else {
        return bLastName.localeCompare(aLastName);
      }
    });
  };

  // Danger Zone helper functions
  const filterAndSortDangerZoneGuests = (guests: any[], searchTerm: string, sortDirection: 'asc' | 'desc') => {
    let filtered = guests;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(guest => 
        guest.name.toLowerCase().includes(search) ||
        (guest.category || []).join(' ').toLowerCase().includes(search) ||
        (guest.knownFor || '').toLowerCase().includes(search)
      );
    }
    
    return [...filtered].sort((a, b) => {
      const aName = (a.name || '').split(' ');
      const bName = (b.name || '').split(' ');
      const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
      const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      
      if (sortDirection === 'asc') {
        return aLastName.localeCompare(bLastName);
      } else {
        return bLastName.localeCompare(aLastName);
      }
    });
  };

  const filterAndSortDangerZoneJournalists = (journalists: any[], searchTerm: string, sortDirection: 'asc' | 'desc') => {
    let filtered = journalists;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(journalist => 
        journalist.name.toLowerCase().includes(search) ||
        journalist.outlet.toLowerCase().includes(search) ||
        (journalist.email || '').toLowerCase().includes(search)
      );
    }
    
    return [...filtered].sort((a, b) => {
      const aName = (a.name || '').split(' ');
      const bName = (b.name || '').split(' ');
      const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
      const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      
      if (sortDirection === 'asc') {
        return aLastName.localeCompare(bLastName);
      } else {
        return bLastName.localeCompare(aLastName);
      }
    });
  };

  // Invitation functions
  const handleSendInvitation = async (journalist: any) => {
    if (!effectiveUser?.name || !effectiveUser?.email) {
      alert('Unable to send invitation - user information not found');
      return;
    }

    setSendingInvitations(prev => new Set(prev).add(journalist.email));
    
    try {
      const success = await inviteJournalist(
        journalist.email, 
        journalist.name, 
        effectiveUser.name,
        effectiveUser.email,
        effectiveUser.name
      );
      
      if (success) {
        setInvitationStatuses(prev => ({
          ...prev,
          [journalist.email]: 'pending'
        }));
        alert(`Invitation sent successfully to ${journalist.name}!`);
      } else {
        alert('Failed to send invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setSendingInvitations(prev => {
        const newSet = new Set(prev);
        newSet.delete(journalist.email);
        return newSet;
      });
    }
  };

  const handleBulkInvite = async (journalists: Array<{ email: string; name: string }>) => {
    if (!effectiveUser?.name || !effectiveUser?.email) {
      alert('Unable to send invitations - user information not found');
      return;
    }

    const confirmSend = confirm(`Send invitations to ${journalists.length} journalists?`);
    if (!confirmSend) return;

    try {
      const results = await bulkInviteJournalists(
        journalists, 
        effectiveUser.name,
        effectiveUser.email,
        effectiveUser.name
      );
      
      // Update invitation statuses for successful invites
      const updates: Record<string, string> = {};
      journalists.forEach(journalist => {
        updates[journalist.email] = 'pending';
      });
      setInvitationStatuses(prev => ({ ...prev, ...updates }));

      // Show results
      let message = `✅ Successfully sent ${results.successful} invitations`;
      if (results.failed.length > 0) {
        message += `\n⚠️ Failed to send ${results.failed.length} invitations`;
        console.log('Failed invitations:', results.failed);
      }
      alert(message);
      
    } catch (error) {
      console.error('Error sending bulk invitations:', error);
      alert('Failed to send invitations. Please try again.');
    }
  };

  // Bulk invitation checkbox functions
  const toggleJournalistSelection = (journalistEmail: string) => {
    setSelectedJournalistsForInvite(prev => {
      const newSet = new Set(prev);
      if (newSet.has(journalistEmail)) {
        newSet.delete(journalistEmail);
      } else {
        newSet.add(journalistEmail);
      }
      return newSet;
    });
  };

  const toggleSelectAllInvitations = (displayJournalists: any[]) => {
    if (selectAllInvitations) {
      setSelectedJournalistsForInvite(new Set());
    } else {
      setSelectedJournalistsForInvite(new Set(displayJournalists.map(j => j.email)));
    }
    setSelectAllInvitations(!selectAllInvitations);
  };

  const handleSendBulkInvitations = async () => {
    const selectedJournalists = allJournalists.filter(j => 
      selectedJournalistsForInvite.has(j.email)
    );
    
    setIsSendingBulkInvites(true);
    try {
      await handleBulkInvite(selectedJournalists.map(j => ({
        email: j.email,
        name: j.name
      })));
      
      // Clear selections after successful send
      setSelectedJournalistsForInvite(new Set());
      setSelectAllInvitations(false);
    } finally {
      setIsSendingBulkInvites(false);
    }
  };

  // PR Team management functions
  const loadPrTeamMembers = async () => {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .in('role', ['pr', 'admin'])  // Only load PR team members and admins, NOT journalists
        .order('name');
      
      if (error) {
        console.error('Error fetching users:', error);
        return;
      }
      
      // Convert users to PR team members format
      const prMembers = users.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role || 'pr',
        department: user.department || 'PR Team',
        permissions: user.permissions || ['read', 'write'],
        created_at: user.created_at,
        last_active: user.last_active || user.created_at
      }));
      
      setPrTeamMembers(prMembers);
    } catch (error) {
      console.error('Failed to load PR team members:', error);
    }
  };

  const addPrTeamMember = async (memberData: any) => {
    try {
      console.log('Database insert data:', {
        name: memberData.name,
        email: memberData.email,
        role: memberData.role,
        department: memberData.department,
        permissions: memberData.permissions,
        outlet: memberData.role === 'journalist' ? memberData.department : null
      });

      const { data, error } = await supabase
        .from('users')
        .insert([{
          name: memberData.name,
          email: memberData.email,
          role: memberData.role,
          department: memberData.department,
          permissions: memberData.permissions,
          outlet: memberData.role === 'journalist' ? memberData.department : null
        }])
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        console.error('Error code:', error.code);
        console.error('Error details:', error.details);
        console.error('Error hint:', error.hint);
        throw error;
      }

      console.log('Successfully inserted user:', data);

      // Update local state with the new member
      const newMember = {
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        department: data.department || 'PR Team',
        permissions: data.permissions || ['read', 'write'],
        created_at: data.created_at,
        last_active: data.last_active || data.created_at
      };
      
      setPrTeamMembers(prev => [...prev, newMember]);
      return newMember;
    } catch (error) {
      console.error('Failed to add PR team member:', error);
      throw error;
    }
  };

  const updatePrTeamMember = async (id: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          name: updates.name,
          email: updates.email,
          role: updates.role,
          department: updates.department,
          permissions: updates.permissions,
          outlet: updates.role === 'journalist' ? updates.department : null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // Update local state
      setPrTeamMembers(prev => prev.map(member => 
        member.id === id ? {
          ...member,
          name: data.name,
          email: data.email,
          role: data.role,
          department: data.department || 'PR Team',
          permissions: data.permissions || ['read', 'write']
        } : member
      ));
    } catch (error) {
      console.error('Failed to update PR team member:', error);
      throw error;
    }
  };

  const deletePrTeamMember = async (id: string) => {
    try {
      console.log('🗑️ Using AdminService to delete PR team member with ID:', id);
      console.log('🗑️ Current user role:', currentUser?.role);
      
      // Use the admin service for proper deletion
      await AdminService.deleteUser(id, currentUser?.role || '');
      
      // Update local state
      const newMembers = prTeamMembers.filter(member => member.id !== id);
      console.log('🔄 Updating local state - removing member from UI');
      setPrTeamMembers([...newMembers]);
      
    } catch (error) {
      console.error('❌ AdminService failed to delete PR team member:', error);
      throw error;
    }
  };

  // PR Team modal handlers
  const openAddPrMemberModal = () => {
    setShowAddPrMemberModal(true);
  };

  const closeAddPrMemberModal = () => {
    setShowAddPrMemberModal(false);
  };

  const openEditPrMemberModal = (member: any) => {
    setEditingPrMember(member);
    setShowEditPrMemberModal(true);
  };

  const closeEditPrMemberModal = () => {
    setEditingPrMember(null);
    setShowEditPrMemberModal(false);
  };

  const openRemovePrMemberModal = (member: any) => {
    setMemberToRemove(member);
    setShowRemovePrMemberModal(true);
  };

  const closeRemovePrMemberModal = () => {
    setShowRemovePrMemberModal(false);
    setMemberToRemove(null);
  };

  const confirmRemovePrMember = async () => {
    if (memberToRemove) {
      try {
        await deletePrTeamMember(memberToRemove.id);
        // Force a complete reload to ensure UI updates
        setTimeout(async () => {
          await loadPrTeamMembers();
          console.log('🔄 Force reloaded PR team members');
        }, 100);
        closeRemovePrMemberModal();
        console.log('✅ Successfully removed PR team member:', memberToRemove.name);
      } catch (error) {
        console.error('❌ Failed to remove PR team member:', error);
        alert('Failed to remove team member. Please try again.');
      }
    }
  };

  // Export functions
  const exportGuestData = () => {
    try {
      // Prepare guest data for export
      const exportData = celebrities.map(guest => {
        const requests = getRequestsByCelebrity(guest.name);
        const completed = requests.filter(req => req.completed[guest.name]).length;
        
        return {
          'Guest Name': guest.name,
          'Category': (guest.category || []).join(', '),
          'Known For': guest.knownFor,
          'Days Available': Array.isArray(guest.days) ? guest.days.join(', ') : guest.days,
          'Agent': getAgentDisplayName(guest),
          'Agent Contact': getAgentContact(guest),
          'PR Available': guest.prAvailable,
          'Story Details': guest.storyDetails || '',
          'Notes': guest.notes || '',
          'Total Interview Requests': requests.length,
          'Completed Interviews': completed,
          'Completion Rate %': requests.length > 0 ? Math.round((completed / requests.length) * 100) : 0,
          'Source': guest.source || 'manual'
        };
      });

      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escape commas and quotes in values
            return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `guest-data-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Exported ${exportData.length} guest records to CSV file.`);
    } catch (error) {
      console.error('Error exporting guest data:', error);
      alert('Error exporting guest data. Please try again.');
    }
  };

  const exportPressData = () => {
    try {
      // Prepare press data for export
      const exportData = allJournalists.map(journalist => {
        const requests = getRequestsByJournalist(journalist.name);
        const totalRequested = requests.reduce((acc, req) => acc + req.celebrities.length, 0);
        const totalCompleted = requests.reduce((acc, req) => 
          acc + Object.values(req.completed).filter(Boolean).length, 0
        );
        
        return {
          'Journalist Name': journalist.name,
          'Outlet': journalist.outlet,
          'Email': journalist.email,
          'Phone': journalist.phone || '',
          'Specialty': journalist.specialty || '',
          'Twitter': journalist.twitter || '',
          'Website': journalist.website || '',
          'Bio': journalist.bio || '',
          'Verified': journalist.verified ? 'Yes' : 'No',
          'Credentials Picked Up': journalist.credentialsPickedUp ? 'Yes' : 'No',
          'Total Interview Requests': requests.length,
          'Total Guests Requested': totalRequested,
          'Total Completed Interviews': totalCompleted,
          'Completion Rate %': totalRequested > 0 ? Math.round((totalCompleted / totalRequested) * 100) : 0,
          'Account Status': journalist.verified ? 'Active' : 'Pending',
          'Source': journalist.source || 'manual'
        };
      });

      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escape commas and quotes in values
            return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `press-data-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Exported ${exportData.length} press records to CSV file.`);
    } catch (error) {
      console.error('Error exporting press data:', error);
      alert('Error exporting press data. Please try again.');
    }
  };

  const downloadGuestTemplate = () => {
    const headers = 'Name,Category,Known For,Days,Agent Name,Agency,Agent Email,Agent Phone,PR Available,Story Details,Notes';
    const example = 'John Smith,Celebrity,"Movie Title, TV Show","Friday, Saturday",Jane Agent,Top Agency,jane@agency.com,555-0100,Available,Great story angle,VIP guest';
    const csvContent = headers + '\n' + example;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'guest-import-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadJournalistTemplate = () => {
    const headers = 'Name,Email,Phone,Primary Outlet,Secondary Outlets,Specialty,Bio,Twitter,Website,Notes';
    const example = 'Jane Reporter,jane@outlet.com,555-0200,Entertainment Weekly,Variety,Film & TV,Covers entertainment industry,@janereporter,https://janereporter.com,Returning press';
    const csvContent = headers + '\n' + example;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'journalist-import-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Load PR team members when component mounts
  React.useEffect(() => {
    if (effectiveUser?.role === 'admin') {
      loadPrTeamMembers();
    }
  }, [effectiveUser]);

  // Credentials management functions
  const updateJournalistCredentials = async (journalistId: number, credentialsPickedUp: boolean) => {
    try {
      await updateJournalist(journalistId, { credentialsPickedUp });
    } catch (error) {
      console.error('Failed to update journalist credentials:', error);
      alert('Failed to update credentials status. Please try again.');
    }
  };
  
  // Edit modals state
  const [showEditJournalistModal, setShowEditJournalistModal] = useState(false);
  const [showEditCelebrityModal, setShowEditCelebrityModal] = useState(false);
  const [editingJournalist, setEditingJournalist] = useState(null);
  const [editingCelebrity, setEditingCelebrity] = useState(null);

  // Controlled form state for Edit Celebrity modal
  const [editCelebForm, setEditCelebForm] = useState({ name: '', category: [] as string[], knownFor: '', prAvailable: 'TBD', days: [] as string[] });

  // Controlled form state for Edit Agent modal
  const [editAgentForm, setEditAgentForm] = useState({ name: '', email: '', phone: '' });

  // ALL MESSAGING DELETED v2
  


  // Add modals state
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [showAddJournalistModal, setShowAddJournalistModal] = useState(false);

  // PR Team management state
  const [prTeamMembers, setPrTeamMembers] = useState([]);
  const [showAddPrMemberModal, setShowAddPrMemberModal] = useState(false);
  const [showEditPrMemberModal, setShowEditPrMemberModal] = useState(false);
  const [editingPrMember, setEditingPrMember] = useState(null);
  const [showRemovePrMemberModal, setShowRemovePrMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  
  // Column widths for resizable columns
  const [columnWidths, setColumnWidths] = useState({
    journalist: 200,
    outlet: 150,
    guest: 150,
    priority: 80,
    status: 100
  });
  const [resizing, setResizing] = useState<{ column: string; startX: number; startWidth: number } | null>(null);

  // Journalist grid column widths
  const [journalistColumnWidths, setJournalistColumnWidths] = useState({
    journalist: 250,
    outlet: 300,
    requests: 150,
    credentials: 120
  });
  const [journalistResizing, setJournalistResizing] = useState<{ column: string; startX: number; startWidth: number } | null>(null);
  
  // Celebrity grid column widths
  const [celebrityColumnWidths, setCelebrityColumnWidths] = useState({
    celebrity: 180,
    category: 120,
    knownFor: 180,
    agent: 150,
    availability: 200
  });
  const [celebrityResizing, setCelebrityResizing] = useState<{ column: string; startX: number; startWidth: number } | null>(null);
  
  // Check if user has delete permissions (PR team only)
  const canDelete = effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin';
  
  // Column resizing handlers
  const handleMouseDown = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column as keyof typeof columnWidths]
    });
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (!resizing) return;
    
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(80, resizing.startWidth + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizing.column]: newWidth
    }));
  };
  
  const handleMouseUp = () => {
    setResizing(null);
  };
  
  // Add event listeners for column resizing
  React.useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing]);

  // Journalist grid column resizing handlers
  const handleJournalistMouseDown = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    setJournalistResizing({
      column,
      startX: e.clientX,
      startWidth: journalistColumnWidths[column as keyof typeof journalistColumnWidths]
    });
  };
  
  const handleCelebrityMouseDown = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    setCelebrityResizing({
      column,
      startX: e.clientX,
      startWidth: celebrityColumnWidths[column as keyof typeof celebrityColumnWidths]
    });
  };
  
  const handleJournalistMouseMove = (e: MouseEvent) => {
    if (!journalistResizing) return;
    
    const diff = e.clientX - journalistResizing.startX;
    const newWidth = Math.max(80, journalistResizing.startWidth + diff);
    
    setJournalistColumnWidths(prev => ({
      ...prev,
      [journalistResizing.column]: newWidth
    }));
  };
  
  const handleJournalistMouseUp = () => {
    setJournalistResizing(null);
  };
  
  const handleCelebrityMouseMove = (e: MouseEvent) => {
    if (!celebrityResizing) return;
    
    const diff = e.clientX - celebrityResizing.startX;
    const newWidth = Math.max(80, celebrityResizing.startWidth + diff);
    
    setCelebrityColumnWidths(prev => ({
      ...prev,
      [celebrityResizing.column]: newWidth
    }));
  };
  
  const handleCelebrityMouseUp = () => {
    setCelebrityResizing(null);
  };
  
  
  // Add event listeners for journalist column resizing
  React.useEffect(() => {
    if (journalistResizing) {
      document.addEventListener('mousemove', handleJournalistMouseMove);
      document.addEventListener('mouseup', handleJournalistMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleJournalistMouseMove);
        document.removeEventListener('mouseup', handleJournalistMouseUp);
      };
    }
  }, [journalistResizing]);
  
  // Add event listeners for celebrity column resizing
  React.useEffect(() => {
    if (celebrityResizing) {
      document.addEventListener('mousemove', handleCelebrityMouseMove);
      document.addEventListener('mouseup', handleCelebrityMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleCelebrityMouseMove);
        document.removeEventListener('mouseup', handleCelebrityMouseUp);
      };
    }
  }, [celebrityResizing]);
  

  // Journalist sorting handler
  const handleJournalistSort = (field: string) => {
    if (journalistSortField === field) {
      setJournalistSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setJournalistSortField(field);
      setJournalistSortDirection('asc');
    }
  };
  
  // Celebrity sorting handler
  const handleCelebritySort = (field: string) => {
    if (celebritySortField === field) {
      setCelebritySortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setCelebritySortField(field);
      setCelebritySortDirection('asc');
    }
  };
  
  // Filter handlers
  const handleCategoryFilter = (category: string) => {
    setCategoryFilter(category);
  };
  
  const handleDaysFilter = (day: string) => {
    setDaysFilter(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };
  
  const handleKnownForFilter = (credit: string) => {
    setKnownForFilter(prev => prev === credit ? '' : credit);
  };
  
  // Helper function to render clickable Known For credits
  const renderKnownForCredits = (knownFor: string) => {
    if (!knownFor) return <span className="text-gray-400">-</span>;
    
    const credits = knownFor.split(',').map(credit => credit.trim()).filter(Boolean);
    
    return (
      <div className="flex flex-wrap gap-1 justify-start">
        {credits.map((credit, index) => (
          <button
            key={index}
            onClick={() => handleKnownForFilter(credit)}
            className={`text-xs px-2 py-0.5 rounded-full transition-colors text-left ${
              knownForFilter === credit
                ? 'bg-indigo-100 text-indigo-700 font-medium ring-1 ring-indigo-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {credit}
          </button>
        ))}
      </div>
    );
  };

  // Handle loading and errors after all hooks
  if (!currentUser) {
    return <div>Loading user...</div>;
  }

  if (guestsLoading || journalistsLoading || requestsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (guestsError) {
    console.warn('Guests database error:', guestsError);
  }
  
  if (journalistsError) {
    console.warn('Journalists database error:', journalistsError);
  }
  
  if (requestsError) {
    console.warn('Interview requests database error:', requestsError);
  }

  // CSV parsing utility
  const parseCSV = (text) => {
    const cleanText = text.replace(/^\uFEFF/, '');
    const lines = cleanText.trim().split(/\r?\n/);
    
    let headerLineIndex = 0;
    let headers = [];
    
    // Find the first line that looks like headers (has actual content)
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line) {
        // Try to parse this line as headers
        const potentialHeaders = line.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        
        // Check if this looks like a header row (has meaningful content)
        const hasValidHeaders = potentialHeaders.some(header => 
          header.length > 0 && 
          /[a-zA-Z]/.test(header) && // Contains letters
          !header.match(/^\d+$/) // Not just numbers
        );
        
        if (hasValidHeaders) {
          headers = potentialHeaders;
          headerLineIndex = i;
          break;
        }
      }
    }
    
    if (headers.length === 0) {
      console.error('No valid header row found in CSV');
      return { headers: [], data: [] };
    }
    
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const data = [];
    for (let i = headerLineIndex + 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }
    
    return { headers, data };
  };

  // Create column mapping for celebrities
  const createColumnMapping = (headers) => {
    const mapping = {};
    const columnVariations = {
      'name': ['Name', 'Full Name', 'Guest Name'],
      'firstName': ['First Name', 'FirstName', 'Given Name'],
      'lastName': ['Last Name', 'LastName', 'Surname', 'Family Name'],
      'knownFor': ['Known For', 'Known_For', 'Famous For', 'Celebrity For'],
      'category': ['Category', 'Type', 'Guest Type'],
      'days': ['Days', 'Day', 'Available Days', 'Event Days'],
      'agentName': ['Agent Name', 'Agent_Name', 'Agent', 'Representative'],
      'agency': ['Agency', 'Agent Agency', 'Management Company', 'Company'],
      'agentEmail': ['Agent Email', 'Agent_Email', 'Agent E-mail', 'Representative Email'],
      'agentPhone': ['Agent Phone', 'Agent_Phone', 'Agent Tel', 'Representative Phone'],
      'prAvailable': ['PR Available', 'PR_Available', 'Available for PR', 'PR Status'],
      'storyDetails': ['Story Details', 'Story_Details', 'Additional Details', 'Bio'],
      'notes': ['Notes', 'Additional Notes', 'Comments', 'Remarks']
    };
    
    headers.forEach((header, index) => {
      const normalizedHeader = header.trim();
      Object.entries(columnVariations).forEach(([key, variations]) => {
        if (variations.some(variation => 
          variation.toLowerCase() === normalizedHeader.toLowerCase()
        )) {
          mapping[key] = index;
        }
      });
    });
    
    
    return mapping;
  };

  // Create column mapping for journalists
  const createJournalistColumnMapping = (headers) => {
    const mapping = {};
    const columnVariations = {
      'name': ['Name', 'Full Name', 'Journalist Name'],
      'firstName': ['First Name', 'FirstName', 'Given Name'],
      'lastName': ['Last Name', 'LastName', 'Surname', 'Family Name'],
      'email': ['Email', 'Email Address', 'E-mail', 'Contact Email'],
      'phone': ['Phone', 'Phone Number', 'Tel', 'Telephone', 'Contact Phone'],
      'outlet': ['Primary Outlet', 'Media Outlet', 'Outlet', 'Publication', 'Organization', 'Company'],
      'secondaryOutlets': ['Secondary Outlets', 'Secondary_Outlets', 'Additional Outlets', 'Other Outlets'],
      'specialty': ['Specialty', 'Specialization', 'Beat', 'Focus Area'],
      'bio': ['Bio', 'Biography', 'About', 'Description'],
      'twitter': ['Twitter', 'Twitter Handle', 'X Handle', 'Social Media'],
      'website': ['Website', 'Web Site', 'URL', 'Personal Website'],
      'notes': ['Notes', 'Additional Notes', 'Comments', 'Remarks'],
      'pressTier': ['Press Tier', 'Tier', 'Press Level']
    };

    headers.forEach((header, index) => {
      const normalizedHeader = header.trim();
      Object.entries(columnVariations).forEach(([key, variations]) => {
        if (variations.some(variation =>
          variation.toLowerCase() === normalizedHeader.toLowerCase()
        )) {
          mapping[key] = index;
        }
      });
    });


    return mapping;
  };

  // Handle celebrity CSV upload
  const handleCelebrityUpload = async (file) => {
    setIsUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const { headers, data } = parseCSV(e.target.result);
        const columnMapping = createColumnMapping(headers);
        
        // Validate required columns
        const hasNameColumn = columnMapping.name !== undefined;
        const hasFirstLastColumns = columnMapping.firstName !== undefined && columnMapping.lastName !== undefined;
        
        if (!hasNameColumn && !hasFirstLastColumns) {
          setUploadResults({
            success: false,
            error: 'Missing name columns: Must have either "Name" column OR both "First Name" and "Last Name" columns',
            processed: 0,
            added: 0,
            skipped: 0
          });
          setIsUploading(false);
          return;
        }

        // Parse rows into guest data and collect unique agents
        const parsedRows = [];
        const uniqueAgentsByEmail = new Map(); // key: email -> agent data

        data.forEach((rowData, index) => {
          try {
            const row = {};
            Object.entries(columnMapping).forEach(([key, columnIndex]) => {
              const headerName = headers[columnIndex];
              row[key] = rowData[headerName]?.trim() || '';
            });

            const fullName = hasNameColumn ? row.name : `${row.firstName} ${row.lastName}`.trim();

            // Parse category into array
            const categoryArray = row.category ? row.category.split(',').map(c => c.trim()).filter(Boolean) : [];

            if (!fullName || categoryArray.length === 0) {
              console.warn(`Skipping row ${index + 2}: Missing required fields`);
              return;
            }

            // Process days
            let daysArray = [];
            if (row.days) {
              if (row.days.toLowerCase().includes('all')) {
                daysArray = ['Friday', 'Saturday', 'Sunday'];
              } else {
                daysArray = row.days.split(',').map(d => d.trim()).filter(d => d);
              }
            }

            // Split agent fields on commas and match positionally
            const agentNames = (row.agentName || '').split(',').map(s => s.trim()).filter(Boolean);
            const agentAgencies = (row.agency || '').split(',').map(s => s.trim());
            const agentEmails = (row.agentEmail || '').split(',').map(s => s.trim()).filter(Boolean);
            const agentPhones = (row.agentPhone || '').split(',').map(s => s.trim());

            const rowAgentEmails: string[] = [];

            agentNames.forEach((name, i) => {
              const email = agentEmails[i] || '';
              const phone = agentPhones[i] || '';
              const agency = agentAgencies[i] || agentAgencies[0] || 'Unknown Agency';

              if (email) {
                const emailKey = email.toLowerCase();
                rowAgentEmails.push(emailKey);
                if (!uniqueAgentsByEmail.has(emailKey)) {
                  uniqueAgentsByEmail.set(emailKey, {
                    name,
                    agency,
                    email,
                    phone,
                    eventYear: currentYear,
                    totalRequests: 0,
                    source: 'bulk_upload'
                  });
                }
              }
            });

            parsedRows.push({ fullName, row, daysArray, rowAgentEmails, categoryArray });
          } catch (error) {
            console.warn(`Error processing row ${index + 2}:`, error);
          }
        });

        if (parsedRows.length === 0) {
          setUploadResults({
            success: false,
            error: 'No valid celebrities found in CSV file',
            processed: data.length,
            added: 0,
            skipped: data.length
          });
          setIsUploading(false);
          return;
        }

        // Filter out agents that already exist (by email)
        const existingAgentsByEmail = new Map();
        agents.forEach(a => {
          if (a.email) existingAgentsByEmail.set(a.email.toLowerCase(), a.id);
        });

        const newAgents = Array.from(uniqueAgentsByEmail.values()).filter(
          a => !existingAgentsByEmail.has(a.email.toLowerCase())
        );

        // Create only new agents
        let createdAgents = [];
        if (newAgents.length > 0) {
          const result = await bulkAddGuests([], newAgents);
          createdAgents = result.agents || [];
        }

        // Build email→ID map from existing + newly created
        const agentEmailToId = new Map(existingAgentsByEmail);
        createdAgents.forEach(a => {
          if (a.email) agentEmailToId.set(a.email.toLowerCase(), a.id);
        });

        // Build guest objects with agent assignments
        const processedGuests = parsedRows.map(({ fullName, row, daysArray, rowAgentEmails, categoryArray }) => {
          const guestAgents = rowAgentEmails
            .filter(email => agentEmailToId.has(email))
            .map((email, i) => ({
              id: '',
              agentId: agentEmailToId.get(email),
              name: '', agency: '', email: '', phone: '',
              isPrimary: i === 0
            }));

          return {
            name: fullName,
            category: categoryArray,
            days: daysArray,
            knownFor: row.knownFor || '',
            agents: guestAgents,
            prAvailable: row.prAvailable || 'TBD',
            storyDetails: row.storyDetails || '',
            notes: row.notes || '',
            source: 'bulk_upload',
            priority: 'Unassigned' as const
          };
        });

        // Upload guests (with agent assignments handled via junction table)
        await bulkAddGuests(processedGuests, []);

        setUploadResults({
          success: true,
          processed: data.length,
          added: processedGuests.length,
          skipped: data.length - processedGuests.length
        });

      } catch (error) {
        console.error('Celebrity upload error:', error);
        setUploadResults({
          success: false,
          error: `Error parsing CSV: ${error.message}`,
          processed: 0,
          added: 0,
          updated: 0,
          skipped: 0
        });
      }
      
      setIsUploading(false);
    };
    
    reader.readAsText(file);
  };

  // Handle journalist CSV upload
  const handleJournalistUpload = async (file) => {
    setIsUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const { headers, data } = parseCSV(e.target.result);
        const journalistColumnMapping = createJournalistColumnMapping(headers);
        
        // Validate required columns
        const hasNameColumn = journalistColumnMapping.name !== undefined;
        const hasFirstLastColumns = journalistColumnMapping.firstName !== undefined && journalistColumnMapping.lastName !== undefined;
        
        if (!hasNameColumn && !hasFirstLastColumns) {
          setUploadResults({
            success: false,
            error: 'Missing name columns: Must have either "Name" column OR both "First Name" and "Last Name" columns',
            processed: 0,
            added: 0,
            skipped: 0
          });
          setIsUploading(false);
          return;
        }

        const processedJournalists = [];
        
        data.forEach((rowData, index) => {
          try {
            const row = {};
            Object.entries(journalistColumnMapping).forEach(([key, columnIndex]) => {
              const headerName = headers[columnIndex];
              row[key] = rowData[headerName]?.trim() || '';
            });

            const fullName = hasNameColumn ? row.name : `${row.firstName} ${row.lastName}`.trim();
            
            if (!fullName || !row.outlet) {
              console.warn(`Skipping row ${index + 2}: Missing required fields (name, outlet)`);
              return;
            }

            const validTiers = ['A', 'B', 'C'];
            const rawTier = (row.pressTier || '').toUpperCase().trim();
            const pressTier = validTiers.includes(rawTier) ? rawTier : 'C';

            const journalist = {
              name: fullName,
              outlet: row.outlet,
              email: row.email,
              phone: row.phone || '',
              specialty: row.specialty || '',
              bio: row.bio || '',
              twitter: row.twitter || '',
              website: row.website || '',
              verified: false,
              credentialsPickedUp: false,
              notes: row.notes || '',
              pressTier,
              source: 'bulk_upload'
            };

            processedJournalists.push(journalist);
          } catch (error) {
            console.warn(`Error processing row ${index + 2}:`, error);
          }
        });

        if (processedJournalists.length === 0) {
          setUploadResults({
            success: false,
            error: 'No valid journalists found in CSV file',
            processed: data.length,
            added: 0,
            updated: 0,
            skipped: data.length
          });
          setIsUploading(false);
          return;
        }

        // Upload to database using upsert
        try {
          const upsertResult = await bulkUpsertJournalists(processedJournalists);

          setUploadResults({
            success: true,
            processed: data.length,
            added: upsertResult.added.length,
            updated: upsertResult.updated.length,
            skipped: upsertResult.skipped.length,
            newJournalists: upsertResult.added || []
          });
        } catch (uploadError) {
          console.error('Database upload error:', uploadError);
          
          // Extract specific email if it's a duplicate key error
          let errorMessage = uploadError.message;
          if (errorMessage.includes('duplicate key value violates unique constraint')) {
            const emailMatch = errorMessage.match(/Key \(email\)=\((.*?)\)/);
            if (emailMatch) {
              errorMessage = `Email "${emailMatch[1]}" already exists in the database. This journalist may have been deleted from the UI but still exists in the database.`;
            }
          }
          
          setUploadResults({
            success: false,
            error: errorMessage,
            processed: data.length,
            added: 0,
            updated: 0,
            skipped: 0
          });
        }

      } catch (error) {
        console.error('CSV parsing error:', error);
        setUploadResults({
          success: false,
          error: `Error parsing CSV: ${error.message}`,
          processed: 0,
          added: 0,
          updated: 0,
          skipped: 0
        });
      }
      
      setIsUploading(false);
    };
    
    reader.readAsText(file);
  };

  const openUploadModal = (type) => {
    setUploadType(type);
    setShowUploadModal(true);
    setUploadResults(null);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadType('');
    setUploadResults(null);
    setIsUploading(false);
  };

  // Delete handlers
  const handleSingleDelete = async (id: string, type: 'celebrities' | 'journalists') => {
    if (!canDelete) return;
    
    setDeleteType('single');
    setDeleteTarget(type);
    setSelectedItems([id]);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = (type: 'celebrities' | 'journalists') => {
    if (!canDelete || selectedItems.length === 0) return;
    
    setDeleteType('selected');
    setDeleteTarget(type);
    setShowDeleteModal(true);
  };

  const handleDeleteAll = (type: 'celebrities' | 'journalists') => {
    if (!canDelete) return;
    
    setDeleteType('all');
    setDeleteTarget(type);
    setShowDeleteModal(true);
  };

  const executeDelete = async () => {
    if (!canDelete) return;
    
    setIsDeleting(true);
    
    try {
      if (deleteType === 'single') {
        if (deleteTarget === 'celebrities') {
          await deleteGuest(selectedItems[0]);
        } else {
          // Get journalist name before deletion
          const journalistToDelete = allJournalists.find(j => j.id === selectedItems[0]);
          await deleteJournalist(selectedItems[0]);
          // Delete associated interview requests by name
          if (journalistToDelete) {
            await deleteRequestsByJournalist(journalistToDelete.name);
          }
        }
        setDeleteResults({ success: true, count: 1, type: 'deleted' });
      } else if (deleteType === 'selected') {
        if (deleteTarget === 'celebrities') {
          await bulkDeleteGuests(selectedItems);
        } else {
          // Get journalist names before deletion
          const journalistsToDelete = allJournalists.filter(j => selectedItems.includes(j.id));
          await bulkDeleteJournalists(selectedItems);
          // Delete associated interview requests by name
          for (const journalist of journalistsToDelete) {
            await deleteRequestsByJournalist(journalist.name);
          }
        }
        setDeleteResults({ success: true, count: selectedItems.length, type: 'deleted' });
      } else if (deleteType === 'all') {
        if (deleteTarget === 'celebrities') {
          await deleteAllGuests();
        } else {
          // Get all journalist names before deletion
          const allJournalistNames = allJournalists.map(j => j.name);
          await deleteAllJournalists();
          // Delete all associated interview requests
          for (const name of allJournalistNames) {
            await deleteRequestsByJournalist(name);
          }
          // Clean up any remaining orphaned requests
          await executeCleanupOrphanedRequests();
        }
        setDeleteResults({ success: true, count: 'all', type: 'deleted' });
      }
      
      // Clear selections
      setSelectedItems([]);
    } catch (error) {
      console.error('Delete error:', error);
      setDeleteResults({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Delete failed' 
      });
    }
    
    setIsDeleting(false);
  };

  const executeCleanupOrphanedRequests = async () => {
    try {
      const deletedCount = await cleanupOrphanedRequests();
      console.log(`Cleaned up ${deletedCount} orphaned interview requests`);
      return deletedCount;
    } catch (error) {
      console.error('Cleanup error:', error);
      throw error;
    }
  };


  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteType('');
    setDeleteTarget('');
    setSelectedItems([]);
    setDeleteResults(null);
    setIsDeleting(false);
    setDeleteConfirmText('');
  };

  const toggleItemSelection = (id: string) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = (type: 'celebrities' | 'journalists') => {
    const items = type === 'celebrities' ? celebrities : allJournalists;
    const allIds = items.map(item => item.id);
    
    if (selectedItems.length === allIds.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allIds);
    }
  };

  // Modal handlers
  const openCelebrityModal = (celebrity) => {
    setSelectedCelebrity(celebrity);
    setShowCelebrityModal(true);
  };

  const closeCelebrityModal = () => {
    setShowCelebrityModal(false);
    setSelectedCelebrity(null);
  };

  const openJournalistModal = (journalistName) => {
    // Find the journalist object from the allJournalists array
    const journalist = allJournalists.find(j => j.name === journalistName);
    if (journalist) {
      setSelectedJournalist(journalist);
      setShowJournalistModal(true);
    }
  };

  const closeJournalistModal = () => {
    setShowJournalistModal(false);
    setSelectedJournalist(null);
  };
  
  // Edit modal handlers
  const openEditJournalistModal = (journalist) => {
    setEditingJournalist(journalist);
    setShowEditJournalistModal(true);
  };
  
  const closeEditJournalistModal = () => {
    setShowEditJournalistModal(false);
    setEditingJournalist(null);
  };
  
  const openEditCelebrityModal = (celebrity) => {
    setEditingCelebrity(celebrity);
    setEditCelebForm({
      name: celebrity.name || '',
      category: celebrity.category || [],
      knownFor: celebrity.knownFor || '',
      prAvailable: celebrity.prAvailable || 'TBD',
      days: celebrity.days || []
    });
    setShowEditCelebrityModal(true);
  };
  
  const closeEditCelebrityModal = () => {
    setShowEditCelebrityModal(false);
    setEditingCelebrity(null);
  };



  // Add modal handlers
  const openAddGuestModal = () => {
    setShowAddGuestModal(true);
  };

  const closeAddGuestModal = () => {
    setShowAddGuestModal(false);
  };

  const openAddJournalistModal = () => {
    setShowAddJournalistModal(true);
  };

  const closeAddJournalistModal = () => {
    setShowAddJournalistModal(false);
    setEditingJournalist(null);
  };
  
  // Agent modal handlers
  const openAgentModal = (agent) => {
    setSelectedAgent(agent);
    setShowAgentModal(true);
  };
  
  const closeAgentModal = () => {
    setShowAgentModal(false);
    setSelectedAgent(null);
  };

  // Helper function to sort days in proper order
  const sortDays = (days) => {
    const dayOrder = ['Friday', 'Saturday', 'Sunday'];
    return (days || []).sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
  };

  // Helper function to extract email from contact string
  const extractEmail = (contactString) => {
    if (!contactString) return null;
    const emailMatch = contactString.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
    return emailMatch ? emailMatch[1] : null;
  };

  // Update PR Available status
  const updatePRAvailable = async (celebrityId: string, newStatus: string) => {
    try {
      const updatedGuest = await updateGuest(celebrityId, { prAvailable: newStatus });
      if (updatedGuest) {
        // Update the selected celebrity in the modal
        if (selectedCelebrity && selectedCelebrity.id === celebrityId) {
          setSelectedCelebrity({ ...selectedCelebrity, prAvailable: newStatus });
        }
      }
    } catch (error) {
      console.error('Failed to update PR Available status:', error);
      alert('Failed to update PR Available status. Please try again.');
    }
  };

  // Helper function to group requests by guest for "By Guest" view
  const getRequestsByGuest = (requests: any[]) => {
    const guestRequests: Record<string, any[]> = {};
    
    requests.forEach(request => {
      (request.celebrities || []).forEach(celebrityName => {
        if (!guestRequests[celebrityName]) {
          guestRequests[celebrityName] = [];
        }
        
        // Create a simplified request object for this specific celebrity
        guestRequests[celebrityName].push({
          ...request,
          celebrityName,
          priority: (request.priority && request.priority[celebrityName]) || 'Unassigned',
          isCompleted: (request.completed && request.completed[celebrityName]) || false
        });
      });
    });
    
    return guestRequests;
  };

  // Helper function to filter for new unassigned requests
  const filterNewUnassigned = (requests: any[]) => {
    return requests.filter(request => 
      request.status === 'new' && 
      (request.celebrities || []).some(celebrityName => 
        (request.priority && request.priority[celebrityName]) === 'Unassigned' || 
        !request.priority || 
        !request.priority[celebrityName]
      )
    );
  };

  // Helper function to filter by status
  const filterByStatus = (requests: any[], status: string) => {
    if (status === 'all') return requests;
    return requests.filter(request => request.status === status);
  };

  // Helper function to filter by search term
  const filterBySearch = (requests: any[], searchTerm: string) => {
    if (!searchTerm) return requests;
    const search = searchTerm.toLowerCase();
    return requests.filter(request => 
      (request.journalistName || '').toLowerCase().includes(search) ||
      (request.outlet || '').toLowerCase().includes(search) ||
      (request.celebrities || []).some(celeb => celeb.toLowerCase().includes(search))
    );
  };

  // Helper function to filter by credentials status
  const filterByCredentials = (requests: any[], credentialsFilter: string) => {
    if (credentialsFilter === 'all') return requests;
    
    return requests.filter(request => {
      // Find the journalist by name to check credentials status
      const journalist = allJournalists.find(j => j.name === request.journalistName);
      
      if (credentialsFilter === 'picked-up') {
        return journalist?.credentialsPickedUp === true;
      } else if (credentialsFilter === 'not-picked-up') {
        return journalist?.credentialsPickedUp === false || !journalist;
      }
      return true;
    });
  };

  // Helper function to filter by priority
  const filterByPriority = (requests: any[], priorityFilter: string) => {
    if (priorityFilter === 'all') return requests;
    
    return requests.filter(request => {
      // Check if ANY celebrity in the request has the selected priority
      const priorities = request.priority || {};
      const hasPriority = Object.values(priorities).some(p => {
        if (priorityFilter === 'unassigned') {
          return p === 'Unassigned' || !p;
        }
        return p === priorityFilter;
      });
      
      // If no priorities are set and we're filtering for unassigned, include it
      if (priorityFilter === 'unassigned' && Object.keys(priorities).length === 0) {
        return true;
      }
      
      return hasPriority;
    });
  };

  // Helper function to sort requests
  const sortRequests = (requests: any[], field: string, direction: 'asc' | 'desc') => {
    if (!field) return requests;
    
    return [...requests].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      
      if (field === 'journalist') {
        // Sort by last name for journalist field
        const aName = (a.journalistName || '').split(' ');
        const bName = (b.journalistName || '').split(' ');
        aVal = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
        bVal = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      } else if (field === 'outlet') {
        aVal = normalizeOutletName(a.outlet || '');
        bVal = normalizeOutletName(b.outlet || '');
      } else if (field === 'status') {
        aVal = (a.status || '').toLowerCase();
        bVal = (b.status || '').toLowerCase();
      }
      
      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  };

  // Helper function to sort guests for "By Guest" view
  const sortGuests = (guestNames: string[], direction: 'asc' | 'desc') => {
    return [...guestNames].sort((a, b) => {
      // Sort by last name for guest names
      const aName = a.split(' ');
      const bName = b.split(' ');
      const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
      const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      
      if (direction === 'asc') {
        return aLastName.localeCompare(bLastName);
      } else {
        return bLastName.localeCompare(aLastName);
      }
    });
  };

  // Helper functions for journalist grid
  const filterJournalists = (journalists: any[], searchTerm: string) => {
    if (!searchTerm) return journalists;
    const search = searchTerm.toLowerCase();
    return journalists.filter(journalist => 
      (journalist.name || '').toLowerCase().includes(search) ||
      (journalist.outlet || '').toLowerCase().includes(search) ||
      (journalist.email || '').toLowerCase().includes(search)
    );
  };

  const sortJournalists = (journalists: any[], field: string, direction: 'asc' | 'desc') => {
    return [...journalists].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      
      if (field === 'journalist') {
        // Sort by last name for journalist field
        const aName = (a.name || '').split(' ');
        const bName = (b.name || '').split(' ');
        aVal = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
        bVal = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      } else if (field === 'outlet') {
        aVal = normalizeOutletName(a.outlet || '');
        bVal = normalizeOutletName(b.outlet || '');
      }
      
      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  };
  
  // Guest filtering and sorting functions
  const filterCelebrities = (celebrities: any[], searchTerm: string, categoryFilter: string, daysFilter: string[], knownForFilter: string, tierFilter?: string, avFilter?: string) => {
    let filtered = celebrities;

    // Search term filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(celebrity =>
        (celebrity.name || '').toLowerCase().includes(search) ||
        (celebrity.category || []).join(' ').toLowerCase().includes(search) ||
        (celebrity.knownFor || '').toLowerCase().includes(search) ||
        (celebrity.agents || []).some((a: any) => a.name.toLowerCase().includes(search))
      );
    }

    // Category filter
    if (categoryFilter && categoryFilter !== 'all') {
      filtered = filtered.filter(celebrity => (celebrity.category || []).includes(categoryFilter));
    }

    // Days filter
    if (daysFilter.length > 0) {
      filtered = filtered.filter(celebrity =>
        celebrity.days && celebrity.days.some(day => daysFilter.includes(day))
      );
    }

    // Known For filter
    if (knownForFilter) {
      filtered = filtered.filter(celebrity =>
        (celebrity.knownFor || '').toLowerCase().includes(knownForFilter.toLowerCase())
      );
    }

    // Priority tier filter
    if (tierFilter && tierFilter !== 'all') {
      filtered = filtered.filter(celebrity => celebrity.priority === tierFilter);
    }

    // Availability filter
    if (avFilter && avFilter !== 'all') {
      filtered = filtered.filter(celebrity => celebrity.prAvailable === avFilter);
    }

    return filtered;
  };
  
  const sortCelebrities = (celebrities: any[], field: string, direction: 'asc' | 'desc') => {
    return [...celebrities].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      
      if (field === 'celebrity') {
        // Sort by last name for celebrity field
        const aName = (a.name || '').split(' ');
        const bName = (b.name || '').split(' ');
        aVal = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
        bVal = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
      } else if (field === 'category') {
        aVal = (a.category || []).join(', ').toLowerCase();
        bVal = (b.category || []).join(', ').toLowerCase();
      } else if (field === 'knownFor') {
        aVal = (a.knownFor || '').toLowerCase();
        bVal = (b.knownFor || '').toLowerCase();
      } else if (field === 'agent') {
        aVal = getAgentDisplayName(a).toLowerCase();
        bVal = getAgentDisplayName(b).toLowerCase();
      }
      
      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  };

  // Helper function to handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>

      <div style={{ background: 'linear-gradient(135deg, var(--neutral-900) 0%, var(--acacia-blue-dark) 100%)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Top Bar */}
          <div className="lg:hidden">
            <div className="flex justify-between items-center h-14 py-2">
              {/* Mobile Logo */}
              <div className="flex items-center gap-2">
                <img
                  src="/fanexpo-chicago-logo.png"
                  alt="Fan Expo Chicago"
                  className="h-8 w-auto"
                />
                <h1 className="text-lg font-black text-white" style={{ fontFamily: '"Arial Black", Arial, sans-serif', letterSpacing: '0.05em' }}>
                  PRESS CENTER
                </h1>
              </div>

              {/* Mobile Actions */}
              <div className="flex items-center gap-2">
                <a href="sms:7733125870" className="text-[10px] text-white/70 hover:text-white transition-colors">
                  Press Desk Text Hotline: <span className="font-semibold text-white/90">773-312-5870</span>
                </a>
                <button
                  onClick={logout}
                  className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-md text-sm font-medium touch-manipulation backdrop-blur-sm border border-white/20"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Mobile Welcome */}
            <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-white/70">Welcome, {effectiveUser?.name || 'User'}</span>
              {canSwitchYears && availableYears.length > 1 && (
                <select
                  value={currentYear}
                  onChange={(e) => setCurrentYear(Number(e.target.value))}
                  className={`text-sm border rounded-md px-2 py-1 font-medium ${
                    isViewingArchived
                      ? 'bg-amber-900/30 border-amber-400/50 text-amber-200'
                      : 'bg-white/10 border-white/20 text-white'
                  }`}
                >
                  {availableYears.map(y => (
                    <option key={y.year} value={y.year}>
                      {y.year}{y.is_archived ? ' (Archived)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Desktop Top Bar */}
          <div className="hidden lg:flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center gap-3">
                <img 
                  src="/fanexpo-chicago-logo.png" 
                  alt="Fan Expo Chicago" 
                  className="h-10 w-auto"
                />
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: '"Arial Black", Arial, sans-serif', letterSpacing: '0.05em' }}>
                  PRESS CENTER
                </h1>
                <span className="text-xs text-white/50 mx-2">|</span>
                <a href="sms:7733125870" className="text-xs text-white/70 hover:text-white transition-colors">
                  Press Desk Text Hotline: <span className="font-semibold text-white/90">773-312-5870</span>
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-white/70">Welcome, {effectiveUser?.name || 'User'}</span>

              {canSwitchYears && availableYears.length > 1 && (
                <select
                  value={currentYear}
                  onChange={(e) => setCurrentYear(Number(e.target.value))}
                  className={`text-sm border rounded-md px-2 py-1 font-medium ${
                    isViewingArchived
                      ? 'bg-amber-900/30 border-amber-400/50 text-amber-200'
                      : 'bg-white/10 border-white/20 text-white'
                  }`}
                >
                  {availableYears.map(y => (
                    <option key={y.year} value={y.year}>
                      {y.year}{y.is_archived ? ' (Archived)' : ''}
                    </option>
                  ))}
                </select>
              )}

              <div className="flex items-center space-x-3">
                <button
                  onClick={logout}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md text-sm font-medium backdrop-blur-sm border border-white/20"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-4 px-4 sm:py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          {/* Mobile Navigation - Hamburger Menu */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="btn-secondary flex items-center px-4 py-2 rounded-lg touch-manipulation"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Menu
            </button>
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
              <div className="relative bg-white w-80 max-w-sm flex flex-col shadow-xl">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">Navigation</h3>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 rounded-md text-gray-400 hover:text-gray-500"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-1">
                  <button
                    onClick={() => {
                      setActiveTab('celebrities');
                      setSelectedItems([]);
                      setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      activeTab === 'celebrities' 
                        ? 'bg-pink-50 text-pink-700 border-l-4 border-pink-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Guests ({celebrities.length})
                  </button>
                  
                  {effectiveUser?.role !== 'journalist' && (
                    <button
                      onClick={() => {
                        setActiveTab('journalists');
                        setSelectedItems([]);
                        setSidebarOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        activeTab === 'journalists' 
                          ? 'bg-indigo-100 text-indigo-700 border-l-4 border-indigo-500' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Journalists ({allJournalists.length})
                    </button>
                  )}
                  
                  {effectiveUser?.role !== 'journalist' && (
                    <button
                      onClick={() => {
                        setActiveTab('agents');
                        setSelectedItems([]);
                        setSidebarOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        activeTab === 'agents' 
                          ? 'bg-indigo-100 text-indigo-700 border-l-4 border-indigo-500' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Agents ({(() => {
                        const agentNames = new Set();
                        celebrities.forEach(celebrity => {
                          (celebrity.agents || []).forEach((a: any) => {
                            agentNames.add(a.agentId);
                          });
                        });
                        return agentNames.size;
                      })()})
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setActiveTab('requests');
                      setSelectedItems([]);
                      setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      activeTab === 'requests' 
                        ? 'bg-pink-50 text-pink-700 border-l-4 border-pink-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {effectiveUser?.role === 'journalist' ? 'My Requests' : 'Interview Requests'} ({
                      effectiveUser?.role === 'journalist' 
                        ? getRequestsByJournalist(effectiveUser?.name || '').length
                        : interviewRequests.length
                    })
                  </button>
                  
                  <button
                    onClick={() => {
                      setActiveTab('press-materials');
                      setSelectedItems([]);
                      setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      activeTab === 'press-materials' 
                        ? 'bg-pink-50 text-pink-700 border-l-4 border-pink-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Press Materials
                  </button>
                  
                  <button
                    onClick={() => {
                      setActiveTab('messages');
                      setSelectedItems([]);
                      setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      activeTab === 'messages' 
                        ? 'bg-pink-100 text-pink-700 border-l-4 border-pink-500' 
                        : hasUnreadMessages 
                        ? 'text-gray-700 hover:bg-gray-100 border-2 border-red-500'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span>📢 Messages</span>
                    </div>
                  </button>
                  
                  {(effectiveUser?.role === 'admin' || effectiveUser?.role === 'pr') && (
                    <button
                      onClick={() => {
                        setActiveTab('admin');
                        setSelectedItems([]);
                        setSidebarOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        activeTab === 'admin' 
                          ? 'bg-indigo-100 text-indigo-700 border-l-4 border-indigo-500' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Admin
                    </button>
                  )}
                </nav>
              </div>
            </div>
          )}

          {/* Desktop Navigation - Hidden on Mobile */}
          <nav className="hidden lg:flex space-x-8 overflow-x-auto">
            <button
              onClick={() => {
                setActiveTab('celebrities');
                setSelectedItems([]);
              }}
              className={`nav-tab-button ${
                activeTab === 'celebrities' ? 'active' : ''
              }`}
            >
              Guests ({celebrities.length})
            </button>
            
            {effectiveUser?.role !== 'journalist' && (
              <button
                onClick={() => {
                  setActiveTab('journalists');
                  setSelectedItems([]);
                }}
                className={`nav-tab-button ${
                  activeTab === 'journalists' ? 'active' : ''
                }`}
              >
                Journalists ({allJournalists.length})
              </button>
            )}
            
            {effectiveUser?.role !== 'journalist' && (
              <button
                onClick={() => {
                  setActiveTab('agents');
                  setSelectedItems([]);
                }}
                className={`nav-tab-button ${
                  activeTab === 'agents' ? 'active' : ''
                }`}
              >
                Agents ({(() => {
                  const agentNames = new Set();
                  celebrities.forEach(celebrity => {
                    (celebrity.agents || []).forEach((a: any) => {
                      agentNames.add(a.agentId);
                    });
                  });
                  return agentNames.size;
                })()})
              </button>
            )}
            
            <button
              onClick={() => {
                setActiveTab('requests');
                setSelectedItems([]);
              }}
              className={`nav-tab-button ${
                activeTab === 'requests' ? 'active' : ''
              }`}
            >
              {effectiveUser?.role === 'journalist' ? 'My Requests' : 'Interview Requests'} ({
                effectiveUser?.role === 'journalist' 
                  ? getRequestsByJournalist(effectiveUser?.name || '').length
                  : interviewRequests.length
              })
            </button>
            
            <button
              onClick={() => {
                setActiveTab('press-materials');
                setSelectedItems([]);
              }}
              className={`nav-tab-button ${
                activeTab === 'press-materials' ? 'active' : ''
              }`}
            >
              Press Materials
            </button>
            
            <button
              onClick={() => {
                setActiveTab('messages');
                setSelectedItems([]);
              }}
              className={`nav-tab-button ${
                activeTab === 'messages' ? 'active' : hasUnreadMessages ? 'unread-messages' : ''
              }`}
              style={hasUnreadMessages && activeTab !== 'messages' ? {
                border: '2px solid #ef4444'
              } : {}}
            >
              <div className="flex items-center space-x-2">
                <span>📢 Messages</span>
              </div>
            </button>
            
            {(effectiveUser?.role === 'admin' || effectiveUser?.role === 'pr') && (
              <button
                onClick={() => {
                  setActiveTab('reports');
                  setSelectedItems([]);
                }}
                className={`nav-tab-button ${
                  activeTab === 'reports' ? 'active' : ''
                }`}
              >
                Reports & Analytics
              </button>
            )}
            
            {(effectiveUser?.role === 'admin' || effectiveUser?.role === 'pr') && (
              <button
                onClick={() => {
                  setActiveTab('admin');
                  setSelectedItems([]);
                }}
                className={`nav-tab-button ${
                  activeTab === 'admin' ? 'active' : ''
                }`}
              >
                Admin
              </button>
            )}
          </nav>
        </div>

        {isViewingArchived && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.194-.833-2.964 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm font-medium text-amber-800">
              Viewing archived data from {currentYear}. Data is read-only.
            </span>
          </div>
        )}

        <div className="bg-white shadow-md rounded-xl overflow-hidden">
          <div className="px-4 py-5 sm:p-6" style={{ borderTop: '3px solid var(--fanexpo-magenta)' }}>
            {activeTab === 'celebrities' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {effectiveUser?.role === 'journalist' ? 'Guests Available for Interviews' : 'Guests'}
                  </h2>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full">
                    {/* Search Bar - Mobile optimized */}
                    <div className="relative flex-1 sm:flex-initial">
                      <input
                        type="text"
                        placeholder="Search guests, categories..."
                        value={celebritySearchTerm}
                        onChange={(e) => setCelebritySearchTerm(e.target.value)}
                        className="w-full sm:w-64 pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent touch-manipulation"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 sm:h-4 sm:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    {effectiveUser?.role !== 'journalist' && (
                      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <button
                          onClick={openAddGuestModal}
                          className="flex items-center justify-center px-4 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-base sm:text-sm font-medium touch-manipulation"
                        >
                          <UserPlus size={20} className="mr-2 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Add Guest</span>
                          <span className="sm:hidden">Add Guest</span>
                        </button>
                        <button
                          onClick={() => openUploadModal('celebrities')}
                          className="flex items-center justify-center px-4 py-3 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-base sm:text-sm font-medium touch-manipulation"
                        >
                          <Upload size={20} className="mr-2 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Upload CSV</span>
                          <span className="sm:hidden">Upload CSV</span>
                        </button>
                        <button
                          onClick={downloadGuestTemplate}
                          className="flex items-center justify-center px-4 py-3 sm:py-2 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-base sm:text-sm font-medium touch-manipulation"
                        >
                          <Download size={20} className="mr-2 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">CSV Template</span>
                          <span className="sm:hidden">CSV Template</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Filter Section */}
                <div className="mb-6 space-y-4 bg-gray-50 rounded-xl p-4">
                  {/* Category Filters */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Category</h3>
                    <div className="flex flex-wrap gap-2">
                      {['all', 'Celebrity', 'Voice', 'Anime', 'Gaming', 'Comics', 'Exec'].map(category => (
                        <button
                          key={category}
                          onClick={() => handleCategoryFilter(category)}
                          className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                            categoryFilter === category
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {category === 'all' ? 'All' : category}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Days + Priority + Availability Row */}
                  <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                    {/* Days Filters */}
                    <div className="sm:flex-1 sm:min-w-0">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Days Available</h3>
                      <div className="flex flex-wrap gap-2">
                        {['Friday', 'Saturday', 'Sunday'].map(day => (
                          <button
                            key={day}
                            onClick={() => handleDaysFilter(day)}
                            className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                              daysFilter.includes(day)
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Priority + Availability Dropdowns */}
                    <div className="flex gap-3">
                      {/* Priority Tier Dropdown - PR/Admin only */}
                      {isPRTeam && (
                        <div>
                          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Priority Tier</h3>
                          <select
                            value={guestTierFilter}
                            onChange={(e) => setGuestTierFilter(e.target.value)}
                            className={`text-sm font-medium border rounded-lg px-3 py-2 sm:py-1.5 w-full touch-manipulation ${
                              guestTierFilter === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                              guestTierFilter === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                              guestTierFilter === 'C' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                              guestTierFilter === 'Unassigned' ? 'bg-gray-100 border-gray-300 text-gray-700' :
                              'bg-white border-gray-200 text-gray-600'
                            }`}
                          >
                            <option value="all">All Tiers</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="Unassigned">Unassigned</option>
                          </select>
                        </div>
                      )}

                      {/* Interview Availability Dropdown */}
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Availability</h3>
                        <select
                          value={availabilityFilter}
                          onChange={(e) => setAvailabilityFilter(e.target.value)}
                          className={`text-sm font-medium border rounded-lg px-3 py-2 sm:py-1.5 w-full touch-manipulation ${
                            availabilityFilter === 'Available' ? 'bg-green-50 border-green-300 text-green-700' :
                            availabilityFilter === 'Limited Availability' ? 'bg-yellow-50 border-yellow-300 text-yellow-700' :
                            availabilityFilter === 'Not Available' ? 'bg-red-50 border-red-300 text-red-700' :
                            availabilityFilter === 'TBD' ? 'bg-gray-100 border-gray-300 text-gray-700' :
                            'bg-white border-gray-200 text-gray-600'
                          }`}
                        >
                          <option value="all">All</option>
                          <option value="Available">Available</option>
                          {isPRTeam && <option value="Limited Availability">Limited</option>}
                          <option value="Not Available">Not Available</option>
                          {isPRTeam && <option value="TBD">TBD</option>}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Active Filters Display */}
                  {(categoryFilter !== 'all' || daysFilter.length > 0 || knownForFilter || guestTierFilter !== 'all' || availabilityFilter !== 'all') && (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-gray-600">Active filters:</span>
                      {categoryFilter !== 'all' && (
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs">
                          {categoryFilter}
                        </span>
                      )}
                      {daysFilter.map(day => (
                        <span key={day} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {day}
                        </span>
                      ))}
                      {knownForFilter && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          {knownForFilter}
                        </span>
                      )}
                      {guestTierFilter !== 'all' && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          guestTierFilter === 'A' ? 'bg-rose-100 text-rose-800' :
                          guestTierFilter === 'B' ? 'bg-amber-100 text-amber-800' :
                          guestTierFilter === 'C' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          Tier {guestTierFilter}
                        </span>
                      )}
                      {availabilityFilter !== 'all' && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          availabilityFilter === 'Available' ? 'bg-green-100 text-green-800' :
                          availabilityFilter === 'Limited Availability' ? 'bg-yellow-100 text-yellow-800' :
                          availabilityFilter === 'Not Available' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {availabilityFilter}
                        </span>
                      )}
                      <button
                        onClick={() => {
                          setCategoryFilter('all');
                          setDaysFilter([]);
                          setKnownForFilter('');
                          setGuestTierFilter('all');
                          setAvailabilityFilter('all');
                        }}
                        className="text-gray-500 hover:text-gray-700 text-xs underline"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
                
                {celebrities.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">
                      {effectiveUser?.role === 'journalist' ? 'No guests available for interviews.' : 'No guests found.'}
                    </p>
                    {effectiveUser?.role !== 'journalist' && (
                      <div className="flex flex-col gap-2 items-center">
                        <button
                          onClick={openAddGuestModal}
                          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                        >
                          <UserPlus size={16} className="mr-2" />
                          Add Guest
                        </button>
                        <button
                          onClick={() => openUploadModal('celebrities')}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          Upload a CSV file to get started
                        </button>
                      </div>
                    )}
                  </div>
                ) : effectiveUser?.role === 'journalist' ? (
                  // Separated view for journalists - Available guests first, then Not Available
                  <div className="space-y-8">
                    {(() => {
                      // Apply filtering and sorting for journalists
                      let allFilteredCelebrities = filterCelebrities(celebrities, celebritySearchTerm, categoryFilter, daysFilter, knownForFilter, guestTierFilter, availabilityFilter);
                      allFilteredCelebrities = sortCelebrities(allFilteredCelebrities, celebritySortField, celebritySortDirection);

                      // Get current journalist's press tier
                      const currentJournalist = allJournalists.find(j => j.email === effectiveUser?.email || j.name === effectiveUser?.name);
                      const journalistTier = currentJournalist?.pressTier || 'C';

                      // Helper: check if journalist's tier grants access to a guest's priority
                      const hasTierAccess = (guestPriority: string) => {
                        if (guestPriority === 'Unassigned') return false;
                        if (journalistTier === 'A') return true;
                        if (journalistTier === 'B') return guestPriority === 'B' || guestPriority === 'C';
                        return guestPriority === 'C'; // journalistTier === 'C'
                      };

                      // A guest is "effectively available" for this journalist if tier access passes AND prAvailable is Available/Limited
                      const isGuestAvailable = (celebrity: typeof celebrities[0]) => {
                        if (!hasTierAccess(celebrity.priority)) return false;
                        return celebrity.prAvailable === 'Available' || celebrity.prAvailable === 'Limited Availability';
                      };

                      // Separate available vs not available guests
                      const availableCelebrities = allFilteredCelebrities.filter(celebrity => isGuestAvailable(celebrity));
                      const notAvailableCelebrities = allFilteredCelebrities.filter(celebrity => !isGuestAvailable(celebrity));
                      
                      const renderCelebrityCard = (celebrity) => {
                        const celebrityRequests = getRequestsByCelebrity(celebrity.name)
                        const userRequest = effectiveUser?.role === 'journalist' 
                          ? celebrityRequests.find(r => r.journalistName === effectiveUser?.name)
                          : null
                        
                        const getRequestButtonState = () => {
                          if (!userRequest) return 'available'
                          if (userRequest.status === 'new') return 'requested'
                          if (userRequest.status === 'pending') return 'requested'
                          if (userRequest.status === 'approved') return 'approved'
                          if (userRequest.status === 'scheduled') return 'approved'
                          if (userRequest.status === 'complete') return 'approved'
                          if (userRequest.status === 'declined') return 'rejected'
                          return 'available'
                        }
                        
                        const handleRequestInterview = async () => {
                          if (effectiveUser?.role !== 'journalist') return
                          
                          // If there's already a request, allow canceling it
                          if (userRequest) {
                            try {
                              console.log('Canceling request:', userRequest.id);
                              await deleteRequest(userRequest.id);
                              console.log('Request canceled successfully');
                            } catch (error) {
                              console.error('Failed to cancel request:', error);
                              alert('Failed to cancel interview request. Please try again.');
                            }
                            return;
                          }
                          
                          try {
                            console.log('Creating request for:', celebrity.name, 'by:', effectiveUser?.name);
                            
                            // Find the journalist in the database to get correct outlet info
                            const dbJournalist = allJournalists.find(j => j.email === effectiveUser?.email || j.name === effectiveUser?.name);
                            
                            const newRequest = await createRequest({
                              journalistId: dbJournalist?.id || '', 
                              journalistName: effectiveUser?.name || 'Unknown Journalist',
                              outlet: dbJournalist?.outlet || effectiveUser?.outlet || 'Unknown Outlet',
                              celebrities: [celebrity.name],
                              status: 'new',
                              priority: { [celebrity.name]: 'Unassigned' },
                              completed: { [celebrity.name]: false }
                            })
                            console.log('Request created successfully:', newRequest);
                          } catch (error) {
                            console.error('Failed to create request:', error)
                            alert('Failed to create interview request. Please try again.')
                          }
                        }
                        
                        const buttonState = getRequestButtonState()
                        // Guest is "not available" to this journalist if tier blocked OR prAvailable says so
                        const isNotAvailable = !isGuestAvailable(celebrity)
                        // Press only sees Available or Not Available
                        const displayPrStatus = isNotAvailable ? 'Not Available' : 'Available'

                        const categoryColors = getCategoryColors((celebrity.category || [])[0] || '');
                        const notAvailableStyles = isNotAvailable ? getNotAvailableStyles('Not Available') : null;
                        const cardStyles = notAvailableStyles || categoryColors;

                        return (
                          <div
                            key={celebrity.id}
                            className={`rounded-xl p-4 transition-all shadow-sm hover:shadow-md overflow-hidden ${
                              isNotAvailable
                                ? `${notAvailableStyles.bg} opacity-75`
                                : `bg-white`
                            }`}
                            style={{ borderLeft: `4px solid ${isNotAvailable ? '#94a3b8' : '#e91e63'}` }}
                          >
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <h3 className={`font-medium ${isNotAvailable ? 'text-gray-600' : 'text-gray-900'}`}>
                                {celebrity.name}
                              </h3>
                              {(celebrity.category || []).map((cat, i) => (
                                <span key={i} className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                  isNotAvailable ? 'bg-gray-200 text-gray-600' : getCategoryColors(cat).badge
                                }`}>
                                  {cat}
                                </span>
                              ))}
                              {isNotAvailable && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                                  Not Available
                                </span>
                              )}
                              {!isNotAvailable ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestInterview();
                                  }}
                                  className={`ml-auto px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                    buttonState === 'available'
                                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                      : buttonState === 'requested'
                                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 cursor-pointer'
                                      : buttonState === 'approved'
                                      ? 'bg-green-100 text-green-800 cursor-default'
                                      : 'bg-red-100 text-red-800 cursor-default'
                                  }`}
                                >
                                  {buttonState === 'available' && 'Request Interview'}
                                  {buttonState === 'requested' && 'Cancel Request'}
                                  {buttonState === 'approved' && 'Complete'}
                                  {buttonState === 'rejected' && 'Declined'}
                                </button>
                              ) : (
                                <div className="ml-auto px-4 py-1.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-500 cursor-not-allowed whitespace-nowrap">
                                  Not Available
                                </div>
                              )}
                            </div>

                            <div className={`text-sm mb-2 ${isNotAvailable ? 'text-gray-500' : 'text-gray-600'}`}>
                              {renderKnownForCredits(celebrity.knownFor)}
                            </div>
                            <p className={`text-sm ${isNotAvailable ? 'text-gray-500' : 'text-gray-600'}`}>
                              PR Status: {displayPrStatus}
                            </p>

                            {celebrity.days && celebrity.days.length > 0 && (
                              <p className={`text-xs mt-1 ${isNotAvailable ? 'text-gray-400' : 'text-blue-600'}`}>
                                At show: {celebrity.days.join(', ')}
                              </p>
                            )}
                          </div>
                        )
                      };
                      
                      return (
                        <>
                          {/* Available Guests Section */}
                          {availableCelebrities.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                  Available for Interviews ({availableCelebrities.length})
                                </h3>
                              </div>
                              <div className="grid gap-4">
                                {availableCelebrities.map(renderCelebrityCard)}
                              </div>
                            </div>
                          )}
                          
                          {/* Not Available Guests Section */}
                          {notAvailableCelebrities.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-600">
                                  Not Available ({notAvailableCelebrities.length})
                                </h3>
                                <p className="text-sm text-gray-500">
                                  These guests are currently not available for interviews
                                </p>
                              </div>
                              <div className="grid gap-4">
                                {notAvailableCelebrities.map(renderCelebrityCard)}
                              </div>
                            </div>
                          )}
                          
                          {/* No results message */}
                          {availableCelebrities.length === 0 && notAvailableCelebrities.length === 0 && (
                            <div className="text-center py-8">
                              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                              <p className="text-gray-500 mb-4">No guests match your current filters.</p>
                              <p className="text-sm text-gray-400">Try adjusting your search or filter criteria.</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  // Mobile-optimized cards for PR team with desktop table fallback
                  <div>
                    {(() => {
                      // Apply filtering and sorting
                      let displayCelebrities = filterCelebrities(celebrities, celebritySearchTerm, categoryFilter, daysFilter, knownForFilter, guestTierFilter, availabilityFilter);
                      displayCelebrities = sortCelebrities(displayCelebrities, celebritySortField, celebritySortDirection);

                      if (displayCelebrities.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 mb-4">
                              {celebritySearchTerm ? 'No guests found matching your search.' : 'No guests found.'}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <>
                          {/* Mobile Card Layout */}
                          <div className="lg:hidden space-y-4">
                            {displayCelebrities.map(celebrity => {
                              const celebrityRequests = getRequestsByCelebrity(celebrity.name)
                              const pendingRequests = celebrityRequests.filter(r => r.status === 'new' || r.status === 'pending')
                              const isNotAvailable = celebrity.prAvailable === 'Not Available';
                              
                              return (
                                <div key={celebrity.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden ${isNotAvailable ? 'opacity-80' : ''}`} style={{ borderLeft: `4px solid ${isNotAvailable ? '#ef4444' : '#22c55e'}` }}>
                                  <div className="p-4">
                                    {/* Header with name and availability */}
                                    <div className="flex items-start justify-between mb-3">
                                      <button
                                        onClick={() => openCelebrityModal(celebrity)}
                                        className="font-semibold text-lg text-left touch-manipulation"
                                        style={{ color: 'var(--fanexpo-magenta)' }}
                                      >
                                        {celebrity.name}
                                      </button>
                                      <div className="ml-3">
                                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                          celebrity.prAvailable === 'Available' ? 'bg-green-50 text-green-700 border-green-200' :
                                          celebrity.prAvailable === 'Limited Availability' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                          celebrity.prAvailable === 'Not Available' ? 'bg-red-50 text-red-700 border-red-200' :
                                          'bg-gray-50 text-gray-600 border-gray-200'
                                        }`}>
                                          {celebrity.prAvailable || 'TBD'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Category and Known For */}
                                    <div className="space-y-2 mb-3">
                                      <div className="flex items-center">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-20">Category</span>
                                        <span className="text-sm text-gray-900">{(celebrity.category || []).join(', ')}</span>
                                      </div>
                                      <div className="flex items-start">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-20 mt-0.5">Known For</span>
                                        <div className="text-sm text-gray-900 flex-1">
                                          {renderKnownForCredits(celebrity.knownFor)}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Agent and Days */}
                                    <div className="grid grid-cols-2 gap-4 mb-3 bg-gray-50 rounded-lg p-3 -mx-1">
                                      <div>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Agent</span>
                                        <span className="text-sm font-medium text-gray-900">{getAgentDisplayName(celebrity)}</span>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Days</span>
                                        <span className="text-sm font-medium text-gray-900">
                                          {celebrity.days && celebrity.days.length > 0 ? celebrity.days.join(', ') : '-'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Priority - PR/Admin only */}
                                    {isPRTeam && (
                                    <div className="flex items-center gap-2 mb-3">
                                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Priority</span>
                                      <select
                                        value={celebrity.priority}
                                        onChange={(e) => updateGuest(celebrity.id, { priority: e.target.value as 'A' | 'B' | 'C' | 'Unassigned' })}
                                        className={`text-xs px-2 py-1 border rounded font-medium ${
                                          celebrity.priority === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                                          celebrity.priority === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                                          celebrity.priority === 'C' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                                          'bg-gray-50 border-gray-300 text-gray-500'
                                        }`}
                                      >
                                        <option value="Unassigned">-</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                      </select>
                                    </div>
                                    )}

                                    {/* Pending Requests Info */}
                                    {pendingRequests.length > 0 && (
                                      <div className="pt-3 border-t border-gray-100">
                                        <span className="text-xs font-medium text-orange-600">
                                          {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Desktop Table Layout */}
                          <div className="hidden lg:block overflow-x-auto">
                            <div className="min-w-full bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                              {/* Table Header */}
                              <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10">
                                <div className="flex px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  <div className="relative flex items-center" style={{ width: celebrityColumnWidths.celebrity }}>
                                    <button 
                                      onClick={() => handleCelebritySort('celebrity')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Guest
                                      {celebritySortField === 'celebrity' && (
                                        <span className="ml-1 text-indigo-600">{celebritySortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleCelebrityMouseDown(e, 'celebrity')}
                                    />
                                  </div>
                                  <div className="relative flex items-center" style={{ width: celebrityColumnWidths.category }}>
                                    <button 
                                      onClick={() => handleCelebritySort('category')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Category
                                      {celebritySortField === 'category' && (
                                        <span className="ml-1 text-indigo-600">{celebritySortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleCelebrityMouseDown(e, 'category')}
                                    />
                                  </div>
                                  <div className="relative flex items-center" style={{ width: celebrityColumnWidths.knownFor }}>
                                    <button 
                                      onClick={() => handleCelebritySort('knownFor')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Known For
                                      {celebritySortField === 'knownFor' && (
                                        <span className="ml-1 text-indigo-600">{celebritySortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleCelebrityMouseDown(e, 'knownFor')}
                                    />
                                  </div>
                                  <div className="relative flex items-center" style={{ width: celebrityColumnWidths.agent }}>
                                    <button 
                                      onClick={() => handleCelebritySort('agent')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Agent
                                      {celebritySortField === 'agent' && (
                                        <span className="ml-1 text-indigo-600">{celebritySortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleCelebrityMouseDown(e, 'agent')}
                                    />
                                  </div>
                                  <div className="relative flex items-center flex-1 min-w-0">
                                    <div>Days</div>
                                  </div>
                                  {isPRTeam && (
                                  <div className="relative flex items-center w-20">
                                    <div>Priority</div>
                                  </div>
                                  )}
                                  <div className="relative flex items-center" style={{ width: celebrityColumnWidths.availability }}>
                                    <div>Interview Availability</div>
                                    <div
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleCelebrityMouseDown(e, 'availability')}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Table Body */}
                              <div className="divide-y divide-gray-200">
                                {displayCelebrities.map(celebrity => {
                                  const celebrityRequests = getRequestsByCelebrity(celebrity.name)
                                  const pendingRequests = celebrityRequests.filter(r => r.status === 'new' || r.status === 'pending')
                                  const categoryColors = getCategoryColors((celebrity.category || [])[0] || '');
                                  const isNotAvailable = celebrity.prAvailable === 'Not Available';
                                  
                                  return (
                                    <div key={celebrity.id} className={`transition-colors duration-150 ${categoryColors.bg} hover:bg-slate-50`}>
                                      <div className="flex px-4 py-3 items-center">
                                        <div style={{ width: celebrityColumnWidths.celebrity }}>
                                          <button 
                                            onClick={() => openCelebrityModal(celebrity)}
                                            className="font-medium text-indigo-600 hover:text-indigo-800 text-left text-sm transition-colors"
                                          >
                                            {celebrity.name}
                                          </button>
                                        </div>
                                        <div style={{ width: celebrityColumnWidths.category }}>
                                          <div className="flex flex-wrap gap-1">
                                            {(celebrity.category || []).map((cat, i) => (
                                              <span key={i} className={`text-xs px-1.5 py-0.5 rounded-full ${getCategoryColors(cat).badge}`}>{cat}</span>
                                            ))}
                                          </div>
                                        </div>
                                        <div style={{ width: celebrityColumnWidths.knownFor }} className="self-start pt-0.5">
                                          <div className="text-sm">
                                            {renderKnownForCredits(celebrity.knownFor)}
                                          </div>
                                        </div>
                                        <div style={{ width: celebrityColumnWidths.agent }}>
                                          <div className="text-sm text-gray-900 truncate">{getAgentDisplayName(celebrity)}</div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm">
                                            {celebrity.days && celebrity.days.length > 0 ? (
                                              <span className="text-sm text-gray-700">
                                                {celebrity.days.join(', ')}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </div>
                                        </div>
                                        {isPRTeam && (
                                        <div className="w-20">
                                          <select
                                            value={celebrity.priority}
                                            onChange={(e) => updateGuest(celebrity.id, { priority: e.target.value as 'A' | 'B' | 'C' | 'Unassigned' })}
                                            className={`text-xs px-1.5 py-1 border rounded font-medium ${
                                              celebrity.priority === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                                              celebrity.priority === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                                              celebrity.priority === 'C' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                                              'bg-gray-50 border-gray-300 text-gray-500'
                                            }`}
                                          >
                                            <option value="Unassigned">-</option>
                                            <option value="A">A</option>
                                            <option value="B">B</option>
                                            <option value="C">C</option>
                                          </select>
                                        </div>
                                        )}
                                        <div style={{ width: celebrityColumnWidths.availability }}>
                                          <div className="flex items-center">
                                            <span className={`text-sm font-medium ${
                                              isNotAvailable ? 'text-red-600' : 'text-green-600'
                                            }`}>
                                              {celebrity.prAvailable}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'journalists' && effectiveUser?.role !== 'journalist' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                  <h2 className="section-header">Journalists</h2>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full">
                    {/* Search Bar - Mobile optimized */}
                    <div className="relative flex-1 sm:flex-initial">
                      <input
                        type="text"
                        placeholder="Search journalists, outlets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:w-64 pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent touch-manipulation"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 sm:h-4 sm:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                      <button
                        onClick={openAddJournalistModal}
                        className="flex items-center justify-center px-4 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-base sm:text-sm font-medium touch-manipulation"
                      >
                        <UserPlus size={20} className="mr-2 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Add Journalist</span>
                        <span className="sm:hidden">Add Journalist</span>
                      </button>
                      <button
                        onClick={() => openUploadModal('journalists')}
                        className="flex items-center justify-center px-4 py-3 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-base sm:text-sm font-medium touch-manipulation"
                      >
                        <Upload size={20} className="mr-2 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Upload CSV</span>
                        <span className="sm:hidden">Upload CSV</span>
                      </button>
                      <button
                        onClick={downloadJournalistTemplate}
                        className="flex items-center justify-center px-4 py-3 sm:py-2 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-base sm:text-sm font-medium touch-manipulation"
                      >
                        <Download size={20} className="mr-2 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">CSV Template</span>
                        <span className="sm:hidden">CSV Template</span>
                      </button>
                      {effectiveUser?.role !== 'journalist' && selectedJournalistsForInvite.size > 0 && (
                        <button
                          onClick={handleSendBulkInvitations}
                          disabled={isSendingBulkInvites}
                          className="flex items-center justify-center px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-base sm:text-sm font-medium touch-manipulation"
                        >
                          <Mail size={20} className="mr-2 sm:w-4 sm:h-4" />
                          {isSendingBulkInvites ? (
                            <>Sending... ({selectedJournalistsForInvite.size})</>
                          ) : (
                            <>Send Invitations ({selectedJournalistsForInvite.size})</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Credentials Filter Bar - Only show for PR team */}
                {effectiveUser?.role !== 'journalist' && allJournalists.length > 0 && (
                  <div className="mb-6 bg-gray-50 rounded-xl p-4">
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const credentialsPickedUp = allJournalists.filter(j => j.credentialsPickedUp).length;
                        const credentialsNotPickedUp = allJournalists.length - credentialsPickedUp;
                        
                        return (
                          <>
                            <button 
                              onClick={() => setCredentialsFilter('all')}
                              className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                                credentialsFilter === 'all'
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                              }`}
                            >
                              All ({allJournalists.length})
                            </button>
                            <button 
                              onClick={() => setCredentialsFilter('picked-up')}
                              className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                                credentialsFilter === 'picked-up'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              Picked Up Credentials ({credentialsPickedUp})
                            </button>
                            <button 
                              onClick={() => setCredentialsFilter('not-picked-up')}
                              className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                                credentialsFilter === 'not-picked-up'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              Did Not Pick Up Credentials ({credentialsNotPickedUp})
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {/* Press Tier Filter */}
                    <div className="mt-3">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Press Tier</h3>
                      <div className="flex flex-wrap gap-2">
                        {['all', 'A', 'B', 'C'].map(tier => (
                          <button
                            key={tier}
                            onClick={() => setJournalistTierFilter(tier)}
                            className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                              journalistTierFilter === tier
                                ? tier === 'A' ? 'bg-rose-600 text-white'
                                : tier === 'B' ? 'bg-amber-600 text-white'
                                : tier === 'C' ? 'bg-emerald-600 text-white'
                                : 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {tier === 'all' ? 'All Tiers' : `Tier ${tier}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {allJournalists.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">No journalists found.</p>
                    <div className="flex flex-col gap-2 items-center">
                      <button
                        onClick={openAddJournalistModal}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                      >
                        <UserPlus size={16} className="mr-2" />
                        Add Journalist
                      </button>
                      <button
                        onClick={() => openUploadModal('journalists')}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Upload a CSV file to get started
                      </button>
                    </div>
                  </div>
                ) : (
                  // Mobile-optimized cards for journalists with desktop table fallback
                  <div>
                    {(() => {
                      // Apply filtering and sorting
                      let displayJournalists = filterJournalists(allJournalists, searchTerm);
                      
                      // Apply credentials filter for PR team
                      if (effectiveUser?.role !== 'journalist' && credentialsFilter !== 'all') {
                        if (credentialsFilter === 'picked-up') {
                          displayJournalists = displayJournalists.filter(j => j.credentialsPickedUp);
                        } else if (credentialsFilter === 'not-picked-up') {
                          displayJournalists = displayJournalists.filter(j => !j.credentialsPickedUp);
                        }
                      }

                      // Apply tier filter
                      if (journalistTierFilter !== 'all') {
                        displayJournalists = displayJournalists.filter(j => j.pressTier === journalistTierFilter);
                      }
                      
                      displayJournalists = sortJournalists(displayJournalists, journalistSortField, journalistSortDirection);

                      if (displayJournalists.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 mb-4">
                              {searchTerm ? 'No journalists found matching your search.' : 'No journalists found.'}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <>
                          {/* Mobile Card Layout */}
                          <div className="lg:hidden space-y-4">
                            {displayJournalists.map(journalist => {
                              const journalistRequests = getRequestsByJournalist(journalist.name)
                              const pendingRequests = journalistRequests.filter(r => r.status === 'new' || r.status === 'pending')
                              
                              return (
                                <div key={journalist.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden" style={{ borderLeft: `4px solid ${journalist.verified ? '#22c55e' : 'var(--acacia-blue)'}` }}>
                                  <div className="p-4">
                                    {/* Header with name and verification status */}
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex-1">
                                        <button
                                          onClick={() => openJournalistModal(journalist.name)}
                                          className="font-semibold text-lg text-left touch-manipulation"
                                          style={{ color: 'var(--fanexpo-magenta)' }}
                                        >
                                          {journalist.name}
                                        </button>
                                        {journalist.verified && (
                                          <span className="inline-flex items-center px-2 py-1.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 mt-1">
                                            <Check size={12} className="mr-1" />
                                            Verified
                                          </span>
                                        )}
                                      </div>
                                      {effectiveUser?.role !== 'journalist' && (
                                        <div className="ml-3 space-y-3">
                                          <div>
                                            <label className="flex items-center cursor-pointer touch-manipulation">
                                              <input
                                                type="checkbox"
                                                checked={journalist.credentialsPickedUp || false}
                                                onChange={(e) => updateJournalistCredentials(journalist.id, e.target.checked)}
                                                className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 touch-manipulation"
                                              />
                                              <span className="ml-2 text-sm font-medium text-gray-700">
                                                Credentials
                                              </span>
                                            </label>
                                            <span className="text-xs text-gray-500 mt-1 block ml-7">
                                              {journalist.credentialsPickedUp ? 'Picked Up' : 'Not Picked Up'}
                                            </span>
                                          </div>
                                          <div>
                                            <label className="flex items-center cursor-pointer touch-manipulation">
                                              <input
                                                type="checkbox"
                                                checked={selectedJournalistsForInvite.has(journalist.email)}
                                                onChange={() => toggleJournalistSelection(journalist.email)}
                                                className="w-5 h-5 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 touch-manipulation"
                                              />
                                              <span className="ml-2 text-sm font-medium text-gray-700">
                                                Send Invite
                                              </span>
                                            </label>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Outlet */}
                                    <div className="mb-3">
                                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Outlet</span>
                                      <span className="text-sm font-medium text-gray-900 ml-2">{journalist.outlet}</span>
                                    </div>

                                    {/* Contact Information */}
                                    <div className="bg-gray-50 rounded-lg p-3 -mx-1 mb-3 space-y-2">
                                      <div>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Email</span>
                                        <a href={`mailto:${journalist.email}`} className="text-sm font-medium touch-manipulation" style={{ color: 'var(--acacia-blue)' }}>
                                          {journalist.email}
                                        </a>
                                      </div>
                                      {journalist.phone && (
                                        <div>
                                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Phone</span>
                                          <a href={`tel:${journalist.phone}`} className="text-sm font-medium touch-manipulation" style={{ color: 'var(--acacia-blue)' }}>
                                            {journalist.phone}
                                          </a>
                                        </div>
                                      )}
                                    </div>

                                    {/* Press Tier - PR only */}
                                    {effectiveUser?.role !== 'journalist' && (
                                      <div className="flex items-center gap-2 mb-3">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Press Tier</span>
                                        <select
                                          value={journalist.pressTier}
                                          onChange={(e) => updateJournalist(journalist.id, { pressTier: e.target.value as 'A' | 'B' | 'C' })}
                                          className={`text-xs px-2 py-1 border rounded font-medium ${
                                            journalist.pressTier === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                                            journalist.pressTier === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                                            'bg-emerald-50 border-emerald-300 text-emerald-700'
                                          }`}
                                        >
                                          <option value="A">A</option>
                                          <option value="B">B</option>
                                          <option value="C">C</option>
                                        </select>
                                      </div>
                                    )}

                                    {/* Interview Requests Info */}
                                    {pendingRequests.length > 0 && (
                                      <div className="pt-3 border-t border-gray-100">
                                        <span className="text-xs font-medium text-orange-600">
                                          {pendingRequests.length} pending interview request{pendingRequests.length !== 1 ? 's' : ''}
                                        </span>
                                      </div>
                                    )}

                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Desktop Table Layout */}
                          <div className="hidden lg:block overflow-x-auto">
                            <div className="min-w-full bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                              {/* Table Header */}
                              <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10">
                                <div className="flex px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  <div className="relative flex items-center" style={{ width: journalistColumnWidths.journalist }}>
                                    <button 
                                      onClick={() => handleJournalistSort('journalist')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Journalist
                                      {journalistSortField === 'journalist' && (
                                        <span className="ml-1 text-indigo-600">{journalistSortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleJournalistMouseDown(e, 'journalist')}
                                    />
                                  </div>
                                  <div className="relative flex items-center" style={{ width: journalistColumnWidths.outlet }}>
                                    <button 
                                      onClick={() => handleJournalistSort('outlet')}
                                      className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                    >
                                      Outlet
                                      {journalistSortField === 'outlet' && (
                                        <span className="ml-1 text-indigo-600">{journalistSortDirection === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </button>
                                    <div 
                                      className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                      onMouseDown={(e) => handleJournalistMouseDown(e, 'outlet')}
                                    />
                                  </div>
                                  {effectiveUser?.role !== 'journalist' && (
                                    <div className="relative flex items-center" style={{ width: journalistColumnWidths.credentials }}>
                                      <div>Credentials</div>
                                      <div 
                                        className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full"
                                        onMouseDown={(e) => handleJournalistMouseDown(e, 'credentials')}
                                      />
                                    </div>
                                  )}
                                  {effectiveUser?.role !== 'journalist' && (
                                    <div className="relative flex items-center w-20">
                                      <div>Tier</div>
                                    </div>
                                  )}
                                  {effectiveUser?.role !== 'journalist' && (
                                    <div className="relative flex items-center" style={{ width: '140px' }}>
                                      <div className="flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={selectAllInvitations}
                                          onChange={() => toggleSelectAllInvitations(displayJournalists)}
                                          className="w-4 h-4 text-indigo-600 mr-2"
                                        />
                                        Send Invitation
                                      </div>
                                    </div>
                                  )}
                                  <div className="relative flex items-center flex-1 min-w-0">
                                    <div>Contact</div>
                                  </div>
                                  {effectiveUser?.role !== 'journalist' && (
                                    <div className="w-32 flex justify-end">
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Table Body */}
                              <div className="divide-y divide-gray-200">
                                {displayJournalists.map(journalist => {
                                  const journalistRequests = getRequestsByJournalist(journalist.name)
                                  const pendingRequests = journalistRequests.filter(r => r.status === 'new' || r.status === 'pending')
                                  
                                  return (
                                    <div key={journalist.id} className="hover:bg-slate-50 transition-colors duration-150">
                                      <div className="flex px-4 py-3 items-center">
                                        <div style={{ width: journalistColumnWidths.journalist }}>
                                          <button 
                                            onClick={() => openJournalistModal(journalist.name)}
                                            className="font-medium text-indigo-600 hover:text-indigo-800 text-left text-sm transition-colors"
                                          >
                                            {journalist.name}
                                          </button>
                                          {journalist.verified && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-800 ml-2">
                                              <Check size={10} className="mr-1" />
                                              Verified
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ width: journalistColumnWidths.outlet }}>
                                          <div className="text-sm text-gray-900">{journalist.outlet}</div>
                                        </div>
                                        {effectiveUser?.role !== 'journalist' && (
                                          <div style={{ width: journalistColumnWidths.credentials }}>
                                            <div className="flex items-center justify-center">
                                              <label className="flex items-center cursor-pointer">
                                                <input
                                                  type="checkbox"
                                                  checked={journalist.credentialsPickedUp || false}
                                                  onChange={(e) => updateJournalistCredentials(journalist.id, e.target.checked)}
                                                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                                                />
                                                <span className="ml-2 text-xs text-gray-600">
                                                  {journalist.credentialsPickedUp ? 'Picked Up' : 'Not Picked Up'}
                                                </span>
                                              </label>
                                            </div>
                                          </div>
                                        )}
                                        {effectiveUser?.role !== 'journalist' && (
                                          <div className="w-20">
                                            <select
                                              value={journalist.pressTier}
                                              onChange={(e) => updateJournalist(journalist.id, { pressTier: e.target.value as 'A' | 'B' | 'C' })}
                                              className={`text-xs px-1.5 py-1 border rounded font-medium ${
                                                journalist.pressTier === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                                                journalist.pressTier === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                                                'bg-emerald-50 border-emerald-300 text-emerald-700'
                                              }`}
                                            >
                                              <option value="A">A</option>
                                              <option value="B">B</option>
                                              <option value="C">C</option>
                                            </select>
                                          </div>
                                        )}
                                        {effectiveUser?.role !== 'journalist' && (
                                          <div style={{ width: '140px' }}>
                                            <div className="flex items-center justify-center">
                                              <input
                                                type="checkbox"
                                                checked={selectedJournalistsForInvite.has(journalist.email)}
                                                onChange={() => toggleJournalistSelection(journalist.email)}
                                                className="w-4 h-4 text-indigo-600"
                                              />
                                            </div>
                                          </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm">
                                            <a href={`mailto:${journalist.email}`} className="text-blue-600 hover:text-blue-800 block truncate">
                                              {journalist.email}
                                            </a>
                                            {journalist.phone && (
                                              <a href={`tel:${journalist.phone}`} className="text-blue-600 hover:text-blue-800 text-xs block">
                                                {journalist.phone}
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'agents' && effectiveUser?.role !== 'journalist' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <h2 className="section-header">Agents ({(() => {
                    const agentIds = new Set();
                    celebrities.forEach(celebrity => {
                      (celebrity.agents || []).forEach(a => agentIds.add(a.agentId));
                    });
                    return agentIds.size;
                  })()})</h2>
                  
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => {
                        setAddAgentSelectedGuests([]);
                        setAddAgentGuestSearch('');
                        setShowAddAgentModal(true);
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1 whitespace-nowrap"
                    >
                      <UserPlus size={16} />
                      Add Agent
                    </button>
                    <div className="relative flex-1 sm:w-64">
                      <input
                        type="text"
                        placeholder="Search agents or clients..."
                        value={agentSearchTerm}
                        onChange={(e) => setAgentSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent touch-manipulation"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 sm:h-4 sm:w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {celebrities.length === 0 ? (
                  <div className="text-center py-12">
                    <Users size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-2">No agents found</p>
                    <p className="text-sm text-gray-400">
                      Agents appear when you add guests/celebrities with agent information.
                    </p>
                  </div>
                ) : (
                  (() => {
                    // Build agents map from junction data on celebrities
                    const agentsMap = new Map();

                    celebrities.forEach(celebrity => {
                      (celebrity.agents || []).forEach((ga: any) => {
                        if (!agentsMap.has(ga.agentId)) {
                          agentsMap.set(ga.agentId, {
                            id: ga.agentId,
                            name: ga.name,
                            agency: ga.agency,
                            email: ga.email,
                            phone: ga.phone,
                            clients: [{ ...celebrity, isPrimaryForAgent: ga.isPrimary }]
                          });
                        } else {
                          agentsMap.get(ga.agentId).clients.push({ ...celebrity, isPrimaryForAgent: ga.isPrimary });
                        }
                      });
                    });
                    
                    const allAgents = Array.from(agentsMap.values());
                    
                    // Filter agents based on search
                    const filteredAgents = allAgents.filter(agent => {
                      if (!agentSearchTerm) return true;
                      const searchLower = agentSearchTerm.toLowerCase();
                      
                      // Search in agent info
                      const agentMatch = 
                        agent.name.toLowerCase().includes(searchLower) ||
                        (agent.email || '').toLowerCase().includes(searchLower);
                      
                      // Search in their clients
                      const clientMatch = agent.clients.some(client => 
                        client.name.toLowerCase().includes(searchLower)
                      );
                      
                      return agentMatch || clientMatch;
                    });
                    
                    // Sort agents alphabetically
                    filteredAgents.sort((a, b) => a.name.localeCompare(b.name));
                    
                    return (
                      <div className="space-y-4">
                        {filteredAgents.map((agent, index) => {
                          const totalRequests = agent.clients.reduce((total, celebrity) => 
                            total + getRequestsByCelebrity(celebrity.name).length, 0
                          );
                          const pendingRequests = agent.clients.reduce((total, celebrity) => 
                            total + getRequestsByCelebrity(celebrity.name).filter(r => r.status === 'new' || r.status === 'pending').length, 0
                          );
                          
                          return (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                              <div className="p-6">
                                {/* Agent Header */}
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <h3 className="text-lg font-medium text-gray-900">{agent.name}</h3>
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                      {agent.email && (
                                        <a href={`mailto:${agent.email}`} className="text-blue-600 hover:text-blue-800">
                                          {agent.email}
                                        </a>
                                      )}
                                      {agent.phone && (
                                        <a href={`tel:${agent.phone}`} className="text-blue-600 hover:text-blue-800">
                                          {agent.phone}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        setSelectedAgent(agent);
                                        setEditAgentForm({ name: agent.name || '', email: agent.email || '', phone: agent.phone || '' });
                                        setShowAgentModal(true);
                                      }}
                                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                                    >
                                      Edit Agent
                                    </button>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                                      {agent.clients.length} client{agent.clients.length !== 1 ? 's' : ''}
                                    </span>
                                    {totalRequests > 0 && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                                        {totalRequests} request{totalRequests !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                    {pendingRequests > 0 && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                                        <Clock size={14} className="mr-1" />
                                        {pendingRequests} pending
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Clients Grid */}
                                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                  {agent.clients.map(celebrity => {
                                    const celebrityRequests = getRequestsByCelebrity(celebrity.name);
                                    const pendingRequests = celebrityRequests.filter(r => r.status === 'new' || r.status === 'pending');
                                    const categoryColors = getCategoryColors((celebrity.category || [])[0] || '');
                                    const notAvailableStyles = getNotAvailableStyles(celebrity.prAvailable);
                                    const isNotAvailable = celebrity.prAvailable === 'Not Available';
                                    const cardStyles = notAvailableStyles || categoryColors;
                                    
                                    return (
                                      <div key={celebrity.id} className={`p-3 rounded border hover:shadow-sm transition-shadow ${
                                        isNotAvailable 
                                          ? `${notAvailableStyles.bg} ${notAvailableStyles.border}` 
                                          : `${cardStyles.bg} ${cardStyles.border} bg-white`
                                      }`}>
                                        <div className="flex items-start justify-between mb-2">
                                          <button
                                            onClick={() => openCelebrityModal(celebrity)}
                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors text-left truncate flex-1"
                                          >
                                            {celebrity.name}
                                          </button>
                                          <div className="flex gap-1 ml-2">
                                            {celebrityRequests.length > 0 && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                                {celebrityRequests.length}
                                              </span>
                                            )}
                                            {pendingRequests.length > 0 && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                                <Clock size={8} className="mr-1" />
                                                {pendingRequests.length}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        
                                        <div className="space-y-1 text-xs text-gray-600">
                                          <p><span className="font-medium">Category:</span> 
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs ml-1 ${
                                              isNotAvailable ? 'bg-gray-200 text-gray-600' : categoryColors.badge
                                            }`}>
                                              {(celebrity.category || []).join(', ')}
                                            </span>
                                            {isNotAvailable && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700 ml-1">
                                                Not Available
                                              </span>
                                            )}
                                          </p>
                                          <p><span className="font-medium">Known for:</span> {celebrity.knownFor}</p>
                                          {celebrity.days && celebrity.days.length > 0 && (
                                            <p><span className="font-medium">Available:</span> {celebrity.days.join(', ')}</p>
                                          )}
                                          <p><span className="font-medium">PR:</span> {celebrity.prAvailable}</p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                )}
              </div>
            )}

            {activeTab === 'requests' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {effectiveUser?.role === 'journalist' ? 'My Interview Requests' : 'Interview Requests'}
                  </h2>
                  
                  {/* Controls - only show for PR team */}
                  {effectiveUser?.role !== 'journalist' && (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      {/* Add Interview Request Button */}
                      {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (
                        <button
                          onClick={() => setShowAddRequestModal(true)}
                          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Interview Request
                        </button>
                      )}
                      
                      {/* Search Input */}
                      <div className="relative w-full sm:w-64">
                        <input
                          type="text"
                          placeholder="Search journalists, outlets, or guests..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full sm:w-64 pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent touch-manipulation"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* View Toggle */}
                      <div className="flex items-center bg-white border-2 border-indigo-200 rounded-lg p-1 shadow-sm">
                        <button
                          onClick={() => setRequestsViewMode('by-journalist')}
                          className={`px-3 sm:px-4 py-2 rounded-md text-sm font-bold transition-all touch-manipulation ${
                            requestsViewMode === 'by-journalist'
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'text-indigo-600 hover:bg-indigo-50'
                          }`}
                        >
                          <span className="hidden sm:inline">By Journalist</span>
                          <span className="sm:hidden">Journalist</span>
                        </button>
                        <button
                          onClick={() => setRequestsViewMode('by-guest')}
                          className={`px-3 sm:px-4 py-2 rounded-md text-sm font-bold transition-all touch-manipulation ${
                            requestsViewMode === 'by-guest'
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'text-indigo-600 hover:bg-indigo-50'
                          }`}
                        >
                          <span className="hidden sm:inline">By Guest</span>
                          <span className="sm:hidden">Guest</span>
                        </button>
                      </div>
                      
                      {/* Cleanup Button - Hidden for production */}
                      {false && (
                        <button
                          onClick={executeCleanupOrphanedRequests}
                          className="px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors touch-manipulation"
                          title="Remove interview requests from deleted journalists"
                        >
                          <span className="hidden sm:inline">Clean Up</span>
                          <span className="sm:hidden">🧹</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Request Filters - only show for PR team */}
                {effectiveUser?.role !== 'journalist' && (
                  <div className="mb-6 space-y-3">
                    {/* Credentials Filter */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm font-medium text-gray-700 py-2">Credentials:</span>
                      <button 
                        onClick={() => setRequestCredentialsFilter('all')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          requestCredentialsFilter === 'all' 
                            ? 'bg-gray-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      <button 
                        onClick={() => setRequestCredentialsFilter('picked-up')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          requestCredentialsFilter === 'picked-up' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        Picked Up
                      </button>
                      <button 
                        onClick={() => setRequestCredentialsFilter('not-picked-up')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          requestCredentialsFilter === 'not-picked-up' 
                            ? 'bg-red-600 text-white' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        Not Picked Up
                      </button>
                    </div>

                    {/* Priority Filter */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm font-medium text-gray-700 py-2">Priority:</span>
                      <button 
                        onClick={() => setPriorityFilter('all')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          priorityFilter === 'all' 
                            ? 'bg-gray-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      <button 
                        onClick={() => setPriorityFilter('A')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          priorityFilter === 'A' 
                            ? 'bg-red-600 text-white' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        A Priority
                      </button>
                      <button 
                        onClick={() => setPriorityFilter('B')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          priorityFilter === 'B' 
                            ? 'bg-yellow-600 text-white' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        B Priority
                      </button>
                      <button 
                        onClick={() => setPriorityFilter('C')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          priorityFilter === 'C' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        C Priority
                      </button>
                      <button 
                        onClick={() => setPriorityFilter('unassigned')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          priorityFilter === 'unassigned' 
                            ? 'bg-gray-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Unassigned
                      </button>
                    </div>

                    {/* Status Filter */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm font-medium text-gray-700 py-2">Status:</span>
                      <button 
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'all' 
                            ? 'bg-gray-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All ({interviewRequests.length})
                      </button>
                      <button 
                        onClick={() => setStatusFilter('new')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'new' 
                            ? 'bg-orange-600 text-white' 
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        New ({getRequestsByStatus('new').length})
                      </button>
                      <button 
                        onClick={() => setStatusFilter('pending')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'pending' 
                            ? 'bg-yellow-600 text-white' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        Pending ({getRequestsByStatus('pending').length})
                      </button>
                      <button
                        onClick={() => setStatusFilter('approved')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'approved'
                            ? 'bg-green-600 text-white'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        Approved ({getRequestsByStatus('approved').length})
                      </button>
                      <button
                        onClick={() => setStatusFilter('scheduled')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'scheduled'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        Scheduled ({getRequestsByStatus('scheduled').length})
                      </button>
                      <button
                        onClick={() => setStatusFilter('complete')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'complete'
                            ? 'bg-emerald-700 text-white'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        }`}
                      >
                        Complete ({getRequestsByStatus('complete').length})
                      </button>
                      <button 
                        onClick={() => setStatusFilter('declined')}
                        className={`px-3 py-2 sm:py-1 rounded-full text-sm font-medium transition-colors touch-manipulation ${
                          statusFilter === 'declined' 
                            ? 'bg-red-600 text-white' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        Declined ({getRequestsByStatus('declined').length})
                      </button>
                    </div>
                  </div>
                )}
                
                {(() => {
                  let displayRequests = effectiveUser?.role === 'journalist'
                    ? getRequestsByJournalist(effectiveUser?.name || '')
                    : interviewRequests
                  
                  // Apply filters - only for PR team
                  if (effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') {
                    // Apply credentials filter
                    displayRequests = filterByCredentials(displayRequests, requestCredentialsFilter);
                    
                    // Apply priority filter
                    displayRequests = filterByPriority(displayRequests, priorityFilter);
                    
                    // Apply status filter
                    displayRequests = filterByStatus(displayRequests, statusFilter);
                    
                    // Apply search filter
                    displayRequests = filterBySearch(displayRequests, searchTerm);
                  }
                  
                  // Apply sorting
                  displayRequests = sortRequests(displayRequests, sortField, sortDirection);
                  
                  if (displayRequests.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <Users size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500 mb-4">
                          {effectiveUser?.role === 'journalist' 
                            ? 'You haven\'t made any interview requests yet.' 
                            : 'No interview requests found.'}
                        </p>
                        <p className="text-sm text-gray-400">
                          {effectiveUser?.role === 'journalist'
                            ? 'Go to the Guests tab and click "Request Interview" on any guest you\'d like to interview.'
                            : 'Interview requests from journalists will appear here.'}
                        </p>
                      </div>
                    );
                  }

                  // Show "By Guest" view for PR team when selected
                  if ((effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && requestsViewMode === 'by-guest') {
                    const requestsByGuest = getRequestsByGuest(displayRequests);
                    const guestNames = sortGuests(Object.keys(requestsByGuest), sortField === 'guest' ? sortDirection : 'asc');
                    
                    if (guestNames.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Users size={48} className="mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-500">No guest requests found.</p>
                        </div>
                      );
                    }
                    
                    return (
                      <div>
                        {/* Mobile Card View - By Guest */}
                        <div className="lg:hidden space-y-4">
                          {guestNames.map(guestName => {
                            const guestRequests = requestsByGuest[guestName];
                            const celebrity = celebrities.find(c => c.name === guestName);
                            
                            return (
                              <div key={guestName} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <h3 className="font-medium text-gray-900">{guestName}</h3>
                                      {isPRTeam && celebrity?.priority && celebrity.priority !== 'Unassigned' && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                          celebrity.priority === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                          celebrity.priority === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                          'bg-gray-100 border border-gray-300 text-gray-600'
                                        }`}>
                                          {celebrity.priority}
                                        </span>
                                      )}
                                    </div>
                                    {celebrity && (
                                      <p className="text-sm text-gray-600">{celebrity.description}</p>
                                    )}
                                  </div>
                                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                    {guestRequests.length} request{guestRequests.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                
                                <div className="space-y-3">
                                  {guestRequests.map(request => (
                                    <div key={request.id} className="bg-gray-50 rounded p-3 border">
                                      <div className="flex items-center justify-between mb-2">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900">{request.journalistName}</span>
                                            {isPRTeam && (() => {
                                              const j = allJournalists.find(j => j.name === request.journalistName);
                                              const tier = j?.pressTier || 'C';
                                              return (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                                  tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                                  tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                                  'bg-gray-100 border border-gray-300 text-gray-600'
                                                }`}>
                                                  {tier}
                                                </span>
                                              );
                                            })()}
                                            {isPRTeam && request.journalistPriority && (
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                request.journalistPriority === 'High' ? 'bg-red-100 text-red-700' :
                                                request.journalistPriority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-green-100 text-green-700'
                                              }`}>
                                                {request.journalistPriority}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-sm text-gray-600">
                                            {(() => {
                                              // First try to get outlet from request data
                                              if (request.outlet && request.outlet !== 'Unknown Outlet') {
                                                return request.outlet;
                                              }
                                              // Fallback: look up journalist in allJournalists by name
                                              const journalist = allJournalists.find(j => j.name === request.journalistName);
                                              return journalist?.outlet || 'Unknown Outlet';
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                      <div className={`grid ${isPRTeam ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                                        {/* Priority - Only visible to PR team */}
                                        {isPRTeam && (
                                          <div>
                                            <label className="text-xs text-gray-600 block mb-1">Priority</label>
                                            <select
                                              value={request.priority}
                                              onChange={(e) => updateRequestPriority(request.id, guestName, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                              className={`w-full text-xs p-2 border rounded ${
                                                request.priority === 'A' ? 'bg-red-100 border-red-300 text-red-800' :
                                                request.priority === 'B' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.priority === 'C' ? 'bg-green-100 border-green-300 text-green-800' :
                                                'bg-gray-100 border-gray-300 text-gray-600'
                                              }`}
                                            >
                                              <option value="Unassigned">-</option>
                                              <option value="A">A</option>
                                              <option value="B">B</option>
                                              <option value="C">C</option>
                                            </select>
                                          </div>
                                        )}
                                        <div>
                                          <label className="text-xs text-gray-600 block mb-1">Status</label>
                                          {isPRTeam ? (
                                            <select
                                              value={request.status}
                                              onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                              className={`w-full text-xs p-2 border rounded ${
                                                request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                                request.status === 'pending' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                                request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-800' :
                                                request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                                'bg-red-100 border-red-300 text-red-800'
                                              }`}
                                            >
                                              <option value="new">New</option>
                                              <option value="pending">Pending</option>
                                              <option value="approved">Approved</option>
                                              <option value="scheduled">Scheduled</option>
                                              <option value="complete">Complete</option>
                                              <option value="declined">Declined</option>
                                            </select>
                                          ) : (
                                            <div className={`w-full text-xs p-2 border rounded ${
                                              request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                              request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                              request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                              request.status === 'declined' ? 'bg-red-100 border-red-300 text-red-800' :
                                              'bg-gray-100 border-gray-300 text-gray-800'
                                            }`}>
                                              {request.status === 'new' ? 'New' :
                                               request.status === 'complete' ? 'Complete' :
                                               request.status === 'declined' ? 'Declined' :
                                               request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Desktop Table View - By Guest */}
                        <div className="hidden lg:block overflow-x-auto">
                        <div className="min-w-full bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                          {/* By Guest Grid Header */}
                          <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10">
                            <div className="flex px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              <div className="relative flex items-center" style={{ width: columnWidths.guest }}>
                                <button 
                                  onClick={() => handleSort('guest')}
                                  className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                                >
                                  Guest Name
                                  {sortField === 'guest' && (
                                    <span className="ml-1 text-indigo-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </button>
                                <div 
                                  className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                  onMouseDown={(e) => handleMouseDown(e, 'guest')}
                                />
                              </div>
                              <div className="relative flex items-center flex-1 min-w-0">
                                <div>Journalists</div>
                              </div>
                              <div className="relative flex items-center" style={{ width: columnWidths.priority }}>
                                <div>Priority</div>
                                <div 
                                  className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                  onMouseDown={(e) => handleMouseDown(e, 'priority')}
                                />
                              </div>
                              <div className="relative flex items-center" style={{ width: columnWidths.status }}>
                                <div>Status</div>
                                <div 
                                  className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                  onMouseDown={(e) => handleMouseDown(e, 'status')}
                                />
                              </div>
                            </div>
                          </div>

                          {/* By Guest Grid Body */}
                          <div className="divide-y divide-gray-200">
                            {guestNames.map(guestName => {
                              const guestRequests = requestsByGuest[guestName];
                              const celebrity = celebrities.find(c => c.name === guestName);
                              
                              return (
                                <div key={guestName} className="hover:bg-slate-50 transition-colors duration-150">
                                  {/* Guest Name Row */}
                                  <div className="flex px-6 py-3 items-center bg-gradient-to-r from-slate-50 to-blue-50 border-l-4 border-indigo-500 rounded-sm">
                                    <div style={{ width: columnWidths.guest }}>
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          onClick={() => openCelebrityModal(celebrity)}
                                          className="font-semibold text-indigo-600 hover:text-indigo-800 text-left text-sm transition-colors"
                                        >
                                          {guestName}
                                        </button>
                                        {isPRTeam && celebrity?.priority && celebrity.priority !== 'Unassigned' && (
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                            celebrity.priority === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                            celebrity.priority === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                            'bg-gray-100 border border-gray-300 text-gray-600'
                                          }`}>
                                            {celebrity.priority}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex-1 min-w-0 text-xs text-gray-500">
                                      {guestRequests.length} request{guestRequests.length !== 1 ? 's' : ''}
                                    </div>
                                    <div style={{ width: columnWidths.priority }}></div>
                                    <div style={{ width: columnWidths.status }}></div>
                                  </div>
                                  
                                  {/* Journalist Requests - Compact Inline */}
                                  {sortRequestsByJournalist(guestRequests).map((request, index) => (
                                    <div key={request.id} className="flex px-6 py-2 items-center border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors duration-150">
                                      <div style={{ width: columnWidths.guest }}></div>
                                      
                                      <div className="flex-1 min-w-0">
                                        <div className="ml-4 flex items-center justify-between">
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                onClick={() => openJournalistModal(request.journalistName)}
                                                className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                              >
                                                {request.journalistName}
                                              </button>
                                              {(() => {
                                                const j = allJournalists.find(j => j.name === request.journalistName);
                                                const tier = j?.pressTier || 'C';
                                                return (
                                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                                    tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                                    tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                                    'bg-gray-100 border border-gray-300 text-gray-600'
                                                  }`}>
                                                    {tier}
                                                  </span>
                                                );
                                              })()}
                                              {request.journalistPriority && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                  request.journalistPriority === 'High' ? 'bg-red-100 text-red-700' :
                                                  request.journalistPriority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                  'bg-green-100 text-green-700'
                                                }`}>
                                                  {request.journalistPriority}
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {(() => {
                                                // First try to get outlet from request data
                                                if (request.outlet && request.outlet !== 'Unknown Outlet') {
                                                  return request.outlet;
                                                }
                                                // Fallback: look up journalist in allJournalists by name
                                                const journalist = allJournalists.find(j => j.name === request.journalistName);
                                                return journalist?.outlet || 'Unknown Outlet';
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div style={{ width: columnWidths.priority }}>
                                        <select
                                          value={request.priority}
                                          onChange={(e) => updateRequestPriority(request.id, guestName, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                          className={`w-full text-xs px-1.5 py-0.5 border rounded font-medium ${
                                            request.priority === 'A' ? 'bg-red-100 border-red-300 text-red-800' :
                                            request.priority === 'B' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                            request.priority === 'C' ? 'bg-green-100 border-green-300 text-green-800' :
                                            'bg-gray-100 border-gray-300 text-gray-600'
                                          }`}
                                        >
                                          <option value="Unassigned">-</option>
                                          <option value="A">A</option>
                                          <option value="B">B</option>
                                          <option value="C">C</option>
                                        </select>
                                      </div>
                                      
                                      <div style={{ width: columnWidths.status }}>
                                        <select
                                          value={request.status}
                                          onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                          className={`w-full text-xs px-1.5 py-0.5 border rounded font-medium ${
                                            request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                            request.status === 'pending' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                            request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                            request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-800' :
                                            request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                            'bg-red-100 border-red-300 text-red-800'
                                          }`}
                                        >
                                          <option value="new">New</option>
                                          <option value="pending">Pending</option>
                                          <option value="approved">Approved</option>
                                          <option value="scheduled">Scheduled</option>
                                          <option value="complete">Complete</option>
                                          <option value="declined">Declined</option>
                                        </select>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        </div>
                      </div>
                    );
                  }

                  // Group requests by journalist for better organization
                  const requestsByJournalist = displayRequests.reduce((acc, request) => {
                    const key = `${request.journalistName}-${request.outlet}`;
                    if (!acc[key]) {
                      acc[key] = {
                        journalistName: request.journalistName,
                        outlet: request.outlet,
                        requests: []
                      };
                    }
                    acc[key].requests.push(request);
                    return acc;
                  }, {});

                  const journalistGroups = Object.values(requestsByJournalist);

                  // Default "By Journalist" view - High Density
                  return (
                    <div>
                      {/* Mobile Card View */}
                      <div className="lg:hidden space-y-4">
                        {journalistGroups.map((group, index) => (
                          <div key={index} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-medium text-gray-900">{group.journalistName}</h3>
                                  {isPRTeam && (() => {
                                    const j = allJournalists.find(j => j.name === group.journalistName);
                                    const tier = j?.pressTier || 'C';
                                    return (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                        tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                        tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                        'bg-gray-100 border border-gray-300 text-gray-600'
                                      }`}>
                                        {tier}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <p className="text-sm text-gray-600">{group.outlet}</p>
                              </div>
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                {group.requests.length} request{group.requests.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="space-y-3">
                              {group.requests.map(request => (
                                request.celebrities?.map(celebrityName => {
                                  const priority = (request.priority && request.priority[celebrityName]) || 'Unassigned';
                                  return (
                                    <div key={`${request.id}-${celebrityName}`} className="bg-gray-50 rounded p-3 border">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-900">{celebrityName}</span>
                                        {isPRTeam && (() => {
                                          const celeb = celebrities.find(c => c.name === celebrityName);
                                          const tier = celeb?.priority;
                                          if (!tier || tier === 'Unassigned') return null;
                                          return (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                              tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                              tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                              'bg-gray-100 border border-gray-300 text-gray-600'
                                            }`}>
                                              {tier}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                      {isPRTeam ? (
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="text-xs text-gray-600 block mb-1">Priority</label>
                                            <select
                                              value={priority}
                                              onChange={(e) => updateRequestPriority(request.id, celebrityName, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                              className={`w-full text-xs p-2 border rounded ${
                                                priority === 'A' ? 'bg-red-100 border-red-300 text-red-800' :
                                                priority === 'B' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                priority === 'C' ? 'bg-green-100 border-green-300 text-green-800' :
                                                'bg-gray-100 border-gray-300 text-gray-600'
                                              }`}
                                            >
                                              <option value="Unassigned">-</option>
                                              <option value="A">A</option>
                                              <option value="B">B</option>
                                              <option value="C">C</option>
                                            </select>
                                          </div>
                                          <div>
                                            <label className="text-xs text-gray-600 block mb-1">Status</label>
                                            <select
                                              value={request.status}
                                              onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                              className={`w-full text-xs p-2 border rounded ${
                                                request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                                request.status === 'pending' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                                request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-800' :
                                                request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                                'bg-red-100 border-red-300 text-red-800'
                                              }`}
                                            >
                                              <option value="new">New</option>
                                              <option value="pending">Pending</option>
                                              <option value="approved">Approved</option>
                                              <option value="scheduled">Scheduled</option>
                                              <option value="complete">Complete</option>
                                              <option value="declined">Declined</option>
                                            </select>
                                          </div>
                                          {request.journalistPriority && (
                                            <div className="col-span-2">
                                              <span className="text-xs text-gray-500">Journalist rates: </span>
                                              <span className={`text-xs font-medium ${
                                                request.journalistPriority === 'High' ? 'text-red-700' :
                                                request.journalistPriority === 'Medium' ? 'text-yellow-700' :
                                                'text-green-700'
                                              }`}>
                                                {request.journalistPriority}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="text-xs text-gray-600 block mb-1">My Priority</label>
                                            <select
                                              value={request.journalistPriority || ''}
                                              onChange={(e) => updateJournalistPriority(request.id, (e.target.value || null) as 'High' | 'Medium' | 'Low' | null)}
                                              className={`w-full text-xs p-2 border rounded ${
                                                request.journalistPriority === 'High' ? 'bg-red-100 border-red-300 text-red-800' :
                                                request.journalistPriority === 'Medium' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.journalistPriority === 'Low' ? 'bg-green-100 border-green-300 text-green-800' :
                                                'bg-gray-100 border-gray-300 text-gray-600'
                                              }`}
                                            >
                                              <option value="">-</option>
                                              <option value="High">High</option>
                                              <option value="Medium">Medium</option>
                                              <option value="Low">Low</option>
                                            </select>
                                          </div>
                                          <div>
                                            <label className="text-xs text-gray-600 block mb-1">Status</label>
                                            <div className={`w-full text-xs p-2 border rounded ${
                                              request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                              request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                              request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                              request.status === 'declined' ? 'bg-red-100 border-red-300 text-red-800' :
                                              'bg-gray-100 border-gray-300 text-gray-800'
                                            }`}>
                                              {request.status === 'new' ? 'New' :
                                               request.status === 'complete' ? 'Complete' :
                                               request.status === 'declined' ? 'Declined' :
                                               request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) || []
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden lg:block overflow-x-auto">
                      <div className="min-w-full bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
                        {/* Grid Header */}
                        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10">
                          <div className="flex px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            <div className="relative flex items-center" style={{ width: columnWidths.journalist }}>
                              <button 
                                onClick={() => handleSort('journalist')}
                                className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                              >
                                Journalist
                                {sortField === 'journalist' && (
                                  <span className="ml-1 text-indigo-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </button>
                              <div 
                                className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                onMouseDown={(e) => handleMouseDown(e, 'journalist')}
                              />
                            </div>
                            <div className="relative flex items-center" style={{ width: columnWidths.outlet }}>
                              <button 
                                onClick={() => handleSort('outlet')}
                                className="text-left hover:text-gray-900 transition-colors flex items-center font-medium"
                              >
                                Outlet
                                {sortField === 'outlet' && (
                                  <span className="ml-1 text-indigo-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                              </button>
                              <div 
                                className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                onMouseDown={(e) => handleMouseDown(e, 'outlet')}
                              />
                            </div>
                            <div className="relative flex items-center flex-1 min-w-0">
                              <div>Guests Requested</div>
                            </div>
                            <div className="relative flex items-center" style={{ width: columnWidths.priority }}>
                              <div>Priority</div>
                              <div 
                                className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                onMouseDown={(e) => handleMouseDown(e, 'priority')}
                              />
                            </div>
                            <div className="relative flex items-center" style={{ width: columnWidths.status }}>
                              <div>Status</div>
                              <div 
                                className="absolute right-0 top-0 w-1 h-full bg-indigo-200 cursor-col-resize opacity-0 hover:opacity-100 transition-opacity rounded-full hidden md:block"
                                onMouseDown={(e) => handleMouseDown(e, 'status')}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Grid Body */}
                        <div className="divide-y divide-gray-200">
                          {journalistGroups.map((group, groupIndex) => (
                            <div key={`${group.journalistName}-${group.outlet}`} className="hover:bg-slate-50 transition-colors duration-150">
                              {/* Journalist Header Row */}
                              <div className="flex px-6 py-3 items-center bg-gradient-to-r from-slate-50 to-blue-50 border-l-4 border-indigo-500 rounded-sm">
                                <div style={{ width: columnWidths.journalist }}>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => openJournalistModal(group.journalistName)}
                                      className="font-semibold text-indigo-600 hover:text-indigo-800 text-left text-sm transition-colors"
                                    >
                                      {group.journalistName || 'Unknown Journalist'}
                                    </button>
                                    {isPRTeam && (() => {
                                      const j = allJournalists.find(j => j.name === group.journalistName);
                                      const tier = j?.pressTier || 'C';
                                      return (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                          tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                          tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                          'bg-gray-100 border border-gray-300 text-gray-600'
                                        }`}>
                                          {tier}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                </div>
                                <div style={{ width: columnWidths.outlet }}>
                                  <div className="text-sm text-gray-600">
                                    {(() => {
                                      // First try to get outlet from the group data
                                      if (group.outlet && group.outlet !== 'Unknown Outlet') {
                                        return group.outlet;
                                      }
                                      // Fallback: look up journalist in allJournalists by name
                                      const journalist = allJournalists.find(j => j.name === group.journalistName);
                                      return journalist?.outlet || 'Unknown Outlet';
                                    })()}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0 flex items-center justify-end pr-4">
                                </div>
                                <div style={{ width: columnWidths.priority }}></div>
                                <div style={{ width: columnWidths.status }}></div>
                              </div>
                              
                              {/* Guest Requests - Compact Inline */}
                              {(() => {
                                // Collect all celebrity-request pairs for this journalist
                                const celebrityRequestPairs: Array<{celebrityName: string, request: any}> = [];
                                group.requests.forEach(request => {
                                  (request.celebrities || []).forEach(celebrityName => {
                                    celebrityRequestPairs.push({ celebrityName, request });
                                  });
                                });
                                
                                // Sort all celebrities by last name
                                const sortedPairs = celebrityRequestPairs.sort((a, b) => {
                                  const aName = (a.celebrityName || '').split(' ');
                                  const bName = (b.celebrityName || '').split(' ');
                                  const aLastName = aName.length > 1 ? aName[aName.length - 1].toLowerCase() : aName[0]?.toLowerCase() || '';
                                  const bLastName = bName.length > 1 ? bName[bName.length - 1].toLowerCase() : bName[0]?.toLowerCase() || '';
                                  return aLastName.localeCompare(bLastName);
                                });
                                
                                return sortedPairs.map(({celebrityName, request}, index) => {
                                  const celebrity = celebrities.find(c => c.name === celebrityName);
                                  const isCompleted = (request.completed && request.completed[celebrityName]) || false;
                                  const priority = (request.priority && request.priority[celebrityName]) || 'Unassigned';
                                  
                                  return (
                                    <div key={`${request.id}-${celebrityName}`} className="flex px-6 py-2 items-center border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors duration-150">
                                      <div style={{ width: columnWidths.journalist }}></div>
                                      <div style={{ width: columnWidths.outlet }}></div>
                                      
                                      <div className="flex-1 min-w-0">
                                        <div className="ml-4">
                                          {effectiveUser?.role === 'journalist' ? (
                                            <span className="text-sm font-medium text-gray-900">
                                              {celebrityName}
                                            </span>
                                          ) : (
                                            <div className="flex items-center gap-2">
                                              <button
                                                onClick={() => {
                                                  const celeb = celebrities.find(c => c.name === celebrityName);
                                                  if (celeb) openCelebrityModal(celeb);
                                                }}
                                                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors duration-150"
                                              >
                                                {celebrityName}
                                              </button>
                                              {(() => {
                                                const celeb = celebrities.find(c => c.name === celebrityName);
                                                const tier = celeb?.priority;
                                                if (!tier || tier === 'Unassigned') return null;
                                                return (
                                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                                    tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                                    tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                                    'bg-gray-100 border border-gray-300 text-gray-600'
                                                  }`}>
                                                    {tier}
                                                  </span>
                                                );
                                              })()}
                                              {request.journalistPriority && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                  request.journalistPriority === 'High' ? 'bg-red-100 text-red-700' :
                                                  request.journalistPriority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                  'bg-green-100 text-green-700'
                                                }`}>
                                                  {request.journalistPriority}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') ? (
                                        <>
                                          <div style={{ width: columnWidths.priority }}>
                                            <select
                                              value={priority}
                                              onChange={(e) => updateRequestPriority(request.id, celebrityName, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                              className={`w-full text-xs px-1.5 py-0.5 border rounded font-medium ${
                                                priority === 'A' ? 'bg-red-100 border-red-300 text-red-800' :
                                                priority === 'B' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                priority === 'C' ? 'bg-green-100 border-green-300 text-green-800' :
                                                'bg-gray-100 border-gray-300 text-gray-600'
                                              }`}
                                            >
                                              <option value="Unassigned">-</option>
                                              <option value="A">A</option>
                                              <option value="B">B</option>
                                              <option value="C">C</option>
                                            </select>
                                          </div>
                                          
                                          <div style={{ width: columnWidths.status }}>
                                            <select
                                              value={request.status}
                                              onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                              className={`w-full text-xs px-1.5 py-0.5 border rounded font-medium ${
                                                request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                                request.status === 'pending' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                                request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-800' :
                                                request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                                'bg-red-100 border-red-300 text-red-800'
                                              }`}
                                            >
                                              <option value="new">New</option>
                                              <option value="pending">Pending</option>
                                              <option value="approved">Approved</option>
                                              <option value="scheduled">Scheduled</option>
                                              <option value="complete">Complete</option>
                                              <option value="declined">Declined</option>
                                            </select>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div style={{ width: columnWidths.priority }}>
                                            <select
                                              value={request.journalistPriority || ''}
                                              onChange={(e) => updateJournalistPriority(request.id, (e.target.value || null) as 'High' | 'Medium' | 'Low' | null)}
                                              className={`w-full text-xs px-1.5 py-0.5 border rounded font-medium ${
                                                request.journalistPriority === 'High' ? 'bg-red-100 border-red-300 text-red-800' :
                                                request.journalistPriority === 'Medium' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                                request.journalistPriority === 'Low' ? 'bg-green-100 border-green-300 text-green-800' :
                                                'bg-gray-100 border-gray-300 text-gray-600'
                                              }`}
                                            >
                                              <option value="">-</option>
                                              <option value="High">High</option>
                                              <option value="Medium">Medium</option>
                                              <option value="Low">Low</option>
                                            </select>
                                          </div>
                                          <div style={{ width: columnWidths.status }}>
                                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                              request.status === 'complete' ? 'bg-emerald-700 text-white border-emerald-800' :
                                              request.status === 'declined' ? 'bg-red-100 text-red-800 border-red-200' :
                                              request.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                                              request.status === 'scheduled' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                              request.status === 'new' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                              'bg-gray-100 text-gray-800 border-gray-200'
                                            }`}>
                                              <Clock size={12} className="mr-1" />
                                              {request.status === 'complete' ? 'Complete' :
                                               request.status === 'declined' ? 'Declined' :
                                               request.status === 'approved' ? 'Approved' :
                                               request.status === 'scheduled' ? 'Scheduled' :
                                               request.status === 'pending' ? 'Pending' :
                                               request.status === 'new' ? 'New' :
                                               request.status}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          ))}
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'press-materials' && (
              <PressMaterialsBrowser userRole={effectiveUser?.role || 'journalist'} />
            )}

            {activeTab === 'messages' && currentUser && (
              <div className="h-full">
                <MessagingDashboard 
                  currentUser={currentUser} 
                  onUnreadChange={setHasUnreadMessages}
                />
              </div>
            )}

            {activeTab === 'reports' && effectiveUser?.role !== 'journalist' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => exportGuestData()}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Guest Data
                    </button>
                    <button
                      onClick={() => exportPressData()}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Press Data
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Guest Analytics */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">Guest Analytics</h3>
                    
                    {/* Guest Popularity */}
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">🏆 Most Popular Guests</h4>
                      </div>
                      <div className="space-y-4">
                        {(() => {
                          const guestPopularity = celebrities.map(guest => {
                            const requests = getRequestsByCelebrity(guest.name);
                            const completed = requests.filter(req => req.completed[guest.name]).length;
                            return {
                              name: guest.name,
                              category: guest.category,
                              requests: requests.length,
                              completed,
                              completionRate: requests.length > 0 ? Math.round((completed / requests.length) * 100) : 0
                            };
                          }).sort((a, b) => b.requests - a.requests).slice(0, 10);

                          const maxRequests = Math.max(...guestPopularity.map(g => g.requests), 1);

                          return guestPopularity.map((guest, index) => {
                            const barWidth = (guest.requests / maxRequests) * 100;
                            const trophyColor = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🎖️';
                            
                            return (
                              <div key={guest.name} className="relative">
                                <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-purple-100">
                                  <div className="flex items-center gap-4 flex-1">
                                    <span className="text-lg">{trophyColor}</span>
                                    <span className="text-sm font-bold text-purple-600 w-6">#{index + 1}</span>
                                    <div className="flex-1">
                                      <div className="font-bold text-gray-900">{guest.name}</div>
                                      <div className="text-sm text-purple-600 font-medium">{(guest.category || []).join(', ')}</div>
                                      {/* Visual bar */}
                                      <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500"
                                          style={{ width: `${barWidth}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right ml-4">
                                    <div className="text-lg font-bold text-purple-600">{guest.requests}</div>
                                    <div className="text-xs text-gray-500">requests</div>
                                    <div className="text-sm font-medium text-green-600">{guest.completed} ✓ ({guest.completionRate}%)</div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Category Popularity */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h4a1 1 0 011 1v2M7 4h6M7 4v16a2 2 0 002 2h2a2 2 0 002-2V4m-6 4h4m-4 4h4m-4 4h4" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">📊 Category Interest</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        {(() => {
                          const categoryStats = celebrities.reduce((acc, guest) => {
                            const requests = getRequestsByCelebrity(guest.name);
                            (guest.category || []).forEach(cat => {
                              if (!acc[cat]) {
                                acc[cat] = { requests: 0, guests: 0, completed: 0 };
                              }
                              acc[cat].requests += requests.length;
                              acc[cat].guests += 1;
                              acc[cat].completed += requests.filter(req => req.completed[guest.name]).length;
                            });
                            return acc;
                          }, {});

                          const sortedCategories = Object.entries(categoryStats)
                            .sort(([,a], [,b]) => b.requests - a.requests);
                          
                          const maxRequests = Math.max(...sortedCategories.map(([,stats]) => stats.requests), 1);
                          
                          const categoryEmojis = {
                            'TV': '📺', 'Film': '🎬', 'Music': '🎵', 'Sports': '⚽', 
                            'Comedy': '😄', 'Author': '📚', 'Gaming': '🎮', 'Other': '⭐'
                          };

                          return sortedCategories.map(([category, stats], index) => {
                            const percentage = (stats.requests / maxRequests) * 100;
                            const completionRate = stats.requests > 0 ? Math.round((stats.completed / stats.requests) * 100) : 0;
                            
                            return (
                              <div key={category} className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{categoryEmojis[category] || '⭐'}</span>
                                    <span className="font-bold text-gray-900">{category}</span>
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                      {stats.guests} guests
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-lg font-bold text-blue-600">{stats.requests}</div>
                                    <div className="text-xs text-gray-500">requests</div>
                                  </div>
                                </div>
                                {/* Visual percentage bar */}
                                <div className="mb-2">
                                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span>Interest Level</span>
                                    <span>{Math.round(percentage)}%</span>
                                  </div>
                                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full transition-all duration-700"
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="text-sm text-green-600 font-medium">
                                  ✓ {stats.completed} completed ({completionRate}%)
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Press Analytics */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">Press Analytics</h3>
                    
                    {/* Credential Pickup by Day */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">🎫 Credential Pickup by Day</h4>
                      </div>
                      <div className="grid grid-cols-3 gap-6">
                        {(() => {
                          const days = [
                            { name: 'Friday', emoji: '🗓️', color: 'from-red-400 to-pink-500' },
                            { name: 'Saturday', emoji: '📅', color: 'from-blue-400 to-indigo-500' },
                            { name: 'Sunday', emoji: '🗒️', color: 'from-purple-400 to-violet-500' }
                          ];
                          const totalPickup = allJournalists.filter(j => j.credentialsPickedUp).length;
                          const totalJournalists = allJournalists.length;
                          
                          return days.map((day, index) => {
                            // For demo purposes, distribute randomly. In real implementation, you'd track actual pickup dates
                            const dayPickup = Math.floor(totalPickup / 3) + (day.name === 'Saturday' ? totalPickup % 3 : 0);
                            const percentage = totalJournalists > 0 ? Math.round((dayPickup / totalJournalists) * 100) : 0;
                            
                            return (
                              <div key={day.name} className="relative">
                                <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-green-100 hover:shadow-md transition-shadow">
                                  <div className="text-2xl mb-2">{day.emoji}</div>
                                  <div className="text-3xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${day.color} mb-1">
                                    {dayPickup}
                                  </div>
                                  <div className="text-sm font-medium text-gray-700 mb-2">{day.name}</div>
                                  <div className="text-xs text-green-600 font-bold">{percentage}%</div>
                                  {/* Mini chart */}
                                  <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full bg-gradient-to-r ${day.color} rounded-full transition-all duration-1000`}
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      <div className="mt-6 p-4 bg-white rounded-lg border border-green-100">
                        <div className="flex items-center justify-center gap-2 text-center">
                          <span className="text-2xl">📊</span>
                          <div>
                            <div className="text-lg font-bold text-green-600">
                              {allJournalists.filter(j => j.credentialsPickedUp).length} / {allJournalists.length}
                            </div>
                            <div className="text-sm text-gray-600">Total Credentials Picked Up</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Journalist Activity */}
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">🚀 Most Active Journalists</h4>
                      </div>
                      <div className="space-y-4">
                        {(() => {
                          const journalistStats = allJournalists.map(journalist => {
                            const requests = getRequestsByJournalist(journalist.name);
                            const completed = requests.reduce((acc, req) => {
                              return acc + Object.values(req.completed).filter(Boolean).length;
                            }, 0);
                            return {
                              name: journalist.name,
                              outlet: journalist.outlet,
                              requests: requests.length,
                              completed,
                              completionRate: requests.length > 0 ? Math.round((completed / requests.length) * 100) : 0
                            };
                          }).sort((a, b) => b.requests - a.requests).slice(0, 10);

                          const maxRequests = Math.max(...journalistStats.map(j => j.requests), 1);

                          return journalistStats.map((journalist, index) => {
                            const activityWidth = (journalist.requests / maxRequests) * 100;
                            const medalEmoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅';
                            
                            return (
                              <div key={journalist.name} className="bg-white rounded-lg p-4 shadow-sm border border-orange-100">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg">{medalEmoji}</span>
                                    <span className="text-sm font-bold text-orange-600 w-6">#{index + 1}</span>
                                    <div>
                                      <div className="font-bold text-gray-900">{journalist.name}</div>
                                      <div className="text-sm text-orange-600 font-medium">{journalist.outlet}</div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-lg font-bold text-orange-600">{journalist.requests}</div>
                                    <div className="text-xs text-gray-500">requests</div>
                                  </div>
                                </div>
                                {/* Activity bar */}
                                <div className="mb-2">
                                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span>Activity Level</span>
                                    <span>{Math.round(activityWidth)}%</span>
                                  </div>
                                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full transition-all duration-600"
                                      style={{ width: `${activityWidth}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="text-sm text-green-600 font-medium">
                                  ✅ {journalist.completed} completed ({journalist.completionRate}% success rate)
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Request Status Overview */}
                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-rose-100 rounded-lg">
                          <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">📈 Request Status Overview</h4>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                        {(() => {
                          const statusCounts = {
                            new: getRequestsByStatus('new').length,
                            pending: getRequestsByStatus('pending').length,
                            approved: getRequestsByStatus('approved').length,
                            scheduled: getRequestsByStatus('scheduled').length,
                            complete: getRequestsByStatus('complete').length,
                            declined: getRequestsByStatus('declined').length
                          };
                          const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);

                          const statusConfig = {
                            new: {
                              emoji: '🆕',
                              color: 'from-gray-400 to-gray-500',
                              bgColor: 'bg-gray-50',
                              borderColor: 'border-gray-200',
                              textColor: 'text-gray-700'
                            },
                            pending: {
                              emoji: '⏳',
                              color: 'from-yellow-400 to-orange-500',
                              bgColor: 'bg-yellow-50',
                              borderColor: 'border-yellow-200',
                              textColor: 'text-yellow-700'
                            },
                            approved: {
                              emoji: '👍',
                              color: 'from-green-300 to-green-500',
                              bgColor: 'bg-green-50',
                              borderColor: 'border-green-200',
                              textColor: 'text-green-700'
                            },
                            scheduled: {
                              emoji: '📅',
                              color: 'from-blue-400 to-indigo-500',
                              bgColor: 'bg-blue-50',
                              borderColor: 'border-blue-200',
                              textColor: 'text-blue-700'
                            },
                            complete: {
                              emoji: '✅',
                              color: 'from-emerald-600 to-emerald-800',
                              bgColor: 'bg-emerald-50',
                              borderColor: 'border-emerald-300',
                              textColor: 'text-emerald-800'
                            },
                            declined: {
                              emoji: '❌',
                              color: 'from-red-400 to-rose-500',
                              bgColor: 'bg-red-50',
                              borderColor: 'border-red-200',
                              textColor: 'text-red-700'
                            }
                          };

                          return Object.entries(statusCounts).map(([status, count]) => {
                            const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                            const config = statusConfig[status];

                            return (
                              <div key={status} className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4 text-center hover:shadow-md transition-shadow`}>
                                <div className="text-2xl mb-2">{config.emoji}</div>
                                <div className="text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${config.color} mb-1">
                                  {count}
                                </div>
                                <div className={`text-sm font-medium capitalize ${config.textColor} mb-2`}>{status}</div>
                                <div className={`text-xs ${config.textColor} font-bold`}>{percentage}%</div>
                                {/* Mini progress ring effect */}
                                <div className="mt-3 flex justify-center">
                                  <div className="relative w-8 h-8">
                                    <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 32 32">
                                      <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2" />
                                      <circle 
                                        cx="16" cy="16" r="12" fill="none" 
                                        stroke="currentColor" strokeWidth="2" 
                                        strokeLinecap="round"
                                        strokeDasharray={`${percentage * 0.75} 75`}
                                        className={config.textColor}
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      <div className="mt-6 p-4 bg-white rounded-lg border border-rose-100">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-rose-600 mb-1">
                            {Object.values({
                              new: getRequestsByStatus('new').length,
                              pending: getRequestsByStatus('pending').length,
                              approved: getRequestsByStatus('approved').length,
                              scheduled: getRequestsByStatus('scheduled').length,
                              complete: getRequestsByStatus('complete').length,
                              declined: getRequestsByStatus('declined').length
                            }).reduce((a, b) => a + b, 0)}
                          </div>
                          <div className="text-sm text-gray-600">Total Interview Requests</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'admin' && effectiveUser?.role !== 'journalist' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <h2 className="text-lg font-medium text-gray-900">Admin Panel</h2>
                  {effectiveUser?.role === 'admin' && (
                    <button
                      onClick={async () => {
                        if (confirm('Are you sure you want to clear ALL Press Gaggle messages? This action cannot be undone.')) {
                          try {
                            console.log('Admin clearing press gaggle...')
                            await messagingService.clearPressGaggle()
                            alert('Press Gaggle cleared successfully!')
                          } catch (error) {
                            console.error('Failed to clear press gaggle:', error)
                            alert('Failed to clear Press Gaggle: ' + error.message)
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 text-sm font-medium"
                    >
                      Clear Press Gaggle
                    </button>
                  )}
                </div>
                {/* PR Team Management Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <UserPlus size={20} className="text-blue-600 mr-2" />
                      <h3 className="text-lg font-medium text-blue-900">PR Team Management</h3>
                    </div>
                    {effectiveUser?.role === 'admin' && (
                      <button
                        onClick={openAddPrMemberModal}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        <UserPlus size={16} className="mr-2" />
                        Add Member
                      </button>
                    )}
                  </div>

                  {effectiveUser?.role === 'admin' ? (
                    <div>
                      <p className="text-sm text-blue-800 mb-4">
                        Manage PR team members and their permissions. Only admins can add, edit, or remove team members.
                      </p>

                      {prTeamMembers.length === 0 ? (
                        <div className="text-center py-8">
                          <Users size={48} className="mx-auto text-blue-400 mb-4" />
                          <p className="text-blue-700 font-medium mb-2">No PR team members found</p>
                          <p className="text-blue-600 text-sm">Click "Add Member" to start building your team.</p>
                        </div>
                      ) : (
                        <div className="bg-white rounded-lg border border-blue-200">
                          <div className="px-6 py-3 border-b border-blue-200 bg-blue-100">
                            <div className="flex items-center justify-between text-sm font-medium text-blue-900">
                              <div className="flex-1">Name</div>
                              <div className="flex-1">Email</div>
                              <div className="w-24">Role</div>
                              <div className="w-20">Actions</div>
                            </div>
                          </div>
                          <div className="divide-y divide-blue-100">
                            {prTeamMembers.map((member) => (
                              <div key={member.id} className="px-6 py-4 hover:bg-blue-50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{member.name}</div>
                                    <div className="text-sm text-gray-500">{member.department || 'PR Team'}</div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm text-gray-900">{member.email}</div>
                                  </div>
                                  <div className="w-24">
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      member.role === 'admin' 
                                        ? 'bg-red-100 text-red-800' 
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {member.role === 'admin' ? 'Admin' : 'PR Team'}
                                    </span>
                                  </div>
                                  <div className="w-20 flex items-center gap-2">
                                    <button
                                      onClick={() => openEditPrMemberModal(member)}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      title="Edit member"
                                    >
                                      Edit
                                    </button>
                                    {member.role !== 'admin' && (
                                      <button
                                        onClick={() => openRemovePrMemberModal(member)}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                        title="Remove member"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-blue-700 font-medium mb-2">Access Restricted</div>
                      <p className="text-blue-600 text-sm">Only administrators can manage PR team members.</p>
                    </div>
                  )}
                </div>
                
                {/* Danger Zone - Delete Management Section */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <Trash2 size={20} className="text-red-600 mr-2" />
                    <h3 className="text-lg font-medium text-red-900">Danger Zone - Delete Management</h3>
                  </div>
                  <p className="text-sm text-red-800 mb-4">
                    ⚠️ These actions are permanent and cannot be undone. Use with caution.
                  </p>
                  
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Celebrities Delete Section */}
                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">Guests ({celebrities.length})</h4>
                          {celebrities.length > 0 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setDangerZoneGuestSort(dangerZoneGuestSort === 'asc' ? 'desc' : 'asc')}
                                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                                title="Sort by name"
                              >
                                <ArrowUpDown size={14} className="mr-1" />
                                {dangerZoneGuestSort === 'asc' ? '↑' : '↓'}
                              </button>
                              <button
                                onClick={() => toggleSelectAll('celebrities')}
                                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                              >
                                {selectedItems.length === celebrities.length ? (
                                  <CheckSquare size={16} className="mr-1" />
                                ) : (
                                  <Square size={16} className="mr-1" />
                                )}
                                Select All
                              </button>
                              {selectedItems.length > 0 && (
                                <span className="text-sm text-gray-500">
                                  ({selectedItems.length} selected)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {celebrities.length > 0 && (
                          <div className="mb-3">
                            <input
                              type="text"
                              placeholder="Search guests..."
                              value={dangerZoneGuestSearch}
                              onChange={(e) => setDangerZoneGuestSearch(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                      
                      {celebrities.length === 0 ? (
                        <p className="text-gray-500 text-sm">No celebrities to delete</p>
                      ) : (
                        <>
                          <div className="max-h-48 overflow-y-auto mb-4 space-y-2">
                            {(() => {
                              const filteredGuests = filterAndSortDangerZoneGuests(celebrities, dangerZoneGuestSearch, dangerZoneGuestSort);
                              return filteredGuests.map(celebrity => (
                              <div key={celebrity.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                <button
                                  onClick={() => toggleItemSelection(celebrity.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {selectedItems.includes(celebrity.id) ? (
                                    <CheckSquare size={16} className="text-blue-600" />
                                  ) : (
                                    <Square size={16} />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{celebrity.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{(celebrity.category || []).join(', ')}</p>
                                </div>
                                <button
                                  onClick={() => handleSingleDelete(celebrity.id, 'celebrities')}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete celebrity"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              ));
                            })()}
                          </div>
                          
                          <div className="space-y-2">
                            {selectedItems.length > 0 && (
                              <button
                                onClick={() => handleBulkDelete('celebrities')}
                                className="w-full flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Delete Selected ({selectedItems.length})
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Journalists Delete Section */}
                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">Journalists ({allJournalists.length})</h4>
                          {allJournalists.length > 0 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setDangerZoneJournalistSort(dangerZoneJournalistSort === 'asc' ? 'desc' : 'asc')}
                                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                                title="Sort by name"
                              >
                                <ArrowUpDown size={14} className="mr-1" />
                                {dangerZoneJournalistSort === 'asc' ? '↑' : '↓'}
                              </button>
                              <button
                                onClick={() => toggleSelectAll('journalists')}
                                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                              >
                                {selectedItems.length === allJournalists.length ? (
                                  <CheckSquare size={16} className="mr-1" />
                                ) : (
                                  <Square size={16} className="mr-1" />
                                )}
                                Select All
                              </button>
                              {selectedItems.length > 0 && (
                                <span className="text-sm text-gray-500">
                                  ({selectedItems.length} selected)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {allJournalists.length > 0 && (
                          <div className="mb-3">
                            <input
                              type="text"
                              placeholder="Search journalists..."
                              value={dangerZoneJournalistSearch}
                              onChange={(e) => setDangerZoneJournalistSearch(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                      
                      {allJournalists.length === 0 ? (
                        <p className="text-gray-500 text-sm">No journalists to delete</p>
                      ) : (
                        <>
                          <div className="max-h-48 overflow-y-auto mb-4 space-y-2">
                            {(() => {
                              const filteredJournalists = filterAndSortDangerZoneJournalists(allJournalists, dangerZoneJournalistSearch, dangerZoneJournalistSort);
                              return filteredJournalists.map(journalist => (
                              <div key={journalist.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                <button
                                  onClick={() => toggleItemSelection(journalist.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {selectedItems.includes(journalist.id) ? (
                                    <CheckSquare size={16} className="text-blue-600" />
                                  ) : (
                                    <Square size={16} />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{journalist.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{journalist.outlet}</p>
                                </div>
                                <button
                                  onClick={() => handleSingleDelete(journalist.id, 'journalists')}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete journalist"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              ));
                            })()}
                          </div>
                          
                          <div className="space-y-2">
                            {selectedItems.length > 0 && (
                              <button
                                onClick={() => handleBulkDelete('journalists')}
                                className="w-full flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Delete Selected ({selectedItems.length})
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Event Year Management Section */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h3 className="text-lg font-medium text-indigo-900">Event Year Management</h3>
                    </div>
                  </div>

                  <p className="text-sm text-indigo-800 mb-4">
                    Manage event years. Create new years for upcoming events and archive past ones.
                  </p>

                  {/* Current Years List */}
                  <div className="bg-white rounded-lg border border-indigo-200 mb-4">
                    <div className="px-6 py-3 border-b border-indigo-200 bg-indigo-100">
                      <div className="flex items-center justify-between text-sm font-medium text-indigo-900">
                        <div className="flex-1">Year</div>
                        <div className="flex-1">Event Name</div>
                        <div className="w-28">Status</div>
                        <div className="w-28">Press Access</div>
                        <div className="w-24">Actions</div>
                      </div>
                    </div>
                    <div className="divide-y divide-indigo-100">
                      {availableYears.map(yearInfo => (
                        <div key={yearInfo.year} className="px-6 py-4 hover:bg-indigo-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 font-medium text-gray-900">{yearInfo.year}</div>
                            <div className="flex-1 text-sm text-gray-700">{yearInfo.event_name}</div>
                            <div className="w-28">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                yearInfo.is_archived
                                  ? 'bg-gray-100 text-gray-600'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {yearInfo.is_archived ? 'Archived' : 'Active'}
                              </span>
                            </div>
                            <div className="w-28">
                              <button
                                onClick={async () => {
                                  const newState = !yearInfo.press_active;
                                  const action = newState ? 'activate' : 'deactivate';
                                  if (confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} press access for ${yearInfo.year}?`)) {
                                    try {
                                      const { error } = await supabase
                                        .from('event_settings')
                                        .update({ press_active: newState })
                                        .eq('year', yearInfo.year);
                                      if (error) throw error;
                                      await refreshYears();
                                    } catch (err: any) {
                                      alert('Failed to update press access: ' + err.message);
                                    }
                                  }
                                }}
                                className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${
                                  yearInfo.press_active
                                    ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                                    : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
                                }`}
                              >
                                {yearInfo.press_active ? 'Active' : 'Inactive'}
                              </button>
                            </div>
                            <div className="w-24">
                              <button
                                onClick={async () => {
                                  const action = yearInfo.is_archived ? 'unarchive' : 'archive';
                                  if (confirm(`Are you sure you want to ${action} ${yearInfo.year}?`)) {
                                    try {
                                      const updates: any = { is_archived: !yearInfo.is_archived };
                                      if (!yearInfo.is_archived) {
                                        updates.press_active = false;
                                      }
                                      const { error } = await supabase
                                        .from('event_settings')
                                        .update(updates)
                                        .eq('year', yearInfo.year);
                                      if (error) throw error;
                                      await refreshYears();
                                    } catch (err: any) {
                                      alert('Failed to update year: ' + err.message);
                                    }
                                  }
                                }}
                                className={`text-sm font-medium ${
                                  yearInfo.is_archived
                                    ? 'text-green-600 hover:text-green-800'
                                    : 'text-amber-600 hover:text-amber-800'
                                }`}
                              >
                                {yearInfo.is_archived ? 'Unarchive' : 'Archive'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Create New Year */}
                  {effectiveUser?.role === 'admin' && (
                    <EventYearCreator refreshYears={refreshYears} availableYears={availableYears} />
                  )}
                </div>

                {/* EXTREME DANGER ZONE - Delete All */}
                <div className="bg-gradient-to-r from-red-600 to-red-800 border-2 border-red-900 rounded-lg p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full mr-3">
                      <span className="text-red-600 font-bold text-lg">⚠️</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">DANGER ZONE: Delete All Data</h3>
                      <p className="text-red-100 text-sm">End-of-show cleanup. This action is PERMANENT and cannot be undone.</p>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-red-400">
                      <h4 className="font-medium text-white mb-2">Delete All Celebrities</h4>
                      <p className="text-red-100 text-sm mb-3">
                        Removes all {celebrities.length} celebrities from the database
                      </p>
                      <button
                        onClick={() => handleDeleteAll('celebrities')}
                        disabled={celebrities.length === 0}
                        className="w-full flex items-center justify-center px-4 py-3 bg-red-900 text-white rounded-lg hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold border border-red-700"
                      >
                        <Trash2 size={16} className="mr-2" />
                        DELETE ALL CELEBRITIES
                      </button>
                    </div>
                    
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border border-red-400">
                      <h4 className="font-medium text-white mb-2">Delete All Journalists</h4>
                      <p className="text-red-100 text-sm mb-3">
                        Removes all {allJournalists.length} journalists from the database
                      </p>
                      <button
                        onClick={() => handleDeleteAll('journalists')}
                        disabled={allJournalists.length === 0}
                        className="w-full flex items-center justify-center px-4 py-3 bg-red-900 text-white rounded-lg hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold border border-red-700"
                      >
                        <Trash2 size={16} className="mr-2" />
                        DELETE ALL JOURNALISTS
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md sm:max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Upload {uploadType === 'celebrities' ? 'Celebrities' : 'Journalists'} CSV
              </h3>
              <button
                onClick={closeUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">CSV Format Requirements</h4>
                <div className="text-sm text-blue-800">
                  {uploadType === 'celebrities' ? (
                    <div>
                      <p className="mb-2"><strong>Required columns:</strong></p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Name (OR First Name + Last Name)</li>
                        <li>Known For, Category, Days</li>
                        <li>Agent Name, Agency, Agent Email, Agent Phone</li>
                      </ul>
                      <p className="mt-2 text-xs"><strong>Category:</strong> Celebrity, Voice, Anime, Gaming, Comics, or Exec</p>
                      <p className="text-xs"><strong>Days:</strong> Friday, Saturday, Sunday (comma-separated)</p>
                    </div>
                  ) : (
                    <div>
                      <p className="mb-2"><strong>Required columns:</strong></p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Name (OR First Name + Last Name)</li>
                        <li>Email, Phone, Primary Outlet</li>
                      </ul>
                      <p className="mt-2 text-xs"><strong>Optional:</strong> Press Tier (A/B/C, defaults to C), Specialty, Bio, Twitter, Website, Notes</p>
                    </div>
                  )}
                </div>
              </div>

              {!isUploading && !uploadResults && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Choose a CSV file to upload</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (uploadType === 'celebrities') {
                          handleCelebrityUpload(file);
                        } else {
                          handleJournalistUpload(file);
                        }
                      }
                    }}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block text-sm font-medium"
                  >
                    Select CSV File
                  </label>
                </div>
              )}

              {isUploading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Processing CSV file...</p>
                </div>
              )}

              {uploadResults && (
                <div className={`p-4 rounded-lg ${uploadResults.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  <h4 className={`font-semibold mb-2 ${uploadResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    Upload Results
                  </h4>
                  {uploadResults.success ? (
                    <div className="text-green-700 space-y-1 text-sm">
                      <p>✅ Successfully processed {uploadResults.processed} rows</p>
                      <p>✅ Added {uploadResults.added} new {uploadType}</p>
                      {uploadResults.updated > 0 && (
                        <p>✅ Updated {uploadResults.updated} existing {uploadType}</p>
                      )}
                      {uploadResults.skipped > 0 && (
                        <p>⚠️ Skipped {uploadResults.skipped} unchanged {uploadType}</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-red-700 text-sm">
                      <p>❌ Upload failed: {uploadResults.error}</p>
                    </div>
                  )}
                  
                  <div className="mt-4 flex gap-2">
                    {uploadResults.success && uploadType === 'journalists' && uploadResults.newJournalists && uploadResults.newJournalists.length > 0 && (
                      <button
                        onClick={() => handleBulkInvite(uploadResults.newJournalists)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        Send Invitations to {uploadResults.newJournalists.length} New Journalists
                      </button>
                    )}
                    {uploadResults.success && (
                      <button
                        onClick={closeUploadModal}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                      >
                        Done
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setUploadResults(null);
                        setIsUploading(false);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                    >
                      Upload Another
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Trash2 size={20} className="text-red-600 mr-3" />
                  <div>
                    <h4 className="font-semibold text-red-900">
                      {deleteType === 'single' 
                        ? `Delete ${deleteTarget === 'celebrities' ? 'Celebrity' : 'Journalist'}`
                        : deleteType === 'selected'
                        ? `Delete ${selectedItems.length} Selected ${deleteTarget}`
                        : `Delete All ${deleteTarget}`
                      }
                    </h4>
                    <p className="text-sm text-red-800 mt-1">
                      {deleteType === 'single'
                        ? 'This action cannot be undone.'
                        : deleteType === 'selected'
                        ? `This will permanently delete ${selectedItems.length} ${deleteTarget}.`
                        : `This will permanently delete ALL ${deleteTarget}. This is typically done at the end of a show.`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {!isDeleting && !deleteResults && (
                <div>
                  {deleteType === 'all' && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        🚨 To confirm this permanent action, type exactly: <code className="bg-yellow-200 px-1 rounded font-mono">DELETE ALL</code>
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="Type: DELETE ALL"
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  )}
                  
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={closeDeleteModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={executeDelete}
                      disabled={deleteType === 'all' && deleteConfirmText !== 'DELETE ALL'}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {deleteType === 'all' ? 'DELETE ALL PERMANENTLY' : 'Delete'}
                    </button>
                  </div>
                </div>
              )}

              {isDeleting && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Deleting...</p>
                </div>
              )}

              {deleteResults && (
                <div className={`p-4 rounded-lg ${deleteResults.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  <h4 className={`font-semibold mb-2 ${deleteResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    {deleteResults.success ? 'Delete Successful' : 'Delete Failed'}
                  </h4>
                  {deleteResults.success ? (
                    <div className="text-green-700 text-sm">
                      <p>✅ Successfully deleted {deleteResults.count === 'all' ? 'all' : deleteResults.count} {deleteTarget}</p>
                    </div>
                  ) : (
                    <div className="text-red-700 text-sm">
                      <p>❌ {deleteResults.error}</p>
                    </div>
                  )}
                  
                  <div className="mt-4">
                    <button
                      onClick={closeDeleteModal}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        deleteResults.success 
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                      }`}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Celebrity Modal */}
      {showCelebrityModal && selectedCelebrity && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{selectedCelebrity.name}</h3>
              <div className="flex items-center gap-3">
                {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (
                  <>
                    <button
                      onClick={() => {
                        openEditCelebrityModal(selectedCelebrity);
                      }}
                      className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </>
                )}
                <button
                  onClick={closeCelebrityModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Celebrity Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Guest Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Category:</span> {(selectedCelebrity.category || []).join(', ')}</p>
                      <p><span className="font-medium">Known For:</span> {selectedCelebrity.knownFor}</p>
                      <p><span className="font-medium">Days at Show:</span> {sortDays(selectedCelebrity.days).join(', ') || 'Not specified'}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">PR Available:</span>
                        <select
                          value={selectedCelebrity.prAvailable}
                          onChange={(e) => updatePRAvailable(selectedCelebrity.id, e.target.value)}
                          className="text-sm px-2 py-1 border rounded"
                        >
                          <option value="TBD">TBD</option>
                          <option value="Available">Available</option>
                          <option value="Limited Availability">Limited Availability</option>
                          <option value="Not Available">Not Available</option>
                        </select>
                      </div>
                      {isPRTeam && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Priority Tier:</span>
                        <select
                          value={selectedCelebrity.priority}
                          onChange={(e) => updateGuest(selectedCelebrity.id, { priority: e.target.value as 'A' | 'B' | 'C' | 'Unassigned' }).then(updated => {
                            if (updated) setSelectedCelebrity({ ...selectedCelebrity, priority: e.target.value as any });
                          })}
                          className={`text-sm px-2 py-1 border rounded font-medium ${
                            selectedCelebrity.priority === 'A' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                            selectedCelebrity.priority === 'B' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                            selectedCelebrity.priority === 'C' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                            'bg-gray-50 border-gray-300 text-gray-500'
                          }`}
                        >
                          <option value="Unassigned">Unassigned</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </select>
                      </div>
                      )}
                    </div>
                  </div>
                  
                  {selectedCelebrity.storyDetails && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Story Details</h4>
                      <p className="text-sm text-gray-600">{selectedCelebrity.storyDetails}</p>
                    </div>
                  )}
                  
                  {selectedCelebrity.notes && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                      <p className="text-sm text-gray-600">{selectedCelebrity.notes}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">Agent{(selectedCelebrity.agents || []).length !== 1 ? 's' : ''}</h4>
                      <button
                        onClick={() => {
                          setShowGuestAgentPicker(true);
                          setGuestAgentSearch('');
                        }}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
                      >
                        <UserPlus size={12} />
                        Add
                      </button>
                    </div>
                    <div className="space-y-3 text-sm">
                      {(selectedCelebrity.agents || []).length === 0 ? (
                        <p className="text-gray-400">No agents assigned</p>
                      ) : (
                        (selectedCelebrity.agents || []).map((ga: any) => (
                          <div key={ga.agentId} className="flex items-start gap-2 group">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {ga.name}
                                {ga.isPrimary && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Primary</span>}
                              </p>
                              {ga.agency && <p className="text-gray-500 text-xs">{ga.agency}</p>}
                              <div className="flex items-center gap-3 mt-1">
                                {ga.email && (
                                  <a href={`mailto:${ga.email}`} className="text-blue-600 hover:text-blue-800 underline">{ga.email}</a>
                                )}
                                {ga.phone && (
                                  <a href={`tel:${ga.phone}`} className="text-blue-600 hover:text-blue-800">{ga.phone}</a>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!ga.isPrimary && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await setGuestAgents(selectedCelebrity.id,
                                        (selectedCelebrity.agents || []).map((a: any) => ({
                                          agentId: a.agentId,
                                          isPrimary: a.agentId === ga.agentId
                                        }))
                                      );
                                    } catch (err) { console.error(err); }
                                  }}
                                  className="text-xs px-1.5 py-0.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Set as primary"
                                >
                                  Primary
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  if (confirm(`Remove ${ga.name} from ${selectedCelebrity.name}?`)) {
                                    try {
                                      await removeAgentFromGuest(selectedCelebrity.id, ga.agentId);
                                    } catch (err) { console.error(err); }
                                  }
                                }}
                                className="text-xs px-1.5 py-0.5 text-red-600 hover:bg-red-50 rounded"
                                title="Remove agent"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}

                      {/* Agent picker */}
                      {showGuestAgentPicker && (
                        <div className="border border-gray-200 rounded-md p-2 bg-white">
                          <input
                            type="text"
                            placeholder="Search existing agents..."
                            value={guestAgentSearch}
                            onChange={(e) => setGuestAgentSearch(e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            autoFocus
                          />
                          <div className="max-h-32 overflow-y-auto">
                            {agents
                              .filter(a =>
                                a.name.toLowerCase().includes(guestAgentSearch.toLowerCase()) &&
                                !(selectedCelebrity.agents || []).some((ga: any) => ga.agentId === a.id)
                              )
                              .slice(0, 8)
                              .map(a => (
                                <button
                                  key={a.id}
                                  onClick={async () => {
                                    try {
                                      await assignAgentToGuest(selectedCelebrity.id, a.id, false);
                                      setShowGuestAgentPicker(false);
                                    } catch (err) { console.error(err); }
                                  }}
                                  className="w-full text-left px-2 py-1.5 hover:bg-gray-50 rounded text-sm"
                                >
                                  <span className="font-medium">{a.name}</span>
                                  {a.agency && <span className="text-gray-400 ml-1">({a.agency})</span>}
                                </button>
                              ))
                            }
                            {agents.filter(a =>
                              a.name.toLowerCase().includes(guestAgentSearch.toLowerCase()) &&
                              !(selectedCelebrity.agents || []).some((ga: any) => ga.agentId === a.id)
                            ).length === 0 && (
                              <p className="text-xs text-gray-400 px-2 py-1">No matching agents. Add one from the Agents tab.</p>
                            )}
                          </div>
                          <button
                            onClick={() => setShowGuestAgentPicker(false)}
                            className="w-full text-xs text-gray-500 hover:text-gray-700 mt-1 py-1"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Interview Requests */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Interview Requests</h4>
                {(() => {
                  const celebrityRequests = getRequestsByCelebrity(selectedCelebrity.name);
                  
                  if (celebrityRequests.length === 0) {
                    return (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Users size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500">No interview requests yet</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-3">
                      {celebrityRequests.map(request => {
                        const priority = request.priority[selectedCelebrity.name] || 'Unassigned';
                        const isCompleted = request.completed[selectedCelebrity.name] || false;
                        
                        return (
                          <div key={request.id} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        closeCelebrityModal();
                                        openJournalistModal(request.journalistName);
                                      }}
                                      className="text-indigo-600 hover:text-indigo-800 transition-colors"
                                    >
                                      {request.journalistName}
                                    </button>
                                    {isPRTeam && (() => {
                                      const j = allJournalists.find(j => j.name === request.journalistName);
                                      const tier = j?.pressTier || 'C';
                                      return (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                          tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                          tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                          'bg-gray-100 border border-gray-300 text-gray-600'
                                        }`}>
                                          {tier}
                                        </span>
                                      );
                                    })()}
                                    {request.journalistPriority && (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                        request.journalistPriority === 'High' ? 'bg-red-100 text-red-700' :
                                        request.journalistPriority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>
                                        {request.journalistPriority}
                                      </span>
                                    )}
                                  </h5>
                                  <span className="text-sm text-gray-600">from {request.outlet}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Request Management Controls */}
                            <div className="flex items-center gap-4 pt-3 border-t">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-700">Status:</span>
                                <select
                                  value={request.status}
                                  onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                  className={`text-xs px-2 py-1 border rounded-md font-medium ${
                                    request.status === 'new' ? 'bg-orange-100 border-orange-300 text-orange-800' :
                                    request.status === 'pending' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                    request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                    request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-800' :
                                    request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                    'bg-red-100 border-red-300 text-red-800'
                                  }`}
                                >
                                  <option value="new">New</option>
                                  <option value="pending">Pending</option>
                                  <option value="approved">Approved</option>
                                  <option value="scheduled">Scheduled</option>
                                  <option value="complete">Complete</option>
                                  <option value="declined">Declined</option>
                                </select>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-700">Priority:</span>
                                <select
                                  value={priority}
                                  onChange={(e) => updateRequestPriority(request.id, selectedCelebrity.name, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                  className={`text-xs px-2 py-1 border rounded-md font-medium ${
                                    priority === 'A' ? 'bg-red-100 border-red-300 text-red-800' :
                                    priority === 'B' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                    priority === 'C' ? 'bg-green-100 border-green-300 text-green-800' :
                                    'bg-gray-100 border-gray-300 text-gray-600'
                                  }`}
                                >
                                  <option value="Unassigned">Unassigned</option>
                                  <option value="A">A (High)</option>
                                  <option value="B">B (Medium)</option>
                                  <option value="C">C (Low)</option>
                                </select>
                              </div>
                              
                              <button
                                onClick={() => markRequestCompleted(request.id, selectedCelebrity.name, !isCompleted)}
                                className={`p-1 rounded ${isCompleted ? 'text-green-600' : 'text-gray-400 hover:text-green-600'}`}
                                title={isCompleted ? 'Mark as not completed' : 'Mark as completed'}
                              >
                                <CheckCircle size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Journalist Modal */}
      {showJournalistModal && selectedJournalist && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-gray-900">{selectedJournalist.name}</h3>
                {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    selectedJournalist.pressTier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                    selectedJournalist.pressTier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                    'bg-gray-100 border border-gray-300 text-gray-600'
                  }`}>
                    Tier {selectedJournalist.pressTier || 'C'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (
                  <button
                    onClick={() => {
                      // Open Add Journalist modal in edit mode
                      setEditingJournalist(selectedJournalist);
                      setShowAddJournalistModal(true);
                      closeJournalistModal();
                    }}
                    className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                )}
                <button
                  onClick={closeJournalistModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Interview Stats - PR/Admin only */}
            {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (() => {
              const journalistRequests = getRequestsByJournalist(selectedJournalist.name);
              let totalRequested = 0;
              let totalCompleted = 0;
              journalistRequests.forEach(request => {
                (request.celebrities || []).forEach(celeb => {
                  totalRequested++;
                  if (request.completed && request.completed[celeb]) {
                    totalCompleted++;
                  }
                });
              });
              return (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold" style={{ color: 'var(--acacia-blue)' }}>{totalRequested}</div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">Interviews Requested</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold" style={{ color: 'var(--fanexpo-magenta)' }}>{totalCompleted}</div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">Interviews Completed</div>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Left Column - Contact & Profile Info */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Primary Outlet:</span> {selectedJournalist.outlet}</p>
                    {selectedJournalist.secondary_outlets && (
                      <p><span className="font-medium">Secondary Outlets:</span> {selectedJournalist.secondary_outlets}</p>
                    )}
                    <p><span className="font-medium">Email:</span> 
                      <a href={`mailto:${selectedJournalist.email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                        {selectedJournalist.email}
                      </a>
                    </p>
                    {selectedJournalist.phone && (
                      <p><span className="font-medium">Phone:</span> 
                        <a href={`tel:${selectedJournalist.phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                          {selectedJournalist.phone}
                        </a>
                      </p>
                    )}
                    {selectedJournalist.specialty && (
                      <p><span className="font-medium">Specialty:</span> {selectedJournalist.specialty}</p>
                    )}
                  </div>
                  
                  {/* Credential Status */}
                  <div className="mt-4 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">Credentials:</span>
                      <span className={`inline-flex items-center px-3 py-1 text-sm font-medium ${
                        selectedJournalist.credentialsPickedUp 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedJournalist.credentialsPickedUp ? 'Picked Up' : 'Not Picked Up'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {selectedJournalist.bio && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Bio</h4>
                    <p className="text-sm text-gray-600">{selectedJournalist.bio}</p>
                  </div>
                )}
              </div>

              {/* Right Column - Social & Web */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Online Presence</h4>
                  <div className="space-y-2 text-sm">
                    {selectedJournalist.twitter && (
                      <p><span className="font-medium">Twitter:</span> 
                        <a href={`https://twitter.com/${selectedJournalist.twitter.replace('@', '')}`} 
                           target="_blank" rel="noopener noreferrer" 
                           className="text-blue-600 hover:text-blue-800 ml-1">
                          @{selectedJournalist.twitter.replace('@', '')}
                        </a>
                      </p>
                    )}
                    {selectedJournalist.website && (
                      <p><span className="font-medium">Website:</span> 
                        <a href={selectedJournalist.website} 
                           target="_blank" rel="noopener noreferrer" 
                           className="text-blue-600 hover:text-blue-800 ml-1">
                          {selectedJournalist.website}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Requests */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Interview Requests from {selectedJournalist.name}</h4>
              {(() => {
                const journalistRequests = getRequestsByJournalist(selectedJournalist.name);
                
                if (journalistRequests.length === 0) {
                  return (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No interview requests found from this journalist.</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {journalistRequests.map(request => 
                      (request.celebrities || []).map(celebrityName => {
                        const priority = (request.priority && request.priority[celebrityName]) || 'Unassigned';
                        const isCompleted = (request.completed && request.completed[celebrityName]) || false;
                        
                        return (
                          <div key={`${request.id}-${celebrityName}`} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h5 className="font-medium text-gray-900">Request for:</h5>
                                  <button
                                    onClick={() => {
                                      const celeb = celebrities.find(c => c.name === celebrityName);
                                      if (celeb) {
                                        closeJournalistModal();
                                        openCelebrityModal(celeb);
                                      }
                                    }}
                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2 py-1 rounded"
                                  >
                                    {celebrityName}
                                  </button>
                                </div>
                                
                                {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') ? (
                                  <div className="space-y-2">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-700">Priority:</span>
                                        <select
                                          value={priority}
                                          onChange={(e) => updateRequestPriority(request.id, celebrityName, e.target.value as 'A' | 'B' | 'C' | 'Unassigned')}
                                          className={`text-xs px-2 py-1 border rounded-md font-medium ${
                                            priority === 'A' ? 'bg-rose-100 border-rose-300 text-rose-700' :
                                            priority === 'B' ? 'bg-amber-100 border-amber-300 text-amber-700' :
                                            priority === 'C' ? 'bg-emerald-100 border-emerald-300 text-emerald-700' :
                                            'bg-gray-100 border-gray-300 text-gray-600'
                                          }`}
                                        >
                                          <option value="Unassigned">-</option>
                                          <option value="A">A</option>
                                          <option value="B">B</option>
                                          <option value="C">C</option>
                                        </select>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-700">Status:</span>
                                        <select
                                          value={request.status}
                                          onChange={(e) => updateRequestStatus(request.id, e.target.value as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined')}
                                          className={`text-xs px-2 py-1 border rounded-md font-medium ${
                                            request.status === 'new' ? 'bg-slate-100 border-slate-300 text-slate-700' :
                                            request.status === 'pending' ? 'bg-amber-100 border-amber-300 text-amber-700' :
                                            request.status === 'approved' ? 'bg-green-100 border-green-300 text-green-700' :
                                            request.status === 'scheduled' ? 'bg-blue-100 border-blue-300 text-blue-700' :
                                            request.status === 'complete' ? 'bg-emerald-700 border-emerald-800 text-white' :
                                            'bg-gray-100 border-gray-300 text-gray-600'
                                          }`}
                                        >
                                          <option value="new">New</option>
                                          <option value="pending">Pending</option>
                                          <option value="approved">Approved</option>
                                          <option value="scheduled">Scheduled</option>
                                          <option value="complete">Complete</option>
                                          <option value="declined">Declined</option>
                                        </select>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-700">Complete:</span>
                                        <button
                                          onClick={() => markRequestCompleted(request.id, celebrityName, !isCompleted)}
                                          className={`p-1 rounded ${isCompleted ? 'text-green-600' : 'text-gray-400 hover:text-green-600'}`}
                                          title={isCompleted ? 'Mark as not completed' : 'Mark as completed'}
                                        >
                                          {isCompleted ? <CheckCircle size={16} /> : <Square size={16} />}
                                        </button>
                                      </div>
                                    </div>
                                    {request.journalistPriority && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">Journalist rates:</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                          request.journalistPriority === 'High' ? 'bg-red-100 text-red-700' :
                                          request.journalistPriority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-green-100 text-green-700'
                                        }`}>
                                          {request.journalistPriority}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                      <span>Status: <span className={`font-medium ${
                                        request.status === 'complete' ? 'text-emerald-700' :
                                        request.status === 'declined' ? 'text-red-600' :
                                        request.status === 'approved' ? 'text-green-600' :
                                        request.status === 'scheduled' ? 'text-blue-600' :
                                        request.status === 'pending' ? 'text-yellow-600' : 'text-gray-600'
                                      }`}>
                                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                      </span></span>
                                      <span>Created: {new Date(request.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-gray-700">My Priority:</span>
                                      <select
                                        value={request.journalistPriority || ''}
                                        onChange={(e) => updateJournalistPriority(request.id, (e.target.value || null) as 'High' | 'Medium' | 'Low' | null)}
                                        className={`text-xs px-2 py-1 border rounded-md font-medium ${
                                          request.journalistPriority === 'High' ? 'bg-red-100 border-red-300 text-red-800' :
                                          request.journalistPriority === 'Medium' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                                          request.journalistPriority === 'Low' ? 'bg-green-100 border-green-300 text-green-800' :
                                          'bg-gray-100 border-gray-300 text-gray-600'
                                        }`}
                                      >
                                        <option value="">-</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                      </select>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Send Invite Section - Only for PR users */}
            {(effectiveUser?.role === 'pr' || effectiveUser?.role === 'admin') && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">System Invitation</h4>
                    <p className="text-sm text-gray-600">Send login credentials to this journalist</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const status = invitationStatuses[selectedJournalist.email];
                      const isSending = sendingInvitations.has(selectedJournalist.email);
                      
                      if (status === 'pending') {
                        return (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-3 py-2 rounded-full text-sm bg-yellow-100 text-yellow-800">
                              <Clock size={14} className="mr-2" />
                              Account Exists - Pending
                            </span>
                            <button
                              onClick={() => handleSendInvitation(selectedJournalist)}
                              disabled={isSending}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {isSending ? 'Sending...' : 'Resend Invitation'}
                            </button>
                          </div>
                        );
                      } else if (status === 'accepted') {
                        return (
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-3 py-2 rounded-full text-sm bg-green-100 text-green-800">
                              <CheckCircle size={14} className="mr-2" />
                              Account Active
                            </span>
                            <button
                              onClick={() => handleSendInvitation(selectedJournalist)}
                              disabled={isSending}
                              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                            >
                              <Mail size={12} className="mr-1" />
                              {isSending ? 'Sending...' : 'Resend Invite'}
                            </button>
                          </div>
                        );
                      } else {
                        return (
                          <button
                            onClick={() => handleSendInvitation(selectedJournalist)}
                            disabled={isSending}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                          >
                            <Mail size={14} className="mr-2" />
                            {isSending ? 'Sending Invitation...' : 'Send Invite'}
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Edit Journalist Modal */}
      {showEditJournalistModal && editingJournalist && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Journalist</h3>
              <button
                onClick={closeEditJournalistModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const updatedData = {
                  name: formData.get('name') as string,
                  outlet: formData.get('outlet') as string,
                  email: formData.get('email') as string || '',
                  phone: formData.get('phone') as string,
                  bio: formData.get('bio') as string,
                  twitter: formData.get('twitter') as string,
                  website: formData.get('website') as string,
                  verified: formData.get('verified') === 'on'
                };
                
                try {
                  await updateJournalist(editingJournalist.id, updatedData);
                  closeEditJournalistModal();
                  // Update the selected journalist if it's the same one
                  if (selectedJournalist && selectedJournalist.id === editingJournalist.id) {
                    setSelectedJournalist({ ...selectedJournalist, ...updatedData });
                  }
                } catch (error) {
                  console.error('Failed to update journalist:', error);
                  alert('Failed to update journalist. Please try again.');
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingJournalist.name}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Outlet *</label>
                  <input
                    type="text"
                    name="outlet"
                    defaultValue={editingJournalist.outlet}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingJournalist.email}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={editingJournalist.phone || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                  <input
                    type="text"
                    name="twitter"
                    defaultValue={editingJournalist.twitter || ''}
                    placeholder="@username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    name="website"
                    defaultValue={editingJournalist.website || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  name="bio"
                  rows={3}
                  defaultValue={editingJournalist.bio || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="verified"
                  id="verified"
                  defaultChecked={editingJournalist.verified || false}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="verified" className="ml-2 block text-sm text-gray-700">
                  Verified journalist
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditJournalistModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Celebrity Modal */}
      {showEditCelebrityModal && editingCelebrity && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Celebrity</h3>
              <button
                onClick={closeEditCelebrityModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const updatedData = {
                  name: editCelebForm.name,
                  category: editCelebForm.category,
                  knownFor: editCelebForm.knownFor,
                  prAvailable: editCelebForm.prAvailable,
                  days: editCelebForm.days
                };

                try {
                  await updateGuest(editingCelebrity.id, updatedData);
                  closeEditCelebrityModal();
                  // Update the selected celebrity if it's the same one
                  if (selectedCelebrity && selectedCelebrity.id === editingCelebrity.id) {
                    setSelectedCelebrity({ ...selectedCelebrity, ...updatedData });
                  }
                } catch (error) {
                  console.error('Failed to update celebrity:', error);
                  alert('Failed to update celebrity. Please try again.');
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={editCelebForm.name}
                    onChange={(e) => setEditCelebForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <div className="flex flex-wrap gap-2 px-3 py-2 border border-gray-300 rounded-md">
                    {['Celebrity', 'Voice', 'Anime', 'Gaming', 'Comics', 'Exec'].map(cat => (
                      <label key={cat} className="inline-flex items-center gap-1 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editCelebForm.category.includes(cat)}
                          onChange={(e) => {
                            setEditCelebForm(prev => ({
                              ...prev,
                              category: e.target.checked
                                ? [...prev.category, cat]
                                : prev.category.filter(c => c !== cat)
                            }));
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Known For *</label>
                  <input
                    type="text"
                    value={editCelebForm.knownFor}
                    onChange={(e) => setEditCelebForm(prev => ({ ...prev, knownFor: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agents</label>
                  <div className="space-y-2">
                    {(editingCelebrity.agents || []).length === 0 ? (
                      <p className="text-sm text-gray-400 py-2">No agents assigned. Manage agents in the Agents tab.</p>
                    ) : (
                      (editingCelebrity.agents || []).map((ga: any) => (
                        <div key={ga.agentId} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md text-sm">
                          <span className="font-medium">{ga.name}</span>
                          {ga.agency && <span className="text-gray-500">({ga.agency})</span>}
                          {ga.isPrimary && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Primary</span>}
                          {ga.email && <a href={`mailto:${ga.email}`} className="text-blue-600 hover:underline ml-auto">{ga.email}</a>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PR Available</label>
                <select
                  value={editCelebForm.prAvailable}
                  onChange={(e) => setEditCelebForm(prev => ({ ...prev, prAvailable: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="TBD">TBD</option>
                  <option value="Available">Available</option>
                  <option value="Limited Availability">Limited Availability</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Available Days</label>
                <div className="flex flex-wrap gap-3">
                  {['Friday', 'Saturday', 'Sunday'].map(day => (
                    <label key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editCelebForm.days.includes(day)}
                        onChange={(e) => {
                          setEditCelebForm(prev => ({
                            ...prev,
                            days: e.target.checked
                              ? [...prev.days, day]
                              : prev.days.filter(d => d !== day)
                          }));
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditCelebrityModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Agent Modal */}
      {showAgentModal && selectedAgent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{selectedAgent.name}</h3>
              <button
                onClick={closeAgentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Agent Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Agency:</span> {selectedAgent.agency}</p>
                      {selectedAgent.email && (
                        <p><span className="font-medium">Email:</span> 
                          <a href={`mailto:${selectedAgent.email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                            {selectedAgent.email}
                          </a>
                        </p>
                      )}
                      {selectedAgent.phone && (
                        <p><span className="font-medium">Phone:</span> 
                          <a href={`tel:${selectedAgent.phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                            {selectedAgent.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Quick Actions</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedAgent.email && (
                        <a
                          href={`mailto:${selectedAgent.email}`}
                          className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          <Mail size={14} className="mr-1" />
                          Email Agent
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent's Celebrities */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Users size={18} className="mr-2 text-gray-500" />
                  Represents ({(selectedAgent.clients || []).length})
                </h4>

                {(() => {
                  const agentCelebrities = selectedAgent.clients || [];
                  
                  if (agentCelebrities.length === 0) {
                    return (
                      <p className="text-gray-500 italic">No celebrities currently linked to this agent</p>
                    );
                  }
                  
                  return (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {agentCelebrities.map(celebrity => {
                        const celebrityRequests = getRequestsByCelebrity(celebrity.name);
                        const pendingRequests = celebrityRequests.filter(r => r.status === 'new' || r.status === 'pending');
                        
                        return (
                          <div key={celebrity.id} className="bg-gray-50 p-4 rounded-lg border hover:shadow-sm transition-shadow">
                            <div className="flex items-start justify-between mb-2">
                              <button
                                onClick={() => {
                                  closeAgentModal();
                                  openCelebrityModal(celebrity);
                                }}
                                className="font-medium text-indigo-600 hover:text-indigo-800 transition-colors text-left"
                              >
                                {celebrity.name}
                              </button>
                              <div className="flex gap-1">
                                {celebrityRequests.length > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                    {celebrityRequests.length}
                                  </span>
                                )}
                                {pendingRequests.length > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">
                                    <Clock size={8} className="mr-1" />
                                    {pendingRequests.length}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                              <p><span className="font-medium">Category:</span> {(celebrity.category || []).join(', ')}</p>
                              <p><span className="font-medium">Known for:</span> {celebrity.knownFor}</p>
                              <p><span className="font-medium">PR Status:</span> {celebrity.prAvailable}</p>
                              {celebrity.days && celebrity.days.length > 0 && (
                                <p><span className="font-medium">Available:</span> {celebrity.days.join(', ')}</p>
                              )}
                            </div>

                            {/* Individual Celebrity Interview Requests */}
                            {celebrityRequests.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <h6 className="text-xs font-medium text-gray-700 mb-2">Interview Requests:</h6>
                                <div className="space-y-2">
                                  {celebrityRequests.slice(0, 3).map(request => (
                                    <div key={request.id} className="text-xs bg-white p-2 rounded border">
                                      <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => {
                                              closeAgentModal();
                                              openJournalistModal(request.journalistName);
                                            }}
                                            className="font-medium text-blue-600 hover:text-blue-800"
                                          >
                                            {request.journalistName}
                                          </button>
                                          {(() => {
                                            const j = allJournalists.find(j => j.name === request.journalistName);
                                            const tier = j?.pressTier || 'C';
                                            return (
                                              <span className={`text-[10px] px-1 py-0 rounded-full font-semibold ${
                                                tier === 'A' ? 'bg-rose-50 border border-rose-300 text-rose-700' :
                                                tier === 'B' ? 'bg-amber-50 border border-amber-300 text-amber-700' :
                                                'bg-gray-100 border border-gray-300 text-gray-600'
                                              }`}>
                                                {tier}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                          request.status === 'new' ? 'bg-orange-100 text-orange-800' :
                                          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          request.status === 'approved' ? 'bg-green-100 text-green-700' :
                                          request.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                          request.status === 'complete' ? 'bg-emerald-700 text-white' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {request.status}
                                        </span>
                                      </div>
                                      <p className="text-gray-500 mt-1">{request.outlet}</p>
                                    </div>
                                  ))}
                                  {celebrityRequests.length > 3 && (
                                    <p className="text-xs text-gray-500 italic">
                                      ... and {celebrityRequests.length - 3} more requests
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add PR Team Member Modal */}
      {showAddPrMemberModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Invite PR Team Member</h3>
              <button
                onClick={closeAddPrMemberModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const memberData = {
                  name: formData.get('name') as string,
                  email: formData.get('email') as string || '',
                  role: formData.get('role') as string,
                  department: formData.get('department') as string
                };
                
                try {
                  console.log('Sending invitation to PR team member:', memberData);
                  
                  // Use invitation service to create new PR team member
                  // All team members have @teamacacia.com addresses for domain-wide delegation
                  const invitationSent = await invitePrTeamMember(
                    memberData.email,
                    memberData.name,
                    memberData.role || 'pr',
                    currentUser?.email || 'morgan@teamacacia.com', // sender email
                    currentUser?.name || 'Admin' // sender name
                  );
                  
                  if (invitationSent) {
                    alert(`Invitation sent successfully to ${memberData.email}!`);
                    closeAddPrMemberModal();
                    // Refresh the PR team list
                    loadPrTeamMembers();
                  } else {
                    throw new Error('Invitation service returned false - check console for details');
                  }
                } catch (error) {
                  console.error('❌ INVITATION ERROR:', error);
                  console.error('❌ ERROR MESSAGE:', error.message);
                  console.error('❌ ERROR STACK:', error.stack);
                  
                  let errorMessage = 'Failed to send invitation. ';
                  if (error.message && error.message.includes('already exists')) {
                    errorMessage += 'This email address already has an account.';
                  } else if (error.message && error.message.includes('User already has an invitation')) {
                    errorMessage += 'This user already has an invitation or account.';
                  } else if (error.message) {
                    errorMessage += `Error: ${error.message}`;
                  } else {
                    errorMessage += 'Please check console for details.';
                  }
                  
                  alert(errorMessage);
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    name="role"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pr">PR Team Member</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    name="department"
                    placeholder="e.g., Public Relations, Communications"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddPrMemberModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit PR Team Member Modal */}
      {showEditPrMemberModal && editingPrMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit PR Team Member</h3>
              <button
                onClick={closeEditPrMemberModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const updates = {
                  name: formData.get('name') as string,
                  email: formData.get('email') as string || '',
                  role: formData.get('role') as string,
                  department: formData.get('department') as string,
                  permissions: (formData.get('role') as string) === 'admin' 
                    ? ['read', 'write', 'delete', 'admin']
                    : ['read', 'write']
                };
                
                try {
                  await updatePrTeamMember(editingPrMember.id, updates);
                  closeEditPrMemberModal();
                } catch (error) {
                  console.error('Failed to update PR team member:', error);
                  alert('Failed to update PR team member. Please try again.');
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingPrMember.name}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingPrMember.email}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    name="role"
                    defaultValue={editingPrMember.role}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pr">PR Team Member</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    name="department"
                    defaultValue={editingPrMember.department}
                    placeholder="e.g., Public Relations, Communications"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      console.log('Sending password reset to:', editingPrMember.email);
                      
                      const resetSent = await sendPasswordReset(
                        editingPrMember.email,
                        editingPrMember.name,
                        currentUser?.email || 'admin@teamacacia.com',
                        currentUser?.email || 'admin@teamacacia.com',
                        currentUser?.name || 'Admin'
                      );
                      
                      if (resetSent) {
                        console.log(`✅ Password reset sent successfully to ${editingPrMember.email}!`);
                      } else {
                        throw new Error('Failed to send password reset');
                      }
                    } catch (error) {
                      console.error('Failed to send password reset:', error);
                      alert('Failed to send password reset. Please try again.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Send Password Reset
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeEditPrMemberModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove PR Team Member Confirmation Modal */}
      {showRemovePrMemberModal && memberToRemove && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Confirm Removal</h3>
              <button
                onClick={closeRemovePrMemberModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to remove <strong>{memberToRemove.name}</strong> from the PR team?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone. The member will lose access to the system immediately.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeRemovePrMemberModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRemovePrMember}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Remove Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddGuestModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Add Guest</h3>
              <button
                onClick={closeAddGuestModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const daysData = formData.getAll('days') as string[];
                const guestData = {
                  name: formData.get('name') as string,
                  category: formData.getAll('category') as string[],
                  days: daysData,
                  knownFor: formData.get('knownFor') as string,
                  agents: [] as any[],
                  prAvailable: formData.get('prAvailable') as string || 'TBD',
                  notes: formData.get('notes') as string || '',
                  source: 'manual',
                  priority: 'Unassigned' as const
                };
                
                try {
                  console.log('Adding guest with data:', guestData);
                  await addGuest(guestData);
                  closeAddGuestModal();
                } catch (error) {
                  console.error('Failed to add guest:', error);
                  alert('Failed to add guest. Please try again.');
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Guest full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <div className="flex flex-wrap gap-2 px-3 py-2 border border-gray-300 rounded-md">
                    {['Celebrity', 'Voice', 'Anime', 'Gaming', 'Comics', 'Exec'].map(cat => (
                      <label key={cat} className="inline-flex items-center gap-1 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          name="category"
                          value={cat}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Known For *</label>
                <input
                  type="text"
                  name="knownFor"
                  required
                  placeholder="Separate credits with a comma"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agents</label>
                <p className="text-sm text-gray-500 italic px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                  Agents can be assigned after creating the guest from the Agents tab.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PR Available</label>
                <select
                  name="prAvailable"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="TBD">TBD</option>
                  <option value="Available">Available</option>
                  <option value="Limited Availability">Limited Availability</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days Available</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <label key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        name="days"
                        value={day}
                        className="mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  rows={2}
                  placeholder="Internal notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddGuestModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Add Guest
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Journalist Modal */}
      {showAddJournalistModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">{editingJournalist ? 'Edit Journalist' : 'Add Journalist'}</h3>
              <button
                onClick={closeAddJournalistModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const journalistData = {
                  name: formData.get('name') as string,
                  outlet: formData.get('outlet') as string,
                  email: formData.get('email') as string || '',
                  phone: formData.get('phone') as string || '',
                  secondary_outlets: formData.get('secondaryOutlets') as string || '',
                  twitter: formData.get('twitter') as string || '',
                  website: formData.get('website') as string || '',
                  notes: formData.get('notes') as string || ''
                };
                
                try {
                  if (editingJournalist) {
                    console.log('Updating journalist with data:', journalistData);
                    await updateJournalist(editingJournalist.id, journalistData);
                  } else {
                    console.log('Adding journalist with data:', journalistData);
                    await addJournalist(journalistData);
                  }
                  closeAddJournalistModal();
                } catch (error) {
                  console.error(`Failed to ${editingJournalist ? 'update' : 'add'} journalist:`, error);
                  alert(`Failed to ${editingJournalist ? 'update' : 'add'} journalist. Please try again.`);
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Journalist full name"
                    defaultValue={editingJournalist?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Outlet *</label>
                  <input
                    type="text"
                    name="outlet"
                    required
                    placeholder="Media outlet or publication"
                    defaultValue={editingJournalist?.outlet || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="journalist@outlet.com"
                    defaultValue={editingJournalist?.email || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(555) 123-4567"
                    defaultValue={editingJournalist?.phone || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Outlets</label>
                <input
                  type="text"
                  name="secondaryOutlets"
                  placeholder="Additional outlets, separated by commas"
                  defaultValue={editingJournalist?.secondary_outlets || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                  <input
                    type="text"
                    name="twitter"
                    placeholder="@username"
                    defaultValue={editingJournalist?.twitter || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    name="website"
                    placeholder="https://website.com"
                    defaultValue={editingJournalist?.website || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  rows={2}
                  placeholder="Internal notes"
                  defaultValue={editingJournalist?.notes || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddJournalistModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {editingJournalist ? 'Save Changes' : 'Add Journalist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Add Interview Request Modal */}
      {showAddRequestModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Add Interview Request</h3>
              <button
                onClick={() => {
                  setShowAddRequestModal(false);
                  setSelectedJournalistForRequest(null);
                  setSelectedGuestForRequest(null);
                  setAddRequestStep('journalist');
                  setJournalistSearchTerm('');
                  setGuestSearchTerm('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Step 1: Select Journalist */}
            {addRequestStep === 'journalist' && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Step 1: Select Journalist</h4>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search for journalist by name..."
                    value={journalistSearchTerm}
                    onChange={(e) => setJournalistSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {allJournalists
                    .filter(journalist => 
                      journalist.name.toLowerCase().includes(journalistSearchTerm.toLowerCase()) ||
                      journalist.outlet.toLowerCase().includes(journalistSearchTerm.toLowerCase())
                    )
                    .map((journalist) => (
                    <div
                      key={journalist.id}
                      onClick={() => {
                        setSelectedJournalistForRequest(journalist);
                        setAddRequestStep('guest');
                      }}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{journalist.name}</div>
                      <div className="text-sm text-gray-600">{journalist.outlet}</div>
                      <div className="text-xs text-gray-500">{journalist.email}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Select Guest */}
            {addRequestStep === 'guest' && selectedJournalistForRequest && (
              <div>
                <div className="mb-4">
                  <button
                    onClick={() => setAddRequestStep('journalist')}
                    className="flex items-center text-indigo-600 hover:text-indigo-800 mb-2"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Journalist Selection
                  </button>
                  <h4 className="text-lg font-semibold">Step 2: Select Guest</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Creating request for: <span className="font-medium">{selectedJournalistForRequest.name}</span> ({selectedJournalistForRequest.outlet})
                  </p>
                </div>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search for guest by name..."
                    value={guestSearchTerm}
                    onChange={(e) => setGuestSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {celebrities
                    .filter(guest => 
                      guest.name.toLowerCase().includes(guestSearchTerm.toLowerCase()) ||
                      guest.knownFor.toLowerCase().includes(guestSearchTerm.toLowerCase())
                    )
                    .map((guest) => (
                    <div
                      key={guest.id}
                      onClick={() => {
                        setSelectedGuestForRequest(guest);
                        setAddRequestStep('confirm');
                      }}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{guest.name}</div>
                      <div className="text-sm text-gray-600">{guest.knownFor}</div>
                      <div className="text-xs text-gray-500">{(guest.category || []).join(', ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {addRequestStep === 'confirm' && selectedJournalistForRequest && selectedGuestForRequest && (
              <div>
                <div className="mb-4">
                  <button
                    onClick={() => setAddRequestStep('guest')}
                    className="flex items-center text-indigo-600 hover:text-indigo-800 mb-2"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Guest Selection
                  </button>
                  <h4 className="text-lg font-semibold mb-4">Step 3: Confirm Interview Request</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Journalist</h5>
                    <p className="text-sm">{selectedJournalistForRequest.name}</p>
                    <p className="text-xs text-gray-600">{selectedJournalistForRequest.outlet}</p>
                    <p className="text-xs text-gray-500">{selectedJournalistForRequest.email}</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Guest</h5>
                    <p className="text-sm">{selectedGuestForRequest.name}</p>
                    <p className="text-xs text-gray-600">{selectedGuestForRequest.knownFor}</p>
                    <p className="text-xs text-gray-500">{(selectedGuestForRequest.category || []).join(', ')}</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAddRequestModal(false);
                      setSelectedJournalistForRequest(null);
                      setSelectedGuestForRequest(null);
                      setAddRequestStep('journalist');
                      setJournalistSearchTerm('');
                      setGuestSearchTerm('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await createRequest({
                          journalistId: selectedJournalistForRequest.id,
                          journalistName: selectedJournalistForRequest.name,
                          outlet: selectedJournalistForRequest.outlet,
                          celebrities: [selectedGuestForRequest.name],
                          status: 'new',
                          priority: { [selectedGuestForRequest.name]: 'Unassigned' },
                          completed: { [selectedGuestForRequest.name]: false }
                        });
                        
                        // Close modal and reset state
                        setShowAddRequestModal(false);
                        setSelectedJournalistForRequest(null);
                        setSelectedGuestForRequest(null);
                        setAddRequestStep('journalist');
                        setJournalistSearchTerm('');
                        setGuestSearchTerm('');
                        
                        alert('Interview request created successfully!');
                      } catch (error) {
                        console.error('Error creating interview request:', error);
                        alert('Error creating interview request. Please try again.');
                      }
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Add Request
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent Edit Modal */}
      {showAgentModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Edit Agent</h3>
              <button
                onClick={() => {
                  setShowAgentModal(false);
                  setSelectedAgent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();

              try {
                await updateAgent(selectedAgent.id, {
                  name: editAgentForm.name,
                  email: editAgentForm.email,
                  phone: editAgentForm.phone
                });

                await reloadGuests();
                setShowAgentModal(false);
                setSelectedAgent(null);
                alert('Agent updated successfully!');
              } catch (error) {
                console.error('Error updating agent:', error);
                alert('Failed to update agent. Please try again.');
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={editAgentForm.name}
                  onChange={(e) => setEditAgentForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editAgentForm.email}
                  onChange={(e) => setEditAgentForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={editAgentForm.phone}
                  onChange={(e) => setEditAgentForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAgentModal(false);
                    setSelectedAgent(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Agent Modal */}
      {showAddAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Add Agent</h3>
              <button
                onClick={() => setShowAddAgentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              try {
                const newAgent = await addAgent({
                  name: formData.get('name') as string,
                  agency: formData.get('agency') as string || '',
                  email: formData.get('email') as string || '',
                  phone: formData.get('phone') as string || '',
                  eventYear: 0,
                  totalRequests: 0,
                  source: 'manual'
                });

                if (newAgent && addAgentSelectedGuests.length > 0) {
                  for (const guestId of addAgentSelectedGuests) {
                    await assignAgentToGuest(guestId, newAgent.id, false);
                  }
                }

                setShowAddAgentModal(false);
                alert('Agent added successfully!');
              } catch (error) {
                console.error('Error adding agent:', error);
                alert('Failed to add agent. Please try again.');
              }
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Agent name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
                  <input
                    type="text"
                    name="agency"
                    placeholder="Agency name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="agent@agency.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              {/* Tag Guests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Guests (optional)</label>
                <input
                  type="text"
                  placeholder="Search guests to tag..."
                  value={addAgentGuestSearch}
                  onChange={(e) => setAddAgentGuestSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 mb-2"
                />

                {/* Selected guests */}
                {addAgentSelectedGuests.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {addAgentSelectedGuests.map(guestId => {
                      const guest = celebrities.find(c => c.id === guestId);
                      return guest ? (
                        <span key={guestId} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          {guest.name}
                          <button
                            type="button"
                            onClick={() => setAddAgentSelectedGuests(prev => prev.filter(id => id !== guestId))}
                            className="hover:text-green-600"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Guest search results */}
                {addAgentGuestSearch && (
                  <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                    {celebrities
                      .filter(c =>
                        c.name.toLowerCase().includes(addAgentGuestSearch.toLowerCase()) &&
                        !addAgentSelectedGuests.includes(c.id)
                      )
                      .slice(0, 10)
                      .map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setAddAgentSelectedGuests(prev => [...prev, c.id]);
                            setAddAgentGuestSearch('');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium">{c.name}</span>
                          <span className="text-gray-400 ml-2">{(c.category || []).join(', ')}</span>
                        </button>
                      ))
                    }
                    {celebrities.filter(c =>
                      c.name.toLowerCase().includes(addAgentGuestSearch.toLowerCase()) &&
                      !addAgentSelectedGuests.includes(c.id)
                    ).length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-400">No matching guests</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddAgentModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add Agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};



export default EventInterviewAppSimple;